/**
 * HRPage — Module Ressources Humaines SuiviPlus
 * Tabs : Dashboard / Employés / Charges RH / Documents / Paie & Déclarations
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Users,
  BarChart3,
  DollarSign,
  Briefcase,
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  Edit,
  Trash2,
  X,
  Check,
  Upload,
  FileText,
  AlertTriangle,
  TrendingUp,
  Calendar,
  ArrowRight,
  Eye,
  Zap,
  Archive,
  User,
  Phone,
  Mail,
  MapPin,
  Building,
  ArrowLeft,
  Clock,
  Loader,
  Download,
  RefreshCw,
  Send,
  GraduationCap,
  MessageSquare,
  CheckCircle,
  ChevronUp,
  Star,
  Stethoscope,
  Bus,
  BarChart2,
  CalendarDays,
  Link2,
  Copy,
  ExternalLink,
  RefreshCcw,
  List,
  Timer,
  ThumbsUp,
  ThumbsDown,
  Camera,
} from "lucide-react";
import {
  getHRDashboard,
  getEmployees,
  getEmployeeById,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  archiveEmployee,
  getSalaryHistory,
  addSalary,
  simulateSalary,
  getAbsences,
  createAbsence,
  validateAbsence,
  refuseAbsence,
  uploadAbsenceDocument,
  getAbsenceDocumentFile,
  deleteAbsence,
  sendAbsenceEmail,
  uploadEmployeeDocument,
  deleteEmployeeDocument,
  getEmployeeDocumentFile,
  getEmployeeDocumentTypes,
  getHRCosts,
  upsertHRCost,
  deleteHRCost,
  getApplications,
  createApplication,
  updateApplication,
  deleteApplication,
  hireApplicant,
  analyzeCV,
  getEvaluationGrids,
  validateEvaluation,
  getStructures,
  getHRDocuments,
  getHRDocumentFile,
  uploadHRDocument,
  deleteHRDocument,
  reindexHRDocument,
  getAllEmployeeDocuments,
  getHRSettings,
  getRcrLedger,
  adjustRcr,
  getDebtLedger,
  adjustDebt,
  getTransportProviders,
  getTrainingTypes,
  getEmployeeTrainings,
  createTraining,
  updateTraining,
  deleteTraining,
  uploadTrainingAttestation,
  getTrainingAttestation,
  getAllTrainings,
  getAllEmployeeInterviews,
  getEmployeeInterviewTypes,
  getEmployeeInterviewTemplates,
  getEmployeeInterviews,
  createEmployeeInterview,
  saveInterviewAnswers,
  updateInterviewStatus,
  deleteEmployeeInterview,
  getMedicalVisitTypes,
  getEmployeeMedicalVisits,
  createMedicalVisit,
  updateMedicalVisit,
  deleteMedicalVisit,
  uploadMedicalVisitCr,
  deleteMedicalVisitCr,
  getMedicalVisitCrUrl,
  getMedicalVisitCrFile,
  sendMedicalVisitRelance,
  getAllMedicalVisits,
  getMedicalVisitAlerts,
  getTrialPeriods,
  getTrialPeriodAlerts,
  getEmployeeTrialPeriods,
  createTrialPeriod,
  updateTrialPeriod,
  confirmTrialPeriod,
  ruptureTrialPeriod,
  deleteTrialPeriod,
  getHRAnalytics,
  generateEmployeeIcalToken,
  revokeEmployeeIcalToken,
  getEmployeePlanningEntries,
  uploadEmployeePhoto,
  deleteEmployeePhoto,
  getEmployeePhotoBlob,
  getOrgRoles,
  getOrgGroups,
} from "../api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import HRPayrollTab from "../components/hr/HRPayrollTab";
import HRDocViewer from "../components/hr/HRDocViewer";
import ExportButton from "../components/hr/ExportButton";
import EmployeeListExport from "../components/hr/EmployeeListExport";
import OrgChartTab from "../components/hr/OrgChartTab";
import PlanningTab from "../components/hr/PlanningTab";
import OfferLetterModal from "../components/hr/OfferLetterModal";
import GenerateDocumentModal from "../components/hr/templates/GenerateDocumentModal";
import DocumentRequestsTab from "../components/hr/DocumentRequestsTab";
import TransportCollectionTab from "../components/hr/TransportCollectionTab";
import HRDocumentTemplatesTab from "../components/hr/templates/HRDocumentTemplatesTab";
import LoadingSpinner from "../components/LoadingSpinner";
import { useToast } from "../contexts/NotificationContext";
import { useStructure } from "../contexts/StructureContext";
import { useImpersonate } from "../contexts/ImpersonateContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtCurrency = (v) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(v);

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR");
};

const CONTRACT_LABELS = {
  CDI: "CDI",
  CDD: "CDD",
  alternance: "Alternance",
  stage: "Stage",
  interimaire: "Intérimaire",
};
const CONTRACT_COLORS = {
  CDI: "bg-green-100 text-green-700",
  CDD: "bg-orange-100 text-orange-700",
  alternance: "bg-blue-100 text-blue-700",
  stage: "bg-purple-100 text-purple-700",
  interimaire: "bg-gray-100 text-gray-600",
};
const STATUS_LABELS = { active: "Actif", inactive: "Inactif" };
const ABSENCE_LABELS = {
  conge_paye: "Congé payé",
  arret_maladie: "Arrêt maladie",
  conge_maternite: "Maternité",
  conge_paternite: "Paternité",
  conge_parental: "Parental",
  rtt: "RTT",
  conge_sans_solde: "Sans solde",
  formation: "Formation",
  autre: "Autre",
};
const APP_STATUS_LABELS = {
  to_review: "À étudier",
  pending: "En attente",
  shortlisted: "Présélectionné",
  hired: "Embauché",
  rejected: "Refusé",
};
const APP_STATUS_COLORS = {
  to_review: "bg-blue-100 text-blue-700",
  pending: "bg-yellow-100 text-yellow-700",
  shortlisted: "bg-purple-100 text-purple-700",
  hired: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};
const COST_CATEGORIES = [
  "salaires_bruts",
  "charges_patronales",
  "mutuelle",
  "prevoyance",
  "formation",
  "interim",
  "recrutement",
  "autres",
];
const COST_CATEGORY_LABELS = {
  salaires_bruts: "Salaires bruts",
  charges_patronales: "Charges patronales",
  mutuelle: "Mutuelle",
  prevoyance: "Prévoyance",
  formation: "Formation",
  interim: "Intérim",
  recrutement: "Recrutement",
  autres: "Autres",
};

// ─── Helpers analyse CV ───────────────────────────────────────────────────────

const parseMarkdownTable = (markdown) => {
  if (!markdown) return [];
  const lines = markdown
    .split("\n")
    .filter((l) => l.startsWith("|") && !l.includes("---"));
  return lines
    .map((line) => {
      const cols = line
        .split("|")
        .map((c) => c.trim())
        .filter(Boolean);
      if (cols.length >= 3)
        return { criterion: cols[0], analysis: cols[1], score: cols[2] };
      if (cols.length === 2)
        return { criterion: cols[0], analysis: cols[1], score: "—" };
      return null;
    })
    .filter(Boolean)
    .filter((row) => row.criterion !== "Critère"); // exclure la ligne d'en-tête
};

// ─── Composants utilitaires ───────────────────────────────────────────────────

const Badge = ({ color, children }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}
  >
    {children}
  </span>
);

const Modal = ({ title, onClose, children, size = "md", footer }) => {
  const widths = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-white rounded-xl shadow-2xl w-full ${widths[size]} max-h-[90vh] flex flex-col`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"
          >
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

const Btn = ({
  onClick,
  disabled,
  loading,
  variant = "primary",
  size = "md",
  children,
  className = "",
}) => {
  const variants = {
    primary: "bg-primary text-white hover:bg-primary-dark",
    secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    danger: "bg-red-600 text-white hover:bg-red-700",
    ghost: "text-gray-600 hover:bg-gray-100",
  };
  const sizes = {
    sm: "px-2.5 py-1.5 text-xs",
    md: "px-3.5 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {loading && <Loader size={14} className="animate-spin" />}
      {children}
    </button>
  );
};

const FormField = ({ label, required, children, help }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {help && <p className="text-[11px] text-gray-500 mt-1">{help}</p>}
  </div>
);

const inputCls =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";

const ConfirmModal = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  danger = false,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <h3 className="font-semibold text-gray-800">{title}</h3>
        <p className="text-sm text-gray-500">{message}</p>
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={onCancel}>
            Annuler
          </Btn>
          <Btn variant={danger ? "danger" : "primary"} onClick={onConfirm}>
            Confirmer
          </Btn>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50">
      <Icon size={15} className="text-gray-400" />
      <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-2 gap-x-8 gap-y-4">{children}</div>
);

const CopyNameButton = ({ firstName, lastName }) => {
  const [copied, setCopied] = useState(false);
  const fullName = `${(lastName || '').toUpperCase()} ${firstName || ''}`.trim();
  const handleCopy = () => {
    navigator.clipboard.writeText(fullName).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button onClick={handleCopy} title="Copier le nom complet"
      className="opacity-60 hover:opacity-100 transition-opacity p-1 rounded text-gray-400 hover:text-primary hover:bg-gray-100">
      {copied ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
    </button>
  );
};

function formatPhone(phone) {
  if (!phone) return null;
  const digits = phone.replace(/[\s.\-()]/g, '').replace(/^(\+33|0033)/, '0');
  if (/^\d{10}$/.test(digits)) return digits.replace(/(\d{2})(?=\d)/g, '$1 ');
  return phone;
}

const Info = ({ label, value, span = 1, copyable = false }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <div className="flex items-center gap-1.5 group">
        <p className="text-sm text-gray-800">{value || "—"}</p>
        {copyable && value && (
          <button
            onClick={handleCopy}
            title={`Copier ${label.toLowerCase()}`}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-gray-400 hover:text-primary hover:bg-gray-100"
          >
            {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Onglet Dashboard ─────────────────────────────────────────────────────────

const ABSENCE_TYPE_LABELS_DASHBOARD = {
  conge_paye: "Congé payé",
  arret_maladie: "Arrêt maladie",
  maladie: "Maladie",
  conge_maternite: "Maternité",
  conge_paternite: "Paternité",
  conge_parental: "Parental",
  rtt: "RTT",
  conge_sans_solde: "Sans solde",
  formation: "Formation",
  autre: "Autre",
};

const fmtMonthShort = (dateStr) => {
  if (!dateStr) return "";
  return new Intl.DateTimeFormat("fr-FR", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  }).format(new Date(dateStr));
};

const DashboardTab = ({ onNavigate, showAllStructures = false }) => {
  const { currentStructure } = useStructure();
  const [data, setData] = useState(null);
  const [medicalAlerts, setMedicalAlerts] = useState({
    depasse: 0,
    bientot: 0,
    attente_cr: 0,
  });
  const [trialAlerts, setTrialAlerts] = useState({ expiree: 0, bientot: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const structureId = showAllStructures
    ? "all"
    : currentStructure?.id || undefined;
  const isMulti = showAllStructures || !currentStructure?.id;

  const loadDashboard = useCallback((silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    const params = structureId ? { structureId } : {};
    Promise.all([
      getHRDashboard(structureId),
      getMedicalVisitAlerts(params).catch(() => ({
        data: { depasse: 0, bientot: 0, attente_cr: 0 },
      })),
      getTrialPeriodAlerts(params).catch(() => ({
        data: { expiree: 0, bientot: 0 },
      })),
    ])
      .then(([dashRes, medRes, trialRes]) => {
        setData(dashRes.data);
        setMedicalAlerts(medRes.data);
        setTrialAlerts(trialRes.data);
        if (silent) toast.success('Dashboard mis à jour');
      })
      .catch((err) => {
        console.error(err);
        setError("Erreur lors du chargement du dashboard");
        toast.error("Erreur lors du chargement du dashboard");
      })
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [structureId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadDashboard(); }, [structureId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading)
    return (
      <div className="py-12">
        <LoadingSpinner size="large" text="Chargement..." />
      </div>
    );
  if (error) return <div className="text-red-600 text-sm p-4">{error}</div>;
  if (!data) return null;

  const alerts = data.alerts || {};
  const alertItems = [];
  if (alerts.chargedPayrollRatioAlert)
    alertItems.push({
      text: "Ratio coût salarial chargé / CA dépasse 70 %",
      level: "warning",
    });
  if (alerts.missingLastMonthCosts)
    alertItems.push({
      text: "Les charges RH du mois précédent ne sont pas saisies",
      level: "warning",
      tab: "charges",
    });
  if (alerts.cddExpiringCount > 0)
    alertItems.push({
      text: `${alerts.cddExpiringCount} CDD expire${alerts.cddExpiringCount > 1 ? "nt" : ""} dans les 30 prochains jours`,
      level: "warning",
    });
  if (alerts.missingTransportCount > 0)
    alertItems.push({
      text: `${alerts.missingTransportCount} employé${alerts.missingTransportCount > 1 ? "s" : ""} sans justificatif de transport ce mois`,
      level: "warning",
    });
  if (alerts.timesheetsNotSubmitted > 0)
    alertItems.push({
      text: `${alerts.timesheetsNotSubmitted} feuille${alerts.timesheetsNotSubmitted > 1 ? "s" : ""} de temps non soumise${alerts.timesheetsNotSubmitted > 1 ? "s" : ""}${alerts.timesheetsAlertLevel === "danger" ? " — urgent" : ""}`,
      level: alerts.timesheetsAlertLevel || "warning",
    });
  if (alerts.trainingsExpiredCount > 0)
    alertItems.push({
      text: `${alerts.trainingsExpiredCount} employé${alerts.trainingsExpiredCount > 1 ? "s" : ""} avec habilitation${alerts.trainingsExpiredCount > 1 ? "s" : ""} expirée${alerts.trainingsExpiredCount > 1 ? "s" : ""}`,
      level: "danger",
      tab: "formations",
    });
  if (alerts.trainingsSoonCount > 0)
    alertItems.push({
      text: `${alerts.trainingsSoonCount} employé${alerts.trainingsSoonCount > 1 ? "s" : ""} avec recyclage à prévoir dans les 3 prochains mois`,
      level: "warning",
      tab: "formations",
    });
  if (medicalAlerts.depasse > 0)
    alertItems.push({
      text: `${medicalAlerts.depasse} visite${medicalAlerts.depasse > 1 ? "s" : ""} médicale${medicalAlerts.depasse > 1 ? "s" : ""} dépassée${medicalAlerts.depasse > 1 ? "s" : ""}`,
      level: "danger",
      tab: "visites-medicales",
      statut: "depasse",
    });
  if (medicalAlerts.bientot > 0)
    alertItems.push({
      text: `${medicalAlerts.bientot} visite${medicalAlerts.bientot > 1 ? "s" : ""} médicale${medicalAlerts.bientot > 1 ? "s" : ""} à prévoir dans les 30 prochains jours`,
      level: "warning",
      tab: "visites-medicales",
      statut: "bientot",
    });
  if (medicalAlerts.attente_cr > 0)
    alertItems.push({
      text: `${medicalAlerts.attente_cr} visite${medicalAlerts.attente_cr > 1 ? "s" : ""} médicale${medicalAlerts.attente_cr > 1 ? "s" : ""} en attente de CR`,
      level: "warning",
      tab: "visites-medicales",
      statut: "attente_cr",
    });
  if (trialAlerts.expiree > 0)
    alertItems.push({
      text: `${trialAlerts.expiree} période${trialAlerts.expiree > 1 ? "s" : ""} d'essai expirée${trialAlerts.expiree > 1 ? "s" : ""} sans décision`,
      level: "danger",
      tab: "periodes-essai",
      statut: "expiree",
    });
  if (trialAlerts.bientot > 0)
    alertItems.push({
      text: `${trialAlerts.bientot} période${trialAlerts.bientot > 1 ? "s" : ""} d'essai se terminent dans les 15 prochains jours`,
      level: "warning",
      tab: "periodes-essai",
      statut: "bientot",
    });

  const ratioColor = alerts.chargedPayrollRatioAlert
    ? "text-red-600"
    : "text-green-600";

  // Fusion CDD + absences finissant bientôt pour Zone 5
  const echeances = [
    ...(data.cddExpiring || []).map((e) => ({
      type: "cdd",
      date: e.cddEndDate,
      label: `CDD — ${e.firstName} ${e.lastName}`,
      employeeId: e.id,
      structureName: e.structureName,
    })),
    ...(data.absencesEndingSoon || []).map((a) => ({
      type: "absence",
      date: a.endDate,
      label: `Retour — ${a.employee.firstName} ${a.employee.lastName}`,
      employeeId: a.employee.id,
      structureName: a.employee.structureName,
    })),
  ].sort((a, b) => new Date(a.date) - new Date(b.date));

  const KpiCard = ({ icon: Icon, iconClass = 'text-primary', label, value, valueClass = 'text-gray-800', tab }) => {
    const clickable = !!tab && !!onNavigate;
    const Tag = clickable ? 'button' : 'div';
    return (
      <Tag
        onClick={clickable ? () => onNavigate(null, tab) : undefined}
        className={[
          'bg-white rounded-xl border border-gray-200 p-4 text-left w-full',
          clickable ? 'cursor-pointer hover:border-primary hover:shadow-sm transition-all group' : '',
        ].join(' ')}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Icon size={16} className={iconClass} />
            <span className="text-xs text-gray-500">{label}</span>
          </div>
          {clickable && <ArrowRight size={13} className="text-gray-300 group-hover:text-primary transition-colors" />}
        </div>
        <div className={`text-2xl font-bold ${valueClass}`}>{value}</div>
      </Tag>
    );
  };

  return (
    <div className="space-y-6">
      {/* ── En-tête dashboard ── */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-600">Vue d'ensemble</h2>
        <button
          onClick={() => loadDashboard(true)}
          disabled={refreshing}
          title="Actualiser le dashboard"
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary hover:bg-gray-100 px-2 py-1 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Actualisation…' : 'Actualiser'}
        </button>
      </div>

      {/* ── Zone 1 : Alertes ── */}
      {alertItems.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-2">
          <Check size={18} className="text-green-600 shrink-0" />
          <span className="text-sm font-medium text-green-800">
            Tout est à jour
          </span>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={18} className="text-amber-600 shrink-0" />
            <span className="font-semibold text-amber-800 text-sm">
              Alertes
            </span>
          </div>
          <ul className="space-y-1">
            {alertItems.map((a, i) => (
              <li
                key={i}
                className={`text-sm ${a.level === "danger" ? "text-red-700 font-medium" : "text-amber-700"}`}
              >
                {"• "}
                {a.tab ? (
                  <button
                    onClick={() =>
                      onNavigate &&
                      onNavigate(
                        null,
                        a.tab,
                        a.statut ? { statut: a.statut } : undefined,
                      )
                    }
                    className="underline underline-offset-2 hover:opacity-80"
                  >
                    {a.text}
                  </button>
                ) : (
                  a.text
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Zone 2 : KPI ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard
          icon={Users} label="Employés actifs"
          value={data.activeEmployeesCount ?? "—"}
          tab="employees"
        />
        <KpiCard
          icon={DollarSign} label="Coût salarial chargé"
          value={fmtCurrency(data.chargedPayroll)}
          tab="charges"
        />
        <KpiCard
          icon={TrendingUp} iconClass={ratioColor} label="Ratio coût / CA"
          value={data.chargedPayrollRatio != null ? `${data.chargedPayrollRatio} %` : "—"}
          valueClass={ratioColor}
          tab="analytique"
        />
        <KpiCard
          icon={Calendar} label="Absences en cours"
          value={data.absencesTodayCount ?? 0}
          tab="dossiers"
        />
        <KpiCard
          icon={Clock} iconClass="text-accent" label="CDD expirant ≤ 30j"
          value={alerts.cddExpiringCount ?? 0}
          tab="employees"
        />
        <KpiCard
          icon={FileText}
          iconClass={alerts.missingTransportCount > 0 ? "text-amber-600" : "text-gray-400"}
          label="Justificatifs transport manquants"
          value={data.missingTransportCount ?? 0}
          valueClass={alerts.missingTransportCount > 0 ? "text-amber-700" : "text-gray-800"}
          tab="dossiers"
        />
      </div>

      {/* ── Zone 4 : Absents aujourd'hui ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-700 text-sm">
            Absents aujourd'hui{" "}
            {data.absencesTodayCount > 0 && (
              <span className="text-gray-400 font-normal">
                ({data.absencesTodayCount})
              </span>
            )}
          </h3>
        </div>
        {data.absencesToday?.length === 0 ? (
          <p className="text-sm text-gray-400 px-4 py-3">
            Aucune absence en cours.
          </p>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">
                    Nom
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">
                    Type
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">
                    Retour prévu
                  </th>
                  {isMulti && (
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">
                      Structure
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.absencesToday.map((a) => (
                  <tr
                    key={a.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() =>
                      onNavigate && onNavigate(a.employee.id, "absences")
                    }
                  >
                    <td className="px-4 py-2.5 font-medium text-gray-800">
                      {a.employee.firstName} {a.employee.lastName}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {ABSENCE_TYPE_LABELS_DASHBOARD[a.type] || a.type}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {fmtDate(a.endDate)}
                    </td>
                    {isMulti && (
                      <td className="px-4 py-2.5 text-gray-500">
                        {a.employee.structureName || "—"}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {data.absencesTodayCount > 10 && (
              <div className="px-4 py-2 border-t border-gray-100">
                <button
                  onClick={() => onNavigate && onNavigate(null, "absences")}
                  className="text-xs text-primary hover:underline"
                >
                  Voir toutes les absences ({data.absencesTodayCount})
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Zone 5 : Échéances 30 jours ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-700 text-sm">
            Prochaines échéances (30 jours)
          </h3>
        </div>
        {echeances.length === 0 ? (
          <p className="text-sm text-gray-400 px-4 py-3">
            Aucune échéance dans les 30 prochains jours.
          </p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {echeances.map((e, i) => (
              <li
                key={i}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm"
                onClick={() => onNavigate && onNavigate(e.employeeId)}
              >
                <span
                  className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold shrink-0 ${e.type === "cdd" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}
                >
                  {e.type === "cdd" ? "C" : "A"}
                </span>
                <span className="flex-1 text-gray-800">{e.label}</span>
                <span className="text-gray-500 shrink-0">
                  {fmtDate(e.date)}
                </span>
                {isMulti && e.structureName && (
                  <span className="text-xs text-gray-400 shrink-0">
                    {e.structureName}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ─── Autocomplétion ville / code postal (geo.api.gouv.fr) ────────────────────

const CommuneField = ({
  postalValue,
  cityValue,
  onPostalChange,
  onCityChange,
  postalLabel = "Code postal",
  cityLabel = "Ville",
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [mode, setMode] = useState(null); // 'postal' | 'city'
  const timer = useRef(null);

  const search = async (type, val) => {
    if (!val || val.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const url =
        type === "postal"
          ? `https://geo.api.gouv.fr/communes?codePostal=${val}&fields=nom,codesPostaux&limit=8`
          : `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(val)}&fields=nom,codesPostaux&boost=population&limit=8`;
      const res = await fetch(url);
      const list = await res.json();
      setSuggestions(
        type === "postal"
          ? list.map((c) => ({ city: c.nom, postal: val }))
          : list.map((c) => ({ city: c.nom, postal: c.codesPostaux[0] || "" })),
      );
    } catch {
      setSuggestions([]);
    }
  };

  const handleChange = (type, val) => {
    type === "postal" ? onPostalChange(val) : onCityChange(val);
    setMode(type);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(type, val), 300);
  };

  const pick = (s) => {
    onPostalChange(s.postal);
    onCityChange(s.city);
    setSuggestions([]);
    setMode(null);
  };

  const dismiss = () =>
    setTimeout(() => {
      setSuggestions([]);
      setMode(null);
    }, 150);

  const Dropdown = ({ active }) =>
    active && suggestions.length > 0 ? (
      <ul className="absolute z-20 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
        {suggestions.map((s, i) => (
          <li
            key={i}
            onMouseDown={() => pick(s)}
            className="px-3 py-2 text-sm cursor-pointer hover:bg-primary/5 flex justify-between items-center"
          >
            <span>{s.city}</span>
            <span className="text-gray-400 text-xs">{s.postal}</span>
          </li>
        ))}
      </ul>
    ) : null;

  return (
    <>
      <FormField label={postalLabel}>
        <div className="relative">
          <input
            className={inputCls}
            value={postalValue}
            onChange={(e) => handleChange("postal", e.target.value)}
            onBlur={dismiss}
          />
          <Dropdown active={mode === "postal"} />
        </div>
      </FormField>
      <FormField label={cityLabel}>
        <div className="relative">
          <input
            className={inputCls}
            value={cityValue}
            onChange={(e) => handleChange("city", e.target.value)}
            onBlur={dismiss}
          />
          <Dropdown active={mode === "city"} />
        </div>
      </FormField>
    </>
  );
};

// ─── Formulaire Employé ───────────────────────────────────────────────────────

const EmployeeForm = ({ initial = {}, structures = [], onSave, onClose }) => {
  const [form, setForm] = useState({
    firstName: initial.firstName || "",
    lastName: initial.lastName || "",
    civilite: initial.civilite || "",
    birthName: initial.birthName || "",
    otherFirstNames: initial.otherFirstNames || "",
    birthDate: initial.birthDate ? initial.birthDate.split("T")[0] : "",
    inseeNumber: initial.inseeNumber || "",
    birthPostalCode: initial.birthPostalCode || "",
    birthCity: initial.birthCity || "",
    birthCountry: initial.birthCountry || "",
    email: initial.email || "",
    phone: initial.phone || "",
    address: initial.address || "",
    postalCode: initial.postalCode || "",
    city: initial.city || "",
    country: initial.country || "",
    position: initial.position || "",
    contractType: initial.contractType || "CDI",
    status: initial.status || "active",
    startDate: initial.startDate ? initial.startDate.split("T")[0] : "",
    cddEndDate: initial.cddEndDate ? initial.cddEndDate.split("T")[0] : "",
    workHours: initial.workHours ?? 35,
    hourlyRate: initial.hourlyRate ?? "",
    initialSalary: initial.currentSalary ?? "",
    conventionCategory: initial.conventionCategory || "",
    conventionEchelon: initial.conventionEchelon || "",
    transportAmount: initial.transportAmount ?? "",
    // Tri-state : "" = défaut (selon contract_type), "true" = forcer inclus, "false" = forcer exclu
    includeInPayroll: initial.includeInPayroll === true ? "true"
                    : initial.includeInPayroll === false ? "false"
                    : "",
    mutuelleType: initial.mutuelleType || "en_attente",
    mutuelleValidUntil: initial.mutuelleValidUntil
      ? new Date(initial.mutuelleValidUntil).toISOString().split("T")[0]
      : "",
    mutuelleDocument: initial.mutuelleDocument || "",
    planningPdfStructures:
      initial.planningPdfStructures ||
      (initial.structureId ? JSON.stringify([initial.structureId]) : ""),
    notes: initial.notes || "",
    structureId:
      initial.structureId ||
      (structures[0]?.id ? String(structures[0].id) : ""),
    secondaryStructureIds:
      initial.secondaryStructures?.map(s => s.structureId) || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { hourlyRate: _hr, ...formRest } = form;
      const payload = {
        ...formRest,
        workHours: Number(form.workHours),
        // Date de fin acceptée pour tous les types de contrat (CDI inclus).
        // Vide → null explicite pour permettre à l'admin d'effacer une date.
        cddEndDate: form.cddEndDate || null,
        secondaryStructureIds: form.secondaryStructureIds || [],
      };
      if (initial.id) {
        const { initialSalary: _s, ...updatePayload } = payload;
        await updateEmployee(initial.id, updatePayload);
        toast.success("Employé modifié");
      } else {
        await createEmployee(payload);
        toast.success("Employé ajouté");
      }
      onSave();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Erreur lors de l'enregistrement",
      );
      toast.error("Erreur lors de l'enregistrement de l'employé");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg">
          {error}
        </div>
      )}
      {structures.length > 1 && (
        <FormField label="Structure principale" required>
          <select
            className={inputCls}
            value={form.structureId}
            onChange={(e) => set("structureId", e.target.value)}
            required
          >
            {structures.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </FormField>
      )}
      {structures.length > 1 && (
        <FormField label="Structures secondaires">
          <div className="flex flex-wrap gap-2">
            {structures
              .filter(s => s.id !== form.structureId)
              .map(s => {
                const isSelected = (form.secondaryStructureIds || []).includes(s.id);
                return (
                  <label
                    key={s.id}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition ${isSelected ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={isSelected}
                      onChange={() => {
                        const current = form.secondaryStructureIds || [];
                        set(
                          'secondaryStructureIds',
                          isSelected ? current.filter(id => id !== s.id) : [...current, s.id]
                        );
                      }}
                    />
                    {s.name}
                  </label>
                );
              })}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            L'employé apparaîtra dans l'organigramme de chaque structure cochée.
          </p>
        </FormField>
      )}
      {/* État civil */}
      <div className="grid grid-cols-3 gap-3">
        <FormField label="Civilité">
          <select
            className={inputCls}
            value={form.civilite}
            onChange={(e) => set("civilite", e.target.value)}
          >
            <option value="">—</option>
            <option value="M.">M.</option>
            <option value="Mme">Mme</option>
          </select>
        </FormField>
        <FormField label="Prénom" required>
          <input
            className={inputCls}
            value={form.firstName}
            onChange={(e) => set("firstName", e.target.value)}
            required
          />
        </FormField>
        <FormField label="Nom" required>
          <input
            className={inputCls}
            value={form.lastName}
            onChange={(e) => set("lastName", e.target.value)}
            required
          />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Autres prénoms">
          <input
            className={inputCls}
            value={form.otherFirstNames}
            onChange={(e) => set("otherFirstNames", e.target.value)}
            placeholder="Tous les prénoms"
          />
        </FormField>
        <FormField label="Nom de naissance">
          <input
            className={inputCls}
            value={form.birthName}
            onChange={(e) => set("birthName", e.target.value)}
          />
        </FormField>
        <FormField label="Date de naissance">
          <input
            className={inputCls}
            type="date"
            value={form.birthDate}
            onChange={(e) => set("birthDate", e.target.value)}
          />
        </FormField>
        <FormField label="N° INSEE (carte vitale)">
          <input
            className={inputCls}
            value={form.inseeNumber}
            onChange={(e) => set("inseeNumber", e.target.value)}
            placeholder="Ex: 1 85 12 75 123 456 78"
          />
        </FormField>
        <CommuneField
          postalValue={form.birthPostalCode}
          cityValue={form.birthCity}
          onPostalChange={(v) => set("birthPostalCode", v)}
          onCityChange={(v) => set("birthCity", v)}
          postalLabel="Code postal de naissance"
          cityLabel="Commune de naissance"
        />
        <FormField label="Pays de naissance">
          <input
            className={inputCls}
            value={form.birthCountry}
            onChange={(e) => set("birthCountry", e.target.value)}
            placeholder="Ex: France"
          />
        </FormField>
      </div>
      {/* Contact */}
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Email">
          <input
            className={inputCls}
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </FormField>
        <FormField label="Téléphone">
          <input
            className={inputCls}
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </FormField>
      </div>
      <FormField label="Poste" required>
        <input
          className={inputCls}
          value={form.position}
          onChange={(e) => set("position", e.target.value)}
          required
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Type de contrat">
          <select
            className={inputCls}
            value={form.contractType}
            onChange={(e) => set("contractType", e.target.value)}
          >
            {Object.entries(CONTRACT_LABELS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Statut">
          <select
            className={inputCls}
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
          >
            {Object.entries(STATUS_LABELS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <FormField
          label="Inclure dans la paie et les feuilles de temps"
          help={
            form.includeInPayroll === ''
              ? `Par défaut : CDI / CDD / alternance ✅, Prestataire / Stage ❌. Actuel selon « ${form.contractType || '—'} » : ${
                  ['CDI', 'CDD', 'alternance'].includes(form.contractType) ? '✅ inclus' : '❌ exclu'
                }`
              : form.includeInPayroll === 'true'
              ? '✅ Forcé inclus (override du défaut)'
              : '❌ Forcé exclu (override du défaut)'
          }
        >
          <select
            className={inputCls}
            value={form.includeInPayroll}
            onChange={(e) => set('includeInPayroll', e.target.value)}
          >
            <option value="">Par défaut (selon type de contrat)</option>
            <option value="true">Forcer inclus</option>
            <option value="false">Forcer exclu</option>
          </select>
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Date d'entrée" required>
          <input
            className={inputCls}
            type="date"
            value={form.startDate}
            onChange={(e) => set("startDate", e.target.value)}
            required
          />
        </FormField>
        <FormField
          label={getEndDateLabel(form.contractType)}
          required={isEndDateMandatory(form.contractType)}
          help={form.contractType === "CDI" ? "Optionnel — l'employé passera inactif automatiquement après cette date" : undefined}
        >
          <input
            className={inputCls}
            type="date"
            value={form.cddEndDate}
            onChange={(e) => set("cddEndDate", e.target.value)}
            required={isEndDateMandatory(form.contractType)}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Heures/semaine">
          <input
            className={inputCls}
            type="number"
            min={0}
            max={48}
            value={form.workHours}
            onChange={(e) => set("workHours", e.target.value)}
          />
        </FormField>
        <FormField label="Taux horaire (€)">
          {(() => {
            const sal = initial.id
              ? initial.currentSalary
              : parseFloat(form.initialSalary);
            const wh = parseFloat(form.workHours);
            const hr =
              sal && wh > 0 ? (sal / ((wh * 52) / 12)).toFixed(4) : null;
            return (
              <div className={`${inputCls} bg-gray-50 text-gray-600`}>
                {hr ? (
                  `${hr} €/h`
                ) : (
                  <span className="text-gray-400 italic">
                    calculé depuis le salaire
                  </span>
                )}
              </div>
            );
          })()}
        </FormField>
        {!initial.id && (
          <FormField label="Salaire brut mensuel (€)">
            <input
              className={inputCls}
              type="number"
              min={0}
              value={form.initialSalary}
              onChange={(e) => set("initialSalary", e.target.value)}
              placeholder="Ex: 1800"
            />
          </FormField>
        )}
        {initial.id && initial.currentSalary && (
          <FormField label="Salaire actuel">
            <div className={`${inputCls} bg-gray-50 text-gray-600`}>
              {new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
              }).format(initial.currentSalary)}
              <span className="ml-2 text-xs text-gray-400">
                (modifiable via l'onglet Salaires)
              </span>
            </div>
          </FormField>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Catégorie convention">
          <input
            className={inputCls}
            value={form.conventionCategory}
            onChange={(e) => set("conventionCategory", e.target.value)}
            placeholder="Ex: Technicien"
          />
        </FormField>
        <FormField label="Échelon">
          <input
            className={inputCls}
            value={form.conventionEchelon}
            onChange={(e) => set("conventionEchelon", e.target.value)}
            placeholder="Ex: 2"
          />
        </FormField>
        <FormField label="Titre transport (€/mois)">
          <input
            type="number"
            step="0.01"
            min="0"
            className={inputCls}
            value={form.transportAmount}
            onChange={(e) => set("transportAmount", e.target.value)}
            placeholder="Ex: 86.34"
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <FormField label="Mutuelle">
          <select
            className={inputCls}
            value={form.mutuelleType}
            onChange={(e) => set("mutuelleType", e.target.value)}
          >
            <option value="en_attente">En attente (à relancer)</option>
            <option value="entreprise">Mutuelle entreprise</option>
            <option value="conjoint">Mutuelle conjoint</option>
          </select>
        </FormField>
        {form.mutuelleType === "conjoint" && (
          <FormField label="Validité mutuelle conjoint">
            <input
              type="date"
              className={inputCls}
              value={form.mutuelleValidUntil}
              onChange={(e) => set("mutuelleValidUntil", e.target.value)}
            />
          </FormField>
        )}
        <FormField label="Justificatif mutuelle">
          <input
            type="text"
            className={inputCls}
            value={form.mutuelleDocument}
            onChange={(e) => set("mutuelleDocument", e.target.value)}
            placeholder="Nom du document"
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <FormField label="Plannings PDF (structures)">
          <div className="flex flex-wrap gap-2">
            {structures.map((s) => {
              const selected = form.planningPdfStructures
                ? typeof form.planningPdfStructures === "string"
                  ? JSON.parse(form.planningPdfStructures || "[]")
                  : form.planningPdfStructures
                : [];
              const isSelected =
                Array.isArray(selected) && selected.includes(s.id);
              return (
                <label
                  key={s.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition ${isSelected ? "bg-indigo-100 border-indigo-300 text-indigo-700" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={isSelected}
                    onChange={() => {
                      const current = Array.isArray(selected)
                        ? [...selected]
                        : [];
                      const updated = isSelected
                        ? current.filter((id) => id !== s.id)
                        : [...current, s.id];
                      set("planningPdfStructures", JSON.stringify(updated));
                    }}
                  />
                  {s.name}
                </label>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Vide = apparait dans le planning de sa structure. Cochez pour
            personnaliser.
          </p>
        </FormField>
      </div>
      <FormField label="Adresse">
        <input
          className={inputCls}
          value={form.address}
          onChange={(e) => set("address", e.target.value)}
        />
      </FormField>
      <div className="grid grid-cols-3 gap-3">
        <FormField label="Code postal">
          <input
            className={inputCls}
            value={form.postalCode}
            onChange={(e) => set("postalCode", e.target.value)}
          />
        </FormField>
        <FormField label="Ville">
          <input
            className={inputCls}
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
          />
        </FormField>
        <FormField label="Pays">
          <input
            className={inputCls}
            value={form.country}
            onChange={(e) => set("country", e.target.value)}
            placeholder="Ex: France"
          />
        </FormField>
      </div>
      <FormField label="Notes">
        <textarea
          className={inputCls}
          rows={2}
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
        />
      </FormField>
      <div className="flex gap-2 justify-end pt-2">
        <Btn variant="secondary" onClick={onClose}>
          Annuler
        </Btn>
        <Btn type="submit" loading={saving}>
          Enregistrer
        </Btn>
      </div>
    </form>
  );
};

// ─── Panneau détail Employé (migration progressive) ──────────────────────────

const EmployeeDetail = ({ employee, onClose }) => {
  const [tab, setTab] = useState("info");
  const [rcrData, setRcrData] = useState({ balance: 0, ledger: [] });
  const [debtData, setDebtData] = useState({ balance: 0, ledger: [] });
  const [adjustForm, setAdjustForm] = useState({
    type: "",
    amount: "",
    comment: "",
  });
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (tab !== "counters") return;
    setLoadingCounters(true);
    Promise.all([
      getRcrLedger(employee.id)
        .then((res) => setRcrData(res.data))
        .catch(() => setRcrData({ balance: 0, ledger: [] })),
      getDebtLedger(employee.id)
        .then((res) => setDebtData(res.data))
        .catch(() => setDebtData({ balance: 0, ledger: [] })),
    ]).finally(() => setLoadingCounters(false));
  }, [tab, employee.id]);

  const handleAdjustCounter = async (type) => {
    if (!adjustForm.amount || adjustForm.type !== type) return;
    setAdjusting(true);
    try {
      if (type === "rcr") {
        await adjustRcr(employee.id, {
          amount: Number(adjustForm.amount),
          comment: adjustForm.comment || "Ajustement manuel",
        });
        const res = await getRcrLedger(employee.id);
        setRcrData(res.data);
        toast.success("Compteur RCR ajusté");
      } else {
        await adjustDebt(employee.id, {
          amount: Number(adjustForm.amount),
          comment: adjustForm.comment || "Ajustement manuel",
        });
        const res = await getDebtLedger(employee.id);
        setDebtData(res.data);
        toast.success("Compteur dette ajusté");
      }
      setAdjustForm({ type: "", amount: "", comment: "" });
    } catch {
      toast.error("Erreur ajustement compteur");
    } finally {
      setAdjusting(false);
    }
  };

  const tabClass = (t) =>
    `px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${tab === t ? "bg-white text-primary border-b-2 border-primary" : "text-gray-500 hover:text-gray-700"}`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 bg-white rounded-t-xl border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              {employee.lastName} {employee.firstName}
            </h2>
            <p className="text-sm text-gray-500">
              {employee.position || "—"} — {employee.contractType || "—"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex gap-1 px-4 pt-2 bg-gray-50">
          {[
            ["info", "Infos"],
            ["salary", "Salaires"],
            ["absences", "Absences"],
            ["counters", "Compteurs"],
            ["documents", "Documents"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={tabClass(k)}>
              {l}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {tab === "info" && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-400">Nom</div>
                  <div className="font-medium text-gray-800">
                    {employee.lastName} {employee.firstName}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Poste</div>
                  <div className="font-medium text-gray-800">
                    {employee.position || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Email</div>
                  <div className="font-medium text-gray-800">
                    {employee.email || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Téléphone</div>
                  <div className="font-medium text-gray-800">
                    {employee.phone || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Type contrat</div>
                  <div className="font-medium text-gray-800">
                    {employee.contractType || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Date d'entrée</div>
                  <div className="font-medium text-gray-800">
                    {fmtDate(employee.startDate)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {(tab === "salary" || tab === "absences" || tab === "documents") && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <p className="text-sm text-gray-500">
                Cette section sera ajoutée prochainement dans la migration du
                module RH.
              </p>
            </div>
          )}

          {tab === "counters" && (
            <div className="space-y-6">
              {loadingCounters ? (
                <div className="flex justify-center py-6">
                  <Loader className="animate-spin text-primary" size={20} />
                </div>
              ) : (
                <>
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">
                        Compteur RCR
                      </h4>
                      <span
                        className={`text-lg font-bold ${Number(rcrData.balance) > 0 ? "text-green-600" : Number(rcrData.balance) < 0 ? "text-red-600" : "text-gray-400"}`}
                      >
                        {Number(rcrData.balance || 0).toFixed(2)}h
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.25"
                        placeholder="Heures (+/-)"
                        value={
                          adjustForm.type === "rcr" ? adjustForm.amount : ""
                        }
                        onChange={(e) =>
                          setAdjustForm({
                            type: "rcr",
                            amount: e.target.value,
                            comment:
                              adjustForm.type === "rcr"
                                ? adjustForm.comment
                                : "",
                          })
                        }
                        className={`${inputCls} !w-28 !py-1.5`}
                      />
                      <input
                        type="text"
                        placeholder="Commentaire"
                        value={
                          adjustForm.type === "rcr" ? adjustForm.comment : ""
                        }
                        onChange={(e) =>
                          setAdjustForm((f) => ({
                            ...f,
                            type: "rcr",
                            comment: e.target.value,
                          }))
                        }
                        className={`${inputCls} !py-1.5`}
                      />
                      <Btn
                        size="sm"
                        onClick={() => handleAdjustCounter("rcr")}
                        disabled={
                          !adjustForm.amount || adjustForm.type !== "rcr"
                        }
                        loading={adjusting && adjustForm.type === "rcr"}
                      >
                        Ajuster
                      </Btn>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">
                        Heures à rattraper (dette)
                      </h4>
                      <span
                        className={`text-lg font-bold ${Number(debtData.balance) > 0 ? "text-amber-600" : "text-gray-400"}`}
                      >
                        {Number(debtData.balance || 0).toFixed(2)}h
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.25"
                        placeholder="Heures (+/-)"
                        value={
                          adjustForm.type === "debt" ? adjustForm.amount : ""
                        }
                        onChange={(e) =>
                          setAdjustForm({
                            type: "debt",
                            amount: e.target.value,
                            comment:
                              adjustForm.type === "debt"
                                ? adjustForm.comment
                                : "",
                          })
                        }
                        className={`${inputCls} !w-28 !py-1.5`}
                      />
                      <input
                        type="text"
                        placeholder="Commentaire"
                        value={
                          adjustForm.type === "debt" ? adjustForm.comment : ""
                        }
                        onChange={(e) =>
                          setAdjustForm((f) => ({
                            ...f,
                            type: "debt",
                            comment: e.target.value,
                          }))
                        }
                        className={`${inputCls} !py-1.5`}
                      />
                      <Btn
                        size="sm"
                        onClick={() => handleAdjustCounter("debt")}
                        disabled={
                          !adjustForm.amount || adjustForm.type !== "debt"
                        }
                        loading={adjusting && adjustForm.type === "debt"}
                      >
                        Ajuster
                      </Btn>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Onglet Employés (design du projet stage) ────────────────────────────────

function EmployeeRowAvatar({ employee }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const initials = `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase();

  useEffect(() => {
    if (!employee.photoUrl) { setBlobUrl(null); return; }
    let url;
    let cancelled = false;
    getEmployeePhotoBlob(employee.id)
      .then((res) => { if (!cancelled) { url = URL.createObjectURL(res.data); setBlobUrl(url); } })
      .catch(() => { if (!cancelled) setBlobUrl(null); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [employee.id, employee.photoUrl]);

  if (blobUrl) return (
    <img src={blobUrl} alt="" className="w-8 h-8 rounded-full object-cover border border-gray-200 flex-shrink-0" />
  );
  return (
    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs flex-shrink-0">
      {initials}
    </div>
  );
}

const STAGE_CONTRACT_TYPES = [
  "CDI",
  "CDD",
  "Alternance",
  "Stage",
  "Intérimaire",
  "Prestataire",
];

// Tous les types autres que CDI peuvent porter une date de fin (champ visible)
const requiresEndDate = (contractType) =>
  Boolean(contractType) && contractType !== "CDI";

// Date de fin obligatoire (légal) : CDD, Alternance, Stage uniquement.
// Intérimaire et Prestataire restent optionnels.
const END_DATE_MANDATORY_TYPES = new Set(["CDD", "Alternance", "Stage"]);
const isEndDateMandatory = (contractType) =>
  END_DATE_MANDATORY_TYPES.has(contractType);

// Label de la date de fin selon le type de contrat
const getEndDateLabel = (contractType) => {
  switch (contractType) {
    case "CDD":
      return "Fin de CDD";
    case "Alternance":
      return "Fin d'alternance";
    case "Stage":
      return "Fin de stage";
    case "Intérimaire":
      return "Fin de mission";
    case "Prestataire":
      return "Fin de prestation";
    default:
      return "Fin de contrat";
  }
};

// Label court pour le badge "expire bientôt" (liste employés)
const getEndDateBadgeLabel = (contractType) => {
  switch (contractType) {
    case "CDD":
      return "CDD";
    case "Alternance":
      return "Alternance";
    case "Stage":
      return "Stage";
    case "Intérimaire":
      return "Mission";
    case "Prestataire":
      return "Prestation";
    default:
      return "Contrat";
  }
};
const STAGE_STATUS_OPTIONS = [
  { value: "active", label: "Actif" },
  { value: "inactive", label: "Inactif" },
];
const STAGE_MUTUELLE_OPTIONS = [
  { value: "en_attente", label: "En attente (à relancer)" },
  { value: "entreprise", label: "Mutuelle entreprise" },
  { value: "conjoint", label: "Mutuelle conjoint" },
];
const STAGE_EMPTY_FORM = {
  civilite: "",
  firstName: "",
  lastName: "",
  otherFirstNames: "",
  birthName: "",
  birthDate: "",
  inseeNumber: "",
  birthPostalCode: "",
  birthCity: "",
  birthCountry: "",
  email: "",
  phone: "",
  address: "",
  postalCode: "",
  city: "",
  country: "",
  position: "",
  contractType: "CDI",
  status: "active",
  startDate: "",
  endDate: "",
  workHours: 35,
  hourlyRate: "",
  initialSalary: "",
  conventionCategory: "",
  conventionEchelon: "",
  transportAmount: "",
  mutuelleType: "en_attente",
  mutuelleValidUntil: "",
  mutuelleDocument: "",
  notes: "",
  role: "",
  isContinuiteDirection: false,
  secondaryStructureIds: [],
  nonRemunere: false,
  planningPdfStructures: "",
};


const EmployeesTab = ({ showAllStructures = false, onNavigate, pendingEmployeeId, onPendingConsumed }) => {
  const toast = useToast();
  const { currentStructure, structures } = useStructure();
  const [empView, setEmpView] = useState("list"); // "list" | "orgchart"
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [sortBy, setSortBy] = useState({ col: 'lastName', dir: 'asc' });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(STAGE_EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(() => {
    const target = sessionStorage.getItem('hr_open_employee');
    if (target) { sessionStorage.removeItem('hr_open_employee'); return target; }
    return null;
  });
  const [cameFrom] = useState(() => {
    const cf = sessionStorage.getItem('hr_came_from');
    if (cf) { sessionStorage.removeItem('hr_came_from'); return cf; }
    return null;
  });
  const [autoEdit, setAutoEdit] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const [absentEmployeeIds, setAbsentEmployeeIds] = useState(new Set());

  const handleBackFromDetail = useCallback(() => {
    setSelectedEmployeeId(null);
    setAutoEdit(false);
  }, []);

  useEffect(() => {
    if (pendingEmployeeId) {
      setSelectedEmployeeId(pendingEmployeeId);
      onPendingConsumed?.();
    }
  }, [pendingEmployeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const structureId = showAllStructures
        ? "all"
        : currentStructure?.id || "all";
      const { data } = await getEmployees({ structureId });
      setEmployees(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Erreur chargement employés");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStructure?.id, showAllStructures]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const structureId = showAllStructures ? 'all' : currentStructure?.id || 'all';
    getHRDashboard(structureId)
      .then(res => {
        const ids = (res.data?.absencesToday || []).map(a => a.employee?.id).filter(Boolean);
        setAbsentEmployeeIds(new Set(ids));
      })
      .catch(() => {});
  }, [currentStructure?.id, showAllStructures]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFormField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditEmployee(null);
    setForm(STAGE_EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (emp) => {
    // Ouvre le formulaire complet de la page détail employé
    setAutoEdit(true);
    setSelectedEmployeeId(emp.id);
  };

  const submit = async () => {
    if (
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.position.trim() ||
      !form.startDate
    ) {
      toast.error("Prénom, nom, poste et date d'entrée sont obligatoires");
      return;
    }
    if (
      !editEmployee &&
      !form.nonRemunere &&
      (!form.initialSalary || parseFloat(form.initialSalary) <= 0)
    ) {
      toast.error("Le salaire brut mensuel est obligatoire à la création (ou cochez « Non rémunéré »)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        structureId: currentStructure?.id || undefined,
        // Date de fin acceptée pour tous les types de contrat (CDI inclus).
        cddEndDate: form.endDate || null,
      };
      if (editEmployee) {
        await updateEmployee(editEmployee.id, payload);
      } else {
        await createEmployee(payload);
      }
      setShowModal(false);
      await load();
      toast.success(editEmployee ? "Employé modifié" : "Employé créé");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = (emp) => setArchiveConfirm(emp);

  const doArchive = async () => {
    const emp = archiveConfirm;
    setArchiveConfirm(null);
    try {
      await archiveEmployee(emp.id);
      toast.success(`${emp.firstName} ${emp.lastName} archivé`);
      await load();
    } catch {
      toast.error("Erreur archivage");
    }
  };

  const isMulti = showAllStructures || !currentStructure?.id;

  const filtered = employees
    .filter((emp) => {
      const matchSearch =
        `${emp.firstName || ""} ${emp.lastName || ""} ${emp.position || ""}`
          .toLowerCase()
          .includes(search.toLowerCase());
      const matchStatus = statusFilter === "all" || emp.status === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      const dir = sortBy.dir === 'asc' ? 1 : -1;
      switch (sortBy.col) {
        case 'lastName':   return dir * `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
        case 'structure':  return dir * (a.structure?.name || '').localeCompare(b.structure?.name || '');
        case 'position':   return dir * (a.position || '').localeCompare(b.position || '');
        case 'contractType': return dir * (a.contractType || '').localeCompare(b.contractType || '');
        case 'status':     return dir * (a.status || '').localeCompare(b.status || '');
        case 'startDate':  return dir * (new Date(a.startDate || 0) - new Date(b.startDate || 0));
        case 'salary':     return dir * ((a.currentSalary || 0) - (b.currentSalary || 0));
        default:           return 0;
      }
    });

  if (selectedEmployeeId) {
    return (
      <div>
        {cameFrom === 'timesheets' && onNavigate && (
          <button
            onClick={() => onNavigate('timesheets')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft size={15} />
            Retour aux feuilles de temps
          </button>
        )}
        <EmployeeDetailPage
          employeeId={selectedEmployeeId}
          onBack={handleBackFromDetail}
          initialOpenEdit={autoEdit}
        />
      </div>
    );
  }

  // Vue organigramme
  if (empView === 'orgchart') {
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          <button onClick={() => setEmpView('list')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border bg-white text-gray-600 border-gray-200 hover:border-gray-300 transition-colors">
            <List size={14} /> Liste
          </button>
          <button onClick={() => setEmpView('orgchart')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border bg-primary text-white border-primary transition-colors">
            <Users size={14} /> Organigramme
          </button>
        </div>
        <OrgChartTab
          showAllStructures={showAllStructures}
          onEmployeeClick={(emp) => setSelectedEmployeeId(emp.id)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        {/* Toggle Liste / Organigramme */}
        <div className="flex gap-2 mr-2">
          <button onClick={() => setEmpView('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${empView === 'list' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
            <List size={13} /> Liste
          </button>
          <button onClick={() => setEmpView('orgchart')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${empView === 'orgchart' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
            <Users size={13} /> Organigramme
          </button>
        </div>
        <div className="relative flex-1 min-w-40">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un employé..."
            className={`${inputCls} pl-9`}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={inputCls}
        >
          <option value="active">Actifs seulement</option>
          <option value="inactive">Inactifs seulement</option>
          <option value="all">Tous</option>
        </select>
        <EmployeeListExport
          employees={filtered}
          structureName={currentStructure?.name}
        />
        <Btn onClick={openCreate} variant="primary">
          <Plus size={14} /> Ajouter
        </Btn>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-8">
            <LoadingSpinner size="default" text="Chargement..." />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            Aucun employé
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  { col: 'lastName',     label: 'Nom',          align: 'left'  },
                  ...(isMulti ? [{ col: 'structure', label: 'Structure', align: 'left' }] : []),
                  { col: 'position',     label: 'Poste',        align: 'left'  },
                  { col: 'contractType', label: 'Contrat',      align: 'left'  },
                  { col: 'status',       label: 'Statut',       align: 'left'  },
                  { col: 'startDate',    label: 'Entrée',       align: 'left'  },
                  { col: 'salary',       label: 'Salaire brut', align: 'right' },
                ].map(({ col, label, align }) => (
                  <th
                    key={col}
                    onClick={() => setSortBy(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }))}
                    className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-primary transition-colors text-${align}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {label}
                      {sortBy.col === col
                        ? sortBy.dir === 'asc'
                          ? <ChevronUp size={12} className="text-primary" />
                          : <ChevronDown size={12} className="text-primary" />
                        : <ChevronUp size={12} className="opacity-0 group-hover:opacity-30" />}
                    </span>
                  </th>
                ))}
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((emp) => (
                <tr
                  key={emp.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedEmployeeId(emp.id)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <EmployeeRowAvatar employee={emp} />
                      <span className="font-medium text-gray-800">
                        {emp.lastName} {emp.firstName}
                      </span>
                      {absentEmployeeIds.has(emp.id) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 font-medium whitespace-nowrap">
                          Absent
                        </span>
                      )}
                      {emp.cddEndDate && (() => {
                        const days = Math.ceil((new Date(emp.cddEndDate) - new Date()) / 86400000);
                        if (days > 30) return null;
                        const label = getEndDateBadgeLabel(emp.contractType);
                        return days <= 0
                          ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium whitespace-nowrap">{label} expiré</span>
                          : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium whitespace-nowrap">{label} · J-{days}</span>;
                      })()}
                    </div>
                    {!isMulti && currentStructure?.id && emp.structureId !== currentStructure.id && (
                      <span className="inline-flex items-center mt-0.5 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-600 text-[10px] font-medium">
                        Secondaire · {emp.structure?.name}
                      </span>
                    )}
                  </td>
                  {isMulti && (
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      <div>{emp.structure?.name || emp.structureName || "—"}</div>
                      {emp.secondaryStructures?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {emp.secondaryStructures.map(s => (
                            <span
                              key={s.structureId}
                              className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium"
                            >
                              +{s.structure?.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-gray-700">
                    {emp.position || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {emp.contractType || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      color={
                        emp.status === "active"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }
                    >
                      {emp.status === "active" ? "Actif" : "Inactif"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {fmtDate(emp.startDate)}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-right">
                    {fmtCurrency(emp.currentSalary)}
                  </td>
                  <td
                    className="px-4 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(emp)}
                        className="p-1.5 text-gray-500 hover:text-primary hover:bg-primary/5 rounded-lg"
                        title="Modifier"
                      >
                        <Edit size={15} />
                      </button>
                      <button
                        onClick={() => handleArchive(emp)}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Archiver"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <Modal
          title={editEmployee ? "Modifier l'employé" : "Nouvel employé"}
          onClose={() => setShowModal(false)}
          size="xl"
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowModal(false)}>
                Annuler
              </Btn>
              <Btn variant="primary" onClick={submit} loading={saving}>
                {editEmployee ? "Enregistrer" : "Créer"}
              </Btn>
            </>
          }
        >
          <div className="space-y-6">
            {/* État civil */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                État civil
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Civilité">
                  <select
                    value={form.civilite || ""}
                    onChange={(e) => setFormField("civilite", e.target.value)}
                    className={inputCls}
                  >
                    <option value="">—</option>
                    <option value="M.">M.</option>
                    <option value="Mme">Mme</option>
                  </select>
                </FormField>
                <FormField label="Prénom" required>
                  <input
                    type="text"
                    value={form.firstName || ""}
                    onChange={(e) => setFormField("firstName", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Autres prénoms">
                  <input
                    type="text"
                    value={form.otherFirstNames || ""}
                    onChange={(e) =>
                      setFormField("otherFirstNames", e.target.value)
                    }
                    placeholder="Tous les prénoms"
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Nom" required>
                  <input
                    type="text"
                    value={form.lastName || ""}
                    onChange={(e) => setFormField("lastName", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Nom de naissance">
                  <input
                    type="text"
                    value={form.birthName || ""}
                    onChange={(e) => setFormField("birthName", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Date de naissance">
                  <input
                    type="date"
                    value={form.birthDate || ""}
                    onChange={(e) => setFormField("birthDate", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField
                  label="N° INSEE (carte vitale)"
                  className="col-span-2"
                >
                  <input
                    type="text"
                    value={form.inseeNumber || ""}
                    onChange={(e) =>
                      setFormField("inseeNumber", e.target.value)
                    }
                    placeholder="Ex: 1 85 12 75 123 456 78"
                    className={inputCls}
                  />
                </FormField>
              </div>
            </div>

            {/* Lieu de naissance */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Lieu de naissance
              </p>
              <div className="grid grid-cols-2 gap-4">
                <CommuneField
                  postalValue={form.birthPostalCode || ""}
                  cityValue={form.birthCity || ""}
                  onPostalChange={(v) => setFormField("birthPostalCode", v)}
                  onCityChange={(v) => setFormField("birthCity", v)}
                  postalLabel="Code postal de naissance"
                  cityLabel="Commune de naissance"
                />
                <FormField label="Pays de naissance" className="col-span-2">
                  <input
                    type="text"
                    value={form.birthCountry || ""}
                    onChange={(e) =>
                      setFormField("birthCountry", e.target.value)
                    }
                    placeholder="Ex: France"
                    className={inputCls}
                  />
                </FormField>
              </div>
            </div>

            {/* Contact */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Contact
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Email">
                  <input
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => setFormField("email", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Téléphone">
                  <input
                    type="text"
                    value={form.phone || ""}
                    onChange={(e) => setFormField("phone", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Adresse" className="col-span-2">
                  <input
                    type="text"
                    value={form.address || ""}
                    onChange={(e) => setFormField("address", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <CommuneField
                  postalValue={form.postalCode || ""}
                  cityValue={form.city || ""}
                  onPostalChange={(v) => setFormField("postalCode", v)}
                  onCityChange={(v) => setFormField("city", v)}
                />
                <FormField label="Pays" className="col-span-2">
                  <input
                    type="text"
                    value={form.country || ""}
                    onChange={(e) => setFormField("country", e.target.value)}
                    placeholder="Ex: France"
                    className={inputCls}
                  />
                </FormField>
              </div>
            </div>

            {/* Contrat */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Contrat
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Poste" required className="col-span-2">
                  <input
                    type="text"
                    value={form.position || ""}
                    onChange={(e) => setFormField("position", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Type de contrat">
                  <select
                    value={form.contractType || "CDI"}
                    onChange={(e) =>
                      setFormField("contractType", e.target.value)
                    }
                    className={inputCls}
                  >
                    {STAGE_CONTRACT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Statut">
                  <select
                    value={form.status}
                    onChange={(e) => setFormField("status", e.target.value)}
                    className={inputCls}
                  >
                    {STAGE_STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Date d'embauche" required>
                  <input
                    type="date"
                    value={form.startDate || ""}
                    onChange={(e) => setFormField("startDate", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                {requiresEndDate(form.contractType) && (
                  <FormField
                    label={getEndDateLabel(form.contractType)}
                    required={isEndDateMandatory(form.contractType)}
                  >
                    <input
                      type="date"
                      value={form.endDate || ""}
                      onChange={(e) => setFormField("endDate", e.target.value)}
                      className={inputCls}
                      required={isEndDateMandatory(form.contractType)}
                    />
                  </FormField>
                )}
                <FormField label="Heures/semaine">
                  <input
                    type="number"
                    step="0.5"
                    value={form.workHours ?? ""}
                    onChange={(e) => setFormField("workHours", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Taux horaire (€)">
                  {(() => {
                    const wh = parseFloat(form.workHours);
                    const sal =
                      editEmployee?.currentSalary ??
                      parseFloat(form.initialSalary);
                    const hr =
                      sal && wh > 0
                        ? (parseFloat(sal) / ((wh * 52) / 12)).toFixed(4)
                        : null;
                    return (
                      <div className={`${inputCls} bg-gray-50 text-gray-600`}>
                        {hr ? (
                          `${hr} €/h`
                        ) : (
                          <span className="text-gray-400 italic">
                            calculé depuis le salaire
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </FormField>
                {!editEmployee ? (
                  <div className="col-span-2 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer w-fit">
                      <input
                        type="checkbox"
                        checked={form.nonRemunere || false}
                        onChange={(e) => {
                          setFormField("nonRemunere", e.target.checked);
                          if (e.target.checked) setFormField("initialSalary", "");
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Non rémunéré</span>
                    </label>
                    {!form.nonRemunere && (
                      <FormField label="Salaire brut mensuel (€)" required>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={form.initialSalary}
                          onChange={(e) =>
                            setFormField("initialSalary", e.target.value)
                          }
                          placeholder="Ex: 1800.00"
                          className={inputCls}
                        />
                      </FormField>
                    )}
                  </div>
                ) : (
                  <FormField label="Salaire actuel" className="col-span-2">
                    <div className={`${inputCls} bg-gray-50 text-gray-600`}>
                      {editEmployee.currentSalary ? (
                        new Intl.NumberFormat("fr-FR", {
                          style: "currency",
                          currency: "EUR",
                        }).format(editEmployee.currentSalary)
                      ) : (
                        <span className="text-gray-400 italic">
                          Non renseigné
                        </span>
                      )}
                      <span className="ml-2 text-xs text-gray-400">
                        (modifiable via la fiche employé, onglet Salaires)
                      </span>
                    </div>
                  </FormField>
                )}
                <FormField label="Catégorie convention">
                  <input
                    type="text"
                    value={form.conventionCategory}
                    onChange={(e) =>
                      setFormField("conventionCategory", e.target.value)
                    }
                    placeholder="Ex: Technicien"
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Échelon">
                  <input
                    type="text"
                    value={form.conventionEchelon}
                    onChange={(e) =>
                      setFormField("conventionEchelon", e.target.value)
                    }
                    placeholder="Ex: 2"
                    className={inputCls}
                  />
                </FormField>
                <FormField
                  label="Titre transport (€/mois)"
                  className="col-span-2"
                >
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.transportAmount}
                    onChange={(e) =>
                      setFormField("transportAmount", e.target.value)
                    }
                    placeholder="Ex: 86.34"
                    className={inputCls}
                  />
                </FormField>
              </div>
            </div>

            {/* Mutuelle */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Mutuelle
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Mutuelle">
                  <select
                    value={form.mutuelleType}
                    onChange={(e) =>
                      setFormField("mutuelleType", e.target.value)
                    }
                    className={inputCls}
                  >
                    {STAGE_MUTUELLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                {form.mutuelleType === "conjoint" && (
                  <FormField label="Validité mutuelle conjoint">
                    <input
                      type="date"
                      value={form.mutuelleValidUntil}
                      onChange={(e) =>
                        setFormField("mutuelleValidUntil", e.target.value)
                      }
                      className={inputCls}
                    />
                  </FormField>
                )}
                <FormField
                  label="Justificatif mutuelle"
                  className={
                    form.mutuelleType === "conjoint" ? "col-span-2" : ""
                  }
                >
                  <input
                    type="text"
                    value={form.mutuelleDocument}
                    onChange={(e) =>
                      setFormField("mutuelleDocument", e.target.value)
                    }
                    placeholder="Nom du document"
                    className={inputCls}
                  />
                </FormField>
              </div>
            </div>

            {/* ── Plannings PDF (structures) ──────────────────────── */}
            <div className="border-t border-gray-100 pt-4 mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Plannings PDF
              </p>
              <FormField label="Structures affichées dans l'export planning">
                <div className="flex flex-wrap gap-2">
                  {structures.map((s) => {
                    const selected = form.planningPdfStructures
                      ? typeof form.planningPdfStructures === "string"
                        ? (() => {
                            try {
                              return JSON.parse(form.planningPdfStructures || "[]");
                            } catch {
                              return [];
                            }
                          })()
                        : form.planningPdfStructures
                      : [];
                    const isSelected =
                      Array.isArray(selected) && selected.includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition ${
                          isSelected
                            ? "bg-indigo-100 border-indigo-300 text-indigo-700"
                            : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={isSelected}
                          onChange={() => {
                            const current = Array.isArray(selected)
                              ? [...selected]
                              : [];
                            const updated = isSelected
                              ? current.filter((id) => id !== s.id)
                              : [...current, s.id];
                            setFormField(
                              "planningPdfStructures",
                              JSON.stringify(updated),
                            );
                          }}
                        />
                        {s.name}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Coche les structures dans lesquelles l'employé doit apparaître
                  sur l'export PDF planning. Vide = exclu de tous les PDF.
                </p>
              </FormField>
            </div>

            {/* Notes */}
            <FormField label="Notes">
              <textarea
                value={form.notes || ""}
                onChange={(e) => setFormField("notes", e.target.value)}
                rows={2}
                className={inputCls}
              />
            </FormField>
          </div>
        </Modal>
      )}
      <ConfirmModal
        open={!!archiveConfirm}
        title="Archiver cet employé ?"
        message={
          archiveConfirm
            ? `${archiveConfirm.firstName} ${archiveConfirm.lastName} passera inactif (non supprimé).`
            : ""
        }
        onConfirm={doArchive}
        onCancel={() => setArchiveConfirm(null)}
        danger
      />
    </div>
  );
};

const EMPLOYEE_DETAIL_TABS = [
  { id: "infos", label: "Infos", icon: User },
  { id: "salaires", label: "Salaires", icon: TrendingUp },
  { id: "absences", label: "Absences", icon: Calendar },
  { id: "planning", label: "Planning", icon: CalendarDays },
  { id: "formations", label: "Formations", icon: GraduationCap },
  { id: "entretiens", label: "Entretiens", icon: MessageSquare },
  { id: "visites-medicales", label: "Visites médicales", icon: Stethoscope },
  { id: "periode-essai", label: "Période d'essai", icon: Timer },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "doc-requests", label: "Demandes docs", icon: Mail },
  { id: "counters", label: "Compteurs", icon: Clock },
];

const EMPTY_SALARY_FORM = {
  grossMonthlySalary: "",
  baseSalarialBrut: "",
  salaireMutuelle: "",
  effectiveDate: "",
  comment: "",
};

function computeSalarySimulation(gross, mutuelle) {
  const g = parseFloat(gross) || 0;
  const m = parseFloat(mutuelle) || 0;
  const cotisations = g * 0.08;
  const netMensuel = g - cotisations;
  const charges = g * 0.42;
  return {
    brutAnnuel: g * 12,
    cotisations,
    netMensuel,
    netAnnuel: netMensuel * 12,
    chargesMensuelles: charges,
    coutMensuel: g + charges,
    coutAnnuel: (g + charges) * 12,
    mutuelle: m,
  };
}

function fmt2(n) {
  return (
    n.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €"
  );
}

function SimRow({ label, value, bold, sub }) {
  return (
    <div className="flex justify-between items-center">
      <span className={sub ? "text-gray-400" : "text-gray-600"}>{label}</span>
      <span
        className={
          bold ? "font-semibold text-gray-900" : "font-normal text-gray-700"
        }
      >
        {value}
      </span>
    </div>
  );
}

// ── Export helpers ──────────────────────────────────────────────────────────

function slugifyExport(str) {
  return String(str)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function maskIban(iban) {
  if (!iban || iban.length < 8) return iban;
  return iban.slice(0, 4) + " **** **** " + iban.slice(-5);
}

function buildExportSchema(emp) {
  return {
    titre: `Fiche employé — ${emp.firstName} ${emp.lastName}`,
    sousTitre: [emp.position, emp.contractType, emp.structure?.name]
      .filter(Boolean)
      .join(" · "),
    nomFichier: `${slugifyExport(emp.lastName)}-${slugifyExport(emp.firstName)}_fiche`,
    sections: [
      {
        id: "etat_civil",
        titre: "État civil",
        champs: [
          { cle: "civilite", label: "Civilité", valeur: emp.civilite || "—" },
          { cle: "prenoms", label: "Prénom(s)", valeur: emp.firstName || "—" },
          {
            cle: "autres_prenoms",
            label: "Autres prénom(s)",
            valeur: emp.otherFirstNames || "—",
          },
          { cle: "nom", label: "Nom", valeur: emp.lastName || "—" },
          {
            cle: "nom_naissance",
            label: "Nom de naissance",
            valeur: emp.birthName || "—",
          },
          {
            cle: "date_naissance",
            label: "Date de naissance",
            valeur: fmtDate(emp.birthDate),
          },
          {
            cle: "n_insee",
            label: "N° INSEE",
            valeur: emp.inseeNumber ? "*** (donnée sensible)" : "—",
          },
          {
            cle: "lieu_naissance",
            label: "Lieu de naissance",
            valeur:
              [emp.birthPostalCode, emp.birthCity, emp.birthCountry]
                .filter(Boolean)
                .join(", ") || "—",
          },
        ],
      },
      {
        id: "contact",
        titre: "Contact",
        champs: [
          { cle: "email", label: "Email", valeur: emp.email || "—" },
          { cle: "telephone", label: "Téléphone", valeur: emp.phone || "—" },
          {
            cle: "adresse",
            label: "Adresse",
            valeur:
              [emp.address, emp.postalCode, emp.city, emp.country]
                .filter(Boolean)
                .join(", ") || "—",
          },
        ],
      },
      {
        id: "contrat",
        titre: "Contrat",
        champs: [
          { cle: "poste", label: "Poste", valeur: emp.position || "—" },
          {
            cle: "type_contrat",
            label: "Type de contrat",
            valeur: emp.contractType || "—",
          },
          {
            cle: "debut",
            label: "Date de début",
            valeur: fmtDate(emp.startDate),
          },
          {
            cle: "fin_cdd",
            label: "Date de fin (CDD)",
            valeur: fmtDate(emp.cddEndDate),
          },
          {
            cle: "temps_travail",
            label: "Temps de travail",
            valeur: emp.workHours ? `${emp.workHours} h / semaine` : "—",
          },
          {
            cle: "convention",
            label: "Convention collective",
            valeur:
              [
                emp.conventionCategory,
                emp.conventionEchelon
                  ? `échelon ${emp.conventionEchelon}`
                  : null,
              ]
                .filter(Boolean)
                .join(" — ") || "—",
          },
        ],
      },
      {
        id: "remuneration",
        titre: "Rémunération",
        champs: [
          {
            cle: "salaire_brut",
            label: "Salaire brut mensuel",
            valeur: emp.currentSalary ? fmtCurrency(emp.currentSalary) : "—",
          },
          {
            cle: "taux_horaire",
            label: "Taux horaire",
            valeur: emp.hourlyRate ? fmtCurrency(emp.hourlyRate) : "—",
          },
          {
            cle: "transport",
            label: "Titre transport",
            valeur: emp.transportAmount
              ? fmtCurrency(emp.transportAmount)
              : "—",
          },
          {
            cle: "mutuelle",
            label: "Mutuelle",
            valeur: emp.mutuelleType || "—",
          },
        ],
      },
    ],
  };
}

// ────────────────────────────────────────────────────────────────────────────

const EmployeeDetailPage = ({
  employeeId,
  onBack,
  initialTab = "infos",
  allowedTabs = null,
  initialFormationId = null,
  initialOpenEdit = false,
}) => {
  const toast = useToast();
  const { currentStructure, structures } = useStructure();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const autoEditTriggered = useRef(false);

  const visibleTabs = allowedTabs
    ? EMPLOYEE_DETAIL_TABS.filter((t) => allowedTabs.includes(t.id))
    : EMPLOYEE_DETAIL_TABS;

  const safeInitialTab =
    visibleTabs.find((t) => t.id === initialTab)?.id ??
    visibleTabs[0]?.id ??
    "infos";
  const [activeTab, setActiveTab] = useState(safeInitialTab);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOfferLetterModal, setShowOfferLetterModal] = useState(false);
  const [showGenerateDocModal, setShowGenerateDocModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const [photoBlobUrl, setPhotoBlobUrl] = useState(null);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [editForm, setEditForm] = useState(STAGE_EMPTY_FORM);
  // Types de documents administrables (CRUD admin > RH > Types de documents)
  const [docTypes, setDocTypes] = useState([]);
  useEffect(() => {
    getEmployeeDocumentTypes()
      .then((r) => setDocTypes(r.data || []))
      .catch(() => setDocTypes([]));
  }, []);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [icalToken, setIcalToken] = useState(null);
  const [icalLoading, setIcalLoading] = useState(false);
  const [icalCopied, setIcalCopied] = useState(false);
  const [orgRoles, setOrgRoles] = useState([]);
  const [orgGroups, setOrgGroups] = useState([]);
  const [allStructures, setAllStructures] = useState([]);

  useEffect(() => {
    getOrgRoles().then(r => setOrgRoles(r.data)).catch(() => {});
    getOrgGroups().then(r => setOrgGroups(r.data)).catch(() => {});
    getStructures().then(r => setAllStructures(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [pdfSection, setPdfSection] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [rcrData, setRcrData] = useState({ balance: 0, ledger: [] });
  const [debtData, setDebtData] = useState({ balance: 0, ledger: [] });
  const [adjustForm, setAdjustForm] = useState({
    type: "",
    amount: "",
    comment: "",
  });
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [salaries, setSalaries] = useState([]);
  const [loadingSalaries, setLoadingSalaries] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [salaryForm, setSalaryForm] = useState(EMPTY_SALARY_FORM);
  const [savingSalary, setSavingSalary] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await getEmployeeById(employeeId);
        setEmployee(data);
      } catch (err) {
        console.error(err);
        toast.error("Impossible de charger l'employé");
        onBack();
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  // Auto-ouvrir le modal d'édition complet si demandé depuis la liste
  useEffect(() => {
    if (employee && initialOpenEdit && !autoEditTriggered.current) {
      autoEditTriggered.current = true;
      openEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, initialOpenEdit]);

  // Charger la photo de profil
  useEffect(() => {
    if (!employee?.photoUrl) { setPhotoBlobUrl(null); return; }
    let url;
    let cancelled = false;
    getEmployeePhotoBlob(employee.id)
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setPhotoBlobUrl(url);
      })
      .catch(() => { if (!cancelled) setPhotoBlobUrl(null); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [employee?.id, employee?.photoUrl, photoVersion]);

  useEffect(() => {
    if (!employee) return;
    setLoadingSalaries(true);
    getSalaryHistory(employee.id)
      .then((res) => setSalaries(res.data))
      .catch(() => toast.error("Erreur chargement historique salaires"))
      .finally(() => setLoadingSalaries(false));
  }, [employee]);

  const handleCreateSalary = async () => {
    if (
      !salaryForm.grossMonthlySalary ||
      parseFloat(salaryForm.grossMonthlySalary) <= 0
    ) {
      toast.error(
        "Le salaire brut mensuel est obligatoire et doit être positif",
      );
      return;
    }
    if (!salaryForm.effectiveDate) {
      toast.error("La date de prise d'effet est obligatoire");
      return;
    }
    setSavingSalary(true);
    try {
      await addSalary(employee.id, salaryForm);
      setShowSalaryModal(false);
      setSalaryForm(EMPTY_SALARY_FORM);
      const [salRes, empRes] = await Promise.all([
        getSalaryHistory(employee.id),
        getEmployeeById(employee.id),
      ]);
      setSalaries(salRes.data);
      setEmployee(empRes.data);
      toast.success("Salaire ajouté");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de la création");
    }
    setSavingSalary(false);
  };

  const [absences, setAbsences] = useState([]);
  const [absenceSortDir, setAbsenceSortDir] = useState(-1); // -1 = plus récente en premier
  const [absTypeFilter, setAbsTypeFilter] = useState('');
  const [loadingAbsences, setLoadingAbsences] = useState(false);
  const [showAbsenceForm, setShowAbsenceForm] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({
    type: "conge_paye",
    startDate: "",
    endDate: "",
    comment: "",
  });
  const [savingAbsence, setSavingAbsence] = useState(false);
  const [absenceConfirm, setAbsenceConfirm] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null); // id de l'absence en cours d'envoi
  const [emailConfirm, setEmailConfirm] = useState(null); // absence à confirmer avant envoi

  useEffect(() => {
    if (!employee) return;
    setLoadingAbsences(true);
    getAbsences(employee.id)
      .then((res) => setAbsences(res.data))
      .catch(() => toast.error("Erreur chargement absences"))
      .finally(() => setLoadingAbsences(false));
  }, [employee]);

  const handleCreateAbsence = async () => {
    if (!absenceForm.startDate || !absenceForm.endDate) {
      toast.error("Les dates de début et de fin sont obligatoires");
      return;
    }
    if (new Date(absenceForm.endDate) < new Date(absenceForm.startDate)) {
      toast.error("La date de fin doit être >= date de début");
      return;
    }
    const file = absenceFileRef.current?.files?.[0] || null;
    if (absenceForm.type === "maladie" && !file) {
      toast.error(
        "Un justificatif (arrêt de travail PDF) est obligatoire pour une maladie",
      );
      return;
    }
    setSavingAbsence(true);
    try {
      const { data: created } = await createAbsence(employee.id, absenceForm);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await uploadAbsenceDocument(created.id, fd);
      }
      setShowAbsenceForm(false);
      setAbsenceForm({
        type: "conge_paye",
        startDate: "",
        endDate: "",
        comment: "",
      });
      if (absenceFileRef.current) absenceFileRef.current.value = "";
      const res = await getAbsences(employee.id);
      setAbsences(res.data);
      toast.success("Absence créée");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de la création");
    }
    setSavingAbsence(false);
  };

  const handleAbsenceConfirm = async () => {
    if (!absenceConfirm) return;
    try {
      if (absenceConfirm.type === "validate") {
        await validateAbsence(absenceConfirm.id);
        toast.success("Absence validée");
      } else if (absenceConfirm.type === "refuse") {
        await refuseAbsence(absenceConfirm.id);
        toast.success("Absence refusée");
      } else if (absenceConfirm.type === "delete") {
        await deleteAbsence(absenceConfirm.id);
        toast.success("Absence supprimée");
      }
      const res = await getAbsences(employee.id);
      setAbsences(res.data);
    } catch {
      toast.error("Erreur");
    }
    setAbsenceConfirm(null);
  };

  const handleSendEmail = async () => {
    if (!emailConfirm) return;
    setSendingEmail(emailConfirm.id);
    try {
      await sendAbsenceEmail(emailConfirm.id);
      toast.success("Email envoyé au cabinet RH");
      const res = await getAbsences(employee.id);
      setAbsences(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de l'envoi");
    }
    setSendingEmail(null);
    setEmailConfirm(null);
  };

  const [viewingDoc, setViewingDoc] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docUploadForm, setDocUploadForm] = useState({
    type: "contrat",
    notes: "",
  });
  const [docConfirm, setDocConfirm] = useState(null);
  const docFileRef = useRef(null);
  const absenceFileRef = useRef(null);

  // Transport justificatif
  const [transportProviders, setTransportProviders] = useState([]);
  const [showTransportModal, setShowTransportModal] = useState(false);
  const [transportForm, setTransportForm] = useState({
    providerId: "",
    month: "",
    year: "",
  });
  const [transportFile, setTransportFile] = useState(null);
  const [uploadingTransport, setUploadingTransport] = useState(false);
  const [transportDeleteConfirm, setTransportDeleteConfirm] = useState(null);
  const transportFileRef = useRef(null);

  const handleUploadDocument = async (file) => {
    if (!file || !employee) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", docUploadForm.type);
      if (docUploadForm.notes) fd.append("notes", docUploadForm.notes);
      await uploadEmployeeDocument(employee.id, fd);
      const { data } = await getEmployeeById(employee.id);
      setEmployee(data);
      setDocUploadForm({ type: "contrat", notes: "" });
      toast.success("Document ajouté");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur upload document");
    }
    setUploadingDoc(false);
  };

  const handleDeleteDocument = async () => {
    if (!docConfirm) return;
    try {
      await deleteEmployeeDocument(docConfirm.id);
      const { data } = await getEmployeeById(employee.id);
      setEmployee(data);
      toast.success("Document supprimé");
    } catch {
      toast.error("Erreur suppression document");
    }
    setDocConfirm(null);
  };

  const handleViewDocument = (doc) => {
    setViewingDoc({
      id: doc.id,
      fileName: doc.fileName,
      fetchBlob: () => getEmployeeDocumentFile(doc.id),
    });
  };

  const handleViewAbsenceDocument = (absence) => {
    setViewingDoc({
      id: absence.id,
      fileName: absence.documentName,
      fetchBlob: () => getAbsenceDocumentFile(absence.id),
    });
  };

  // Chargement providers transport (une fois au montage)
  useEffect(() => {
    getTransportProviders()
      .then((r) => setTransportProviders(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUploadTransport = async () => {
    const { providerId, month, year } = transportForm;
    if (!transportFile || !providerId || !month || !year) {
      toast.error("Fournisseur, mois, année et fichier sont obligatoires");
      return;
    }
    const period = `${year}-${month}`; // month est déjà "01"…"12"
    setUploadingTransport(true);
    try {
      const fd = new FormData();
      fd.append("file", transportFile);
      fd.append("type", "transport");
      fd.append("transportProviderId", providerId);
      fd.append("period", period); // format "YYYY-MM", parsé en UTC côté backend
      await uploadEmployeeDocument(employee.id, fd);
      const { data } = await getEmployeeById(employee.id);
      setEmployee(data);
      setShowTransportModal(false);
      setTransportFile(null);
      setTransportForm({ providerId: "", month: "", year: "" });
      toast.success("Justificatif ajouté");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur upload justificatif");
    }
    setUploadingTransport(false);
  };

  const handleDeleteTransport = async () => {
    if (!transportDeleteConfirm) return;
    try {
      await deleteEmployeeDocument(transportDeleteConfirm.id);
      const { data } = await getEmployeeById(employee.id);
      setEmployee(data);
      toast.success("Justificatif supprimé");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur suppression");
    }
    setTransportDeleteConfirm(null);
  };

  // ── Formations ───────────────────────────────────────────────────────────────
  const [trainings, setTrainings] = useState([]);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [loadingTrainings, setLoadingTrainings] = useState(false);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [trainingForm, setTrainingForm] = useState({
    title: "",
    trainingTypeId: "",
    nature: "",
    mandatory: false,
    organism: "",
    startDate: "",
    endDate: "",
    durationHours: "",
    cost: "",
    recyclingDate: "",
    financing: "",
    notes: "",
    createHRCost: false,
  });
  const [trainingAttestation, setTrainingAttestation] = useState(null);
  const [savingTraining, setSavingTraining] = useState(false);
  const [exportingFormationsPdf, setExportingFormationsPdf] = useState(false);
  const [trainingDeleteConfirm, setTrainingDeleteConfirm] = useState(null);
  const [viewingTraining, setViewingTraining] = useState(null);
  const [trainingFilterStatus, setTrainingFilterStatus] = useState([]);
  const [trainingFilterType, setTrainingFilterType] = useState("");
  const [trainingFilterSort, setTrainingFilterSort] = useState("startDate");

  useEffect(() => {
    if (!employee) return;
    setLoadingTrainings(true);
    Promise.all([getEmployeeTrainings(employee.id), getTrainingTypes()])
      .then(([trainRes, typesRes]) => {
        setTrainings(trainRes.data);
        setTrainingTypes(typesRes.data);
        // Auto-open edit modal si on arrive depuis le dashboard avec un ID précis
        if (initialFormationId) {
          const target = trainRes.data.find(t => t.id === initialFormationId);
          if (target) openEditTraining(target);
        }
      })
      .catch(() => toast.error("Erreur chargement formations"))
      .finally(() => setLoadingTrainings(false));
  }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreateTraining = () => {
    setEditingTraining(null);
    setTrainingForm({
      title: "",
      trainingTypeId: "",
      nature: "",
      mandatory: false,
      organism: "",
      startDate: "",
      endDate: "",
      durationHours: "",
      cost: "",
      recyclingDate: "",
      financing: "",
      notes: "",
      createHRCost: false,
    });
    setTrainingAttestation(null);
    setShowTrainingModal(true);
  };

  const openEditTraining = (t) => {
    setEditingTraining(t);
    setTrainingForm({
      title: t.title,
      trainingTypeId: t.trainingTypeId || "",
      nature: t.nature || "",
      mandatory: t.mandatory || false,
      organism: t.organism || "",
      startDate: t.startDate ? t.startDate.slice(0, 10) : "",
      endDate: t.endDate ? t.endDate.slice(0, 10) : "",
      durationHours: t.durationHours != null ? String(t.durationHours) : "",
      cost: t.cost != null ? String(t.cost) : "",
      recyclingDate: t.recyclingDate ? t.recyclingDate.slice(0, 10) : "",
      financing: t.financing || "",
      notes: t.notes || "",
      createHRCost: false,
    });
    setTrainingAttestation(null);
    setShowTrainingModal(true);
  };

  const openRecyclingTraining = (t) => {
    setEditingTraining(null);
    setTrainingForm({
      title: t.title,
      trainingTypeId: t.trainingTypeId || "",
      nature: t.nature || "",
      mandatory: t.mandatory || false,
      organism: t.organism || "",
      startDate: "",
      endDate: "",
      durationHours: "",
      cost: "",
      recyclingDate: "",
      financing: t.financing || "",
      notes: "",
      createHRCost: false,
    });
    setTrainingAttestation(null);
    setShowTrainingModal(true);
  };

  const handleTrainingSave = async () => {
    if (!trainingForm.title.trim()) {
      toast.error("L'intitulé est obligatoire");
      return;
    }
    if (!trainingForm.startDate) {
      toast.error("La date de début est obligatoire");
      return;
    }
    if (trainingForm.endDate && trainingForm.endDate < trainingForm.startDate) {
      toast.error("La date de fin doit être postérieure à la date de début");
      return;
    }
    setSavingTraining(true);
    try {
      if (editingTraining) {
        await updateTraining(editingTraining.id, {
          title: trainingForm.title,
          trainingTypeId: trainingForm.trainingTypeId || null,
          nature: trainingForm.nature || null,
          mandatory: trainingForm.mandatory,
          organism: trainingForm.organism || null,
          startDate: trainingForm.startDate,
          endDate: trainingForm.endDate || null,
          durationHours: trainingForm.durationHours !== "" ? parseFloat(trainingForm.durationHours) : null,
          cost: trainingForm.cost !== "" ? parseFloat(trainingForm.cost) : null,
          recyclingDate: trainingForm.recyclingDate || null,
          financing: trainingForm.financing || null,
          notes: trainingForm.notes || null,
        });
        // Upload attestation séparé si changée
        if (trainingAttestation) {
          const fd = new FormData();
          fd.append("file", trainingAttestation);
          await uploadTrainingAttestation(editingTraining.id, fd);
        }
        toast.success("Formation mise à jour");
      } else {
        const fd = new FormData();
        fd.append("title", trainingForm.title);
        if (trainingForm.trainingTypeId)
          fd.append("trainingTypeId", trainingForm.trainingTypeId);
        if (trainingForm.nature) fd.append("nature", trainingForm.nature);
        fd.append("mandatory", String(trainingForm.mandatory));
        if (trainingForm.organism) fd.append("organism", trainingForm.organism);
        fd.append("startDate", trainingForm.startDate);
        if (trainingForm.endDate) fd.append("endDate", trainingForm.endDate);
        if (trainingForm.durationHours !== "")
          fd.append("durationHours", trainingForm.durationHours);
        if (trainingForm.cost !== "") fd.append("cost", trainingForm.cost);
        if (trainingForm.recyclingDate)
          fd.append("recyclingDate", trainingForm.recyclingDate);
        if (trainingForm.financing) fd.append("financing", trainingForm.financing);
        if (trainingForm.notes) fd.append("notes", trainingForm.notes);
        fd.append("createHRCost", String(trainingForm.createHRCost));
        if (trainingAttestation) fd.append("file", trainingAttestation);
        await createTraining(employee.id, fd);
        toast.success("Formation ajoutée");
      }
      setShowTrainingModal(false);
      const res = await getEmployeeTrainings(employee.id);
      setTrainings(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur enregistrement");
    }
    setSavingTraining(false);
  };

  const handleTrainingDelete = async (t) => {
    try {
      await deleteTraining(t.id);
      toast.success("Formation supprimée");
      const res = await getEmployeeTrainings(employee.id);
      setTrainings(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur suppression");
    }
    setTrainingDeleteConfirm(null);
  };

  const handleViewAttestation = (t) => {
    setViewingDoc({
      fileName: t.attestationName || "attestation",
      fetchBlob: () => getTrainingAttestation(t.id),
    });
  };

  const handleExportFormationsPdf = async () => {
    if (!employee || trainings.length === 0) return;
    setExportingFormationsPdf(true);
    try {
      const TEAL   = [20, 184, 166];
      const WHITE  = [255, 255, 255];
      const GRAY50 = [249, 250, 251];
      const GRAY4  = [75, 85, 99];

      const STATUS_PDF = {
        planned:    { label: 'Planifié',       fill: [219, 234, 254], text: [29, 78, 216]   },
        ok:         { label: 'Réalisée',       fill: [220, 252, 231], text: [21, 128, 61]   },
        none:       { label: 'Réalisée',       fill: [220, 252, 231], text: [21, 128, 61]   },
        soon:       { label: 'Expire bientôt', fill: [254, 249, 195], text: [161, 98, 7]    },
        expired:    { label: 'Expiré',         fill: [254, 226, 226], text: [185, 28, 28]   },
        superseded: { label: 'Recyclé',        fill: [243, 244, 246], text: [107, 114, 128] },
      };

      // Calcul effectiveStatus (logique superseded identique à l'onglet)
      const supersededIds = new Set();
      const byType = {};
      trainings.forEach(t => {
        if (!t.trainingTypeId) return;
        if (!byType[t.trainingTypeId]) byType[t.trainingTypeId] = [];
        byType[t.trainingTypeId].push(t);
      });
      Object.values(byType).forEach(group => {
        if (group.length < 2) return;
        group.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        const [latest, ...older] = group;
        if (latest.recyclingStatus !== 'expired' && latest.recyclingStatus !== 'planned') {
          older.forEach(t => { if (t.recyclingStatus === 'expired') supersededIds.add(t.id); });
        }
      });

      const rows = [...trainings]
        .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
        .map(t => ({ ...t, effectiveStatus: supersededIds.has(t.id) ? 'superseded' : t.recyclingStatus }));

      const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
      const empName = `${employee.firstName} ${employee.lastName}`.toUpperCase();
      const structureName = employee.structureName || employee.structure?.name || '';
      const poste = employee.jobTitle || '';

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const M = 10;

      // ── En-tête ────────────────────────────────────────────────────────────
      const headerH = 22;
      pdf.setFillColor(...TEAL);
      pdf.rect(0, 0, pdfW, headerH, 'F');

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      pdf.setTextColor(...WHITE);
      pdf.text('SUIVI DES FORMATIONS', pdfW / 2, headerH / 2 - 2, { align: 'center', baseline: 'middle' });

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(220, 255, 250);
      const subLine = [empName, structureName, poste].filter(Boolean).join('  ·  ');
      pdf.text(subLine, pdfW / 2, headerH / 2 + 5, { align: 'center', baseline: 'middle' });
      pdf.text(today, pdfW - M, headerH / 2, { align: 'right', baseline: 'middle' });

      // ── Bloc infos employé ─────────────────────────────────────────────────
      let y = headerH + 6;
      pdf.setFillColor(...GRAY50);
      pdf.roundedRect(M, y, pdfW - M * 2, 12, 2, 2, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8.5);
      pdf.setTextColor(...GRAY4);
      pdf.text('Employé :', M + 3, y + 4.5, { baseline: 'top' });
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${employee.firstName} ${employee.lastName}`, M + 22, y + 4.5, { baseline: 'top' });
      if (structureName) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Structure :', M + 80, y + 4.5, { baseline: 'top' });
        pdf.setFont('helvetica', 'normal');
        pdf.text(structureName, M + 100, y + 4.5, { baseline: 'top' });
      }
      if (poste) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Poste :', M + 140, y + 4.5, { baseline: 'top' });
        pdf.setFont('helvetica', 'normal');
        pdf.text(poste, M + 156, y + 4.5, { baseline: 'top' });
      }
      pdf.setFont('helvetica', 'bold');
      pdf.text('Total :', M + 3, y + 4.5 + 4.5, { baseline: 'top' });
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${rows.length} formation${rows.length > 1 ? 's' : ''}`, M + 22, y + 4.5 + 4.5, { baseline: 'top' });
      y += 16;

      // ── Table formations ───────────────────────────────────────────────────
      const colWidths = [8, 54, 30, 26, 26, 26, 20];
      // N° | Intitulé | Type | Date réalisation | Échéance recyclage | Statut | Durée(h)

      autoTable(pdf, {
        startY: y,
        margin: { left: M, right: M },
        tableWidth: pdfW - M * 2,
        head: [[
          { content: 'N°',        styles: { halign: 'center', cellWidth: colWidths[0] } },
          { content: 'Intitulé',  styles: { cellWidth: colWidths[1] } },
          { content: 'Type',      styles: { cellWidth: colWidths[2] } },
          { content: 'Réalisée le', styles: { halign: 'center', cellWidth: colWidths[3] } },
          { content: 'Échéance',  styles: { halign: 'center', cellWidth: colWidths[4] } },
          { content: 'Statut',    styles: { halign: 'center', cellWidth: colWidths[5] } },
          { content: 'Durée',     styles: { halign: 'center', cellWidth: colWidths[6] } },
        ]],
        body: rows.map((t, i) => {
          const s = STATUS_PDF[t.effectiveStatus] || STATUS_PDF.none;
          return [
            { content: i + 1,                                        styles: { halign: 'center', textColor: [150, 150, 150] } },
            { content: t.title + (t.organism ? `\n${t.organism}` : ''), styles: { fontStyle: 'normal' } },
            { content: t.trainingType?.name || '—',                  styles: { textColor: [100, 116, 139] } },
            { content: t.startDate ? new Date(t.startDate).toLocaleDateString('fr-FR') : '—', styles: { halign: 'center' } },
            { content: t.recyclingDate ? new Date(t.recyclingDate).toLocaleDateString('fr-FR') : '—', styles: { halign: 'center', textColor: t.recyclingDate ? GRAY4 : [200, 200, 200] } },
            { content: s.label, styles: { halign: 'center', fillColor: s.fill, textColor: s.text, fontStyle: 'bold' } },
            { content: t.durationHours != null ? `${t.durationHours} h` : '—', styles: { halign: 'center' } },
          ];
        }),
        styles: { fontSize: 7.5, cellPadding: 2, overflow: 'linebreak', valign: 'middle' },
        headStyles: { fillColor: TEAL, textColor: WHITE, fontStyle: 'bold', fontSize: 7.5 },
        alternateRowStyles: { fillColor: GRAY50 },
        didDrawPage: (data) => {
          // Pied de page sur chaque page
          const footerH = 8;
          pdf.setFillColor(248, 248, 248);
          pdf.rect(0, pdfH - footerH, pdfW, footerH, 'F');
          pdf.setDrawColor(220, 220, 220);
          pdf.setLineWidth(0.3);
          pdf.line(0, pdfH - footerH, pdfW, pdfH - footerH);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(7.5);
          pdf.setTextColor(80, 80, 80);
          pdf.text('SuiviPlus', M, pdfH - footerH / 2, { baseline: 'middle' });
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(7);
          pdf.setTextColor(150, 150, 150);
          pdf.text('Document confidentiel — Usage interne', pdfW / 2, pdfH - footerH / 2, { align: 'center', baseline: 'middle' });
          pdf.text(`Page ${data.pageNumber}`, pdfW - M, pdfH - footerH / 2, { align: 'right', baseline: 'middle' });
        },
      });

      // ── Récap statuts sous la table ────────────────────────────────────────
      const finalY = pdf.lastAutoTable.finalY + 4;
      const counts = {};
      rows.forEach(t => { counts[t.effectiveStatus] = (counts[t.effectiveStatus] || 0) + 1; });
      const statEntries = Object.entries(STATUS_PDF)
        .filter(([k]) => k !== 'none')
        .map(([k, v]) => ({ label: v.label, count: counts[k] || 0, fill: v.fill, text: v.text }))
        .filter(e => e.count > 0);

      if (statEntries.length > 0 && finalY < pdfH - 20) {
        let bx = M;
        const bh = 8;
        statEntries.forEach(e => {
          const bw = 34;
          pdf.setFillColor(...e.fill);
          pdf.roundedRect(bx, finalY, bw, bh, 1.5, 1.5, 'F');
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(7);
          pdf.setTextColor(...e.text);
          pdf.text(`${e.label} : ${e.count}`, bx + bw / 2, finalY + bh / 2, { align: 'center', baseline: 'middle' });
          bx += bw + 3;
        });
      }

      // ── Sauvegarde ─────────────────────────────────────────────────────────
      const safeName = `${employee.lastName}_${employee.firstName}`
        .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9_]/g, '_');
      pdf.save(`formations_${safeName}.pdf`);
    } catch (e) {
      toast.error('Erreur génération PDF');
    }
    setExportingFormationsPdf(false);
  };

  // Pré-remplissage date de recyclage depuis type + date de fin
  const handleTrainingTypeChange = (typeId) => {
    const type = trainingTypes.find((t) => t.id === typeId);
    const newForm = { ...trainingForm, trainingTypeId: typeId };
    if (type?.defaultRecyclingMonths && trainingForm.endDate) {
      const end = new Date(trainingForm.endDate);
      end.setMonth(end.getMonth() + type.defaultRecyclingMonths);
      newForm.recyclingDate = end.toISOString().slice(0, 10);
    }
    setTrainingForm(newForm);
  };

  const handleTrainingEndDateChange = (val) => {
    const type = trainingTypes.find(
      (t) => t.id === trainingForm.trainingTypeId,
    );
    const newForm = { ...trainingForm, endDate: val };
    if (type?.defaultRecyclingMonths && val) {
      const end = new Date(val);
      end.setMonth(end.getMonth() + type.defaultRecyclingMonths);
      newForm.recyclingDate = end.toISOString().slice(0, 10);
    }
    setTrainingForm(newForm);
  };

  // ── Entretiens ────────────────────────────────────────────────────────────────
  const [interviews, setInterviews] = useState([]);
  const [interviewTypes, setInterviewTypes] = useState([]);
  const [interviewTemplates, setInterviewTemplates] = useState([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planForm, setPlanForm] = useState({
    typeId: "",
    templateId: "",
    interviewerName: "",
    scheduledDate: "",
  });
  const [savingPlan, setSavingPlan] = useState(false);
  const [activeInterview, setActiveInterview] = useState(null);
  const [interviewAnswers, setInterviewAnswers] = useState([]);
  const [interviewNotes, setInterviewNotes] = useState("");
  const [savingAnswers, setSavingAnswers] = useState(false);
  const [interviewDeleteConfirm, setInterviewDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!employee) return;
    setLoadingInterviews(true);
    Promise.all([
      getEmployeeInterviews(employee.id),
      getEmployeeInterviewTypes(),
      getEmployeeInterviewTemplates({ structureId: employee.structureId }),
    ])
      .then(([itvRes, typesRes, tplRes]) => {
        setInterviews(itvRes.data);
        setInterviewTypes(typesRes.data);
        setInterviewTemplates(tplRes.data);
      })
      .catch(() => toast.error("Erreur chargement entretiens"))
      .finally(() => setLoadingInterviews(false));
  }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const openInterview = (itv) => {
    setActiveInterview(itv);
    setInterviewAnswers(Array.isArray(itv.answers) ? itv.answers : []);
    setInterviewNotes(itv.notes || "");
  };

  const getAnswerValue = (questionId) =>
    interviewAnswers.find((a) => a.questionId === questionId)?.answer ?? "";

  const setAnswerValue = (questionId, answer) =>
    setInterviewAnswers((prev) => {
      const existing = prev.findIndex((a) => a.questionId === questionId);
      if (existing >= 0)
        return prev.map((a, i) => (i === existing ? { ...a, answer } : a));
      return [...prev, { questionId, answer }];
    });

  const handleSaveAnswers = async () => {
    setSavingAnswers(true);
    try {
      const updated = await saveInterviewAnswers(activeInterview.id, {
        answers: interviewAnswers,
        notes: interviewNotes,
      });
      setInterviews((prev) =>
        prev.map((i) => (i.id === updated.data.id ? updated.data : i)),
      );
      setActiveInterview(updated.data);
      toast.success("Réponses enregistrées");
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur sauvegarde");
    }
    setSavingAnswers(false);
  };

  const handleInterviewStatus = async (itv, status) => {
    try {
      const updated = await updateInterviewStatus(itv.id, status);
      setInterviews((prev) =>
        prev.map((i) => (i.id === updated.data.id ? updated.data : i)),
      );
      if (activeInterview?.id === itv.id) setActiveInterview(updated.data);
      toast.success(
        status === "completed" ? "Entretien clôturé" : "Statut mis à jour",
      );
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur mise à jour statut");
    }
  };

  const handlePlanInterview = async () => {
    if (!planForm.interviewerName.trim() || !planForm.scheduledDate) {
      toast.error("Intervieweur et date requis");
      return;
    }
    setSavingPlan(true);
    try {
      const res = await createEmployeeInterview(employee.id, planForm);
      setInterviews((prev) => [res.data, ...prev]);
      setShowPlanModal(false);
      setPlanForm({
        typeId: "",
        templateId: "",
        interviewerName: "",
        scheduledDate: "",
      });
      toast.success("Entretien planifié");
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur planification");
    }
    setSavingPlan(false);
  };

  const handleDeleteInterview = async () => {
    try {
      await deleteEmployeeInterview(interviewDeleteConfirm.id);
      setInterviews((prev) =>
        prev.filter((i) => i.id !== interviewDeleteConfirm.id),
      );
      if (activeInterview?.id === interviewDeleteConfirm.id)
        setActiveInterview(null);
      toast.success("Entretien supprimé");
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur suppression");
    }
    setInterviewDeleteConfirm(null);
  };

  // ── Visites médicales ─────────────────────────────────────────────────────────
  const [medicalVisits, setMedicalVisits] = useState([]);
  const [visitSortDir, setVisitSortDir] = useState(-1); // -1 = plus récente en premier
  const [medicalVisitTypes, setMedicalVisitTypes] = useState([]);
  const [loadingMedicalVisits, setLoadingMedicalVisits] = useState(false);
  const [hasInapteDefinitif, setHasInapteDefinitif] = useState(false);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [visitDeleteConfirm, setVisitDeleteConfirm] = useState(null);
  const [crDeleteConfirm, setCrDeleteConfirm] = useState(null);
  const [pendingTypeChange, setPendingTypeChange] = useState(null);
  const [uploadingCr, setUploadingCr] = useState(null);
  const [viewingCr, setViewingCr] = useState(null);
  const [visitForm, setVisitForm] = useState({
    typeId: "",
    convocationDate: "",
    visitDate: "",
    visitTime: "",
    location: "",
    doctor: "",
    aptitude: "",
    nextVisitDate: "",
    notes: "",
  });
  const [savingVisit, setSavingVisit] = useState(false);
  const [sendingRelance, setSendingRelance] = useState(null);

  const APTITUDE_LABELS = {
    apte: "Apte",
    apte_restrictions: "Apte avec restrictions",
    inapte_temporaire: "Inapte temporaire",
    inapte_definitif: "Inapte définitif",
  };

  useEffect(() => {
    if (!employee) return;
    setLoadingMedicalVisits(true);
    Promise.all([getEmployeeMedicalVisits(employee.id), getMedicalVisitTypes()])
      .then(([visitsRes, typesRes]) => {
        const visits = visitsRes.data;
        setMedicalVisits(visits);
        setMedicalVisitTypes(typesRes.data);
        // Badge inapte_definitif dans l'en-tête : basé sur la dernière visite par type
        const latestByType = {};
        for (const v of visits) {
          if (
            !latestByType[v.typeId] ||
            new Date(v.visitDate) > new Date(latestByType[v.typeId].visitDate)
          ) {
            latestByType[v.typeId] = v;
          }
        }
        setHasInapteDefinitif(
          Object.values(latestByType).some(
            (v) => v.aptitude === "inapte_definitif",
          ),
        );
      })
      .catch(() => toast.error("Erreur chargement visites médicales"))
      .finally(() => setLoadingMedicalVisits(false));
  }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const openVisitModal = (visit = null) => {
    if (visit) {
      setEditingVisit(visit);
      setVisitForm({
        typeId: visit.typeId || "",
        convocationDate: visit.convocationDate
          ? String(visit.convocationDate).slice(0, 10)
          : "",
        visitDate: visit.visitDate ? String(visit.visitDate).slice(0, 10) : "",
        visitTime: visit.visitTime || "",
        location: visit.location || "",
        doctor: visit.doctor || "",
        aptitude: visit.aptitude || "",
        nextVisitDate: visit.nextVisitDate
          ? String(visit.nextVisitDate).slice(0, 10)
          : "",
        notes: visit.notes || "",
      });
    } else {
      setEditingVisit(null);
      setVisitForm({
        typeId: "",
        convocationDate: "",
        visitDate: "",
        visitTime: "",
        location: "",
        doctor: "",
        aptitude: "",
        nextVisitDate: "",
        notes: "",
      });
    }
    setShowVisitModal(true);
  };

  const applyVisitTypeChange = (typeId, recalculate) => {
    const type = medicalVisitTypes.find((t) => t.id === typeId);
    const newForm = { ...visitForm, typeId };
    if (recalculate && type?.defaultRenewalMonths && visitForm.visitDate) {
      const d = new Date(visitForm.visitDate);
      d.setMonth(d.getMonth() + type.defaultRenewalMonths);
      newForm.nextVisitDate = d.toISOString().slice(0, 10);
    }
    setVisitForm(newForm);
  };

  const handleVisitTypeChange = (typeId) => {
    const type = medicalVisitTypes.find((t) => t.id === typeId);
    const wouldRecalculate = type?.defaultRenewalMonths && visitForm.visitDate;
    // Demander confirmation si nextVisitDate déjà renseigné et recalcul prévu
    if (wouldRecalculate && visitForm.nextVisitDate) {
      setPendingTypeChange({ typeId, typeName: type.name });
      return;
    }
    applyVisitTypeChange(typeId, true);
  };

  const handleVisitDateChange = (val) => {
    const type = medicalVisitTypes.find((t) => t.id === visitForm.typeId);
    const newForm = { ...visitForm, visitDate: val };
    if (type?.defaultRenewalMonths && val) {
      const d = new Date(val);
      d.setMonth(d.getMonth() + type.defaultRenewalMonths);
      newForm.nextVisitDate = d.toISOString().slice(0, 10);
    }
    setVisitForm(newForm);
  };

  const handleSaveVisit = async () => {
    if (!visitForm.visitDate) {
      toast.error("La date de visite est requise");
      return;
    }
    if (!visitForm.typeId) {
      toast.error("Le type de visite est requis");
      return;
    }
    setSavingVisit(true);
    try {
      if (editingVisit) {
        const res = await updateMedicalVisit(
          employee.id,
          editingVisit.id,
          visitForm,
        );
        setMedicalVisits((prev) =>
          prev.map((v) => (v.id === res.data.id ? res.data : v)),
        );
        toast.success("Visite mise à jour");
      } else {
        const res = await createMedicalVisit(employee.id, visitForm);
        setMedicalVisits((prev) => [res.data, ...prev]);
        toast.success("Visite créée");
      }
      setShowVisitModal(false);
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur sauvegarde visite");
    }
    setSavingVisit(false);
  };

  const handleDeleteVisit = async () => {
    try {
      await deleteMedicalVisit(employee.id, visitDeleteConfirm.id);
      setMedicalVisits((prev) =>
        prev.filter((v) => v.id !== visitDeleteConfirm.id),
      );
      toast.success("Visite supprimée");
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur suppression");
    }
    setVisitDeleteConfirm(null);
  };

  const handleUploadCr = async (visit, file) => {
    setUploadingCr(visit.id);
    try {
      const res = await uploadMedicalVisitCr(employee.id, visit.id, file);
      setMedicalVisits((prev) =>
        prev.map((v) => (v.id === res.data.id ? res.data : v)),
      );
      toast.success("CR uploadé");
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur upload CR");
    }
    setUploadingCr(null);
  };

  const handleConfirmDeleteCr = async () => {
    const visit = crDeleteConfirm;
    setCrDeleteConfirm(null);
    try {
      const res = await deleteMedicalVisitCr(employee.id, visit.id);
      setMedicalVisits((prev) =>
        prev.map((v) => (v.id === res.data.id ? res.data : v)),
      );
      toast.success("CR supprimé");
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur suppression CR");
    }
  };

  const handleSendRelance = async (visit) => {
    setSendingRelance(visit.id);
    try {
      const res = await sendMedicalVisitRelance(employee.id, visit.id);
      setMedicalVisits((prev) =>
        prev.map((v) => (v.id === res.data.id ? res.data : v)),
      );
      toast.success("Relance envoyée");
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur envoi relance");
    }
    setSendingRelance(null);
  };

  // ── Période d'essai ──────────────────────────────────────────────────────────

  const TRIAL_STATUS_CFG = {
    en_cours:  { label: "En cours",  cls: "bg-blue-100 text-blue-700" },
    bientot:   { label: "Bientôt",   cls: "bg-orange-100 text-orange-700" },
    expiree:   { label: "Expirée",   cls: "bg-red-100 text-red-700" },
    confirmee: { label: "Confirmée", cls: "bg-green-100 text-green-700" },
    rompue:    { label: "Rompue",    cls: "bg-gray-100 text-gray-600" },
  };

  const TRIAL_CATEGORY_LABELS = {
    employe:        "Employé (2 mois)",
    agent_maitrise: "Agent de maîtrise (3 mois)",
    cadre:          "Cadre (4 mois)",
    cdd:            "CDD",
  };

  const TRIAL_DURATION_MONTHS = { employe: 2, agent_maitrise: 3, cadre: 4, cdd: null };

  const [trialPeriods, setTrialPeriods] = useState([]);
  const [loadingTrial, setLoadingTrial] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [editingTrial, setEditingTrial] = useState(null);
  const [trialForm, setTrialForm] = useState({ startDate: "", endDate: "", category: "employe", renewedEndDate: "", notes: "" });
  const [savingTrial, setSavingTrial] = useState(false);
  const [trialDeleteConfirm, setTrialDeleteConfirm] = useState(null);
  const [showRuptureModal, setShowRuptureModal] = useState(null);
  const [ruptureMotif, setRuptureMotif] = useState("");
  const [actioning, setActioning] = useState(false);

  useEffect(() => {
    if (!employee) return;
    setLoadingTrial(true);
    getEmployeeTrialPeriods(employee.id)
      .then(res => setTrialPeriods(res.data))
      .catch(() => toast.error("Erreur chargement périodes d'essai"))
      .finally(() => setLoadingTrial(false));
  }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTrialModal = (tp = null) => {
    if (tp) {
      setEditingTrial(tp);
      setTrialForm({
        startDate: tp.startDate ? String(tp.startDate).slice(0, 10) : "",
        endDate: tp.endDate ? String(tp.endDate).slice(0, 10) : "",
        category: tp.category || "employe",
        renewedEndDate: tp.renewedEndDate ? String(tp.renewedEndDate).slice(0, 10) : "",
        notes: tp.notes || "",
      });
    } else {
      setEditingTrial(null);
      const start = employee?.startDate ? String(employee.startDate).slice(0, 10) : "";
      const cat = "employe";
      let end = "";
      if (start && TRIAL_DURATION_MONTHS[cat]) {
        const d = new Date(start);
        d.setMonth(d.getMonth() + TRIAL_DURATION_MONTHS[cat]);
        end = d.toISOString().slice(0, 10);
      }
      setTrialForm({ startDate: start, endDate: end, category: cat, renewedEndDate: "", notes: "" });
    }
    setShowTrialModal(true);
  };

  const handleTrialCategoryChange = (cat) => {
    const months = TRIAL_DURATION_MONTHS[cat];
    const newForm = { ...trialForm, category: cat };
    if (months && trialForm.startDate) {
      const d = new Date(trialForm.startDate);
      d.setMonth(d.getMonth() + months);
      newForm.endDate = d.toISOString().slice(0, 10);
    }
    setTrialForm(newForm);
  };

  const handleSaveTrial = async () => {
    if (!trialForm.startDate) { toast.error("Date de début requise"); return; }
    if (!trialForm.endDate) { toast.error("Date de fin requise"); return; }
    setSavingTrial(true);
    try {
      const payload = {
        startDate: trialForm.startDate,
        endDate: trialForm.endDate,
        category: trialForm.category,
        renewedEndDate: trialForm.renewedEndDate || undefined,
        notes: trialForm.notes || undefined,
      };
      if (editingTrial) {
        const res = await updateTrialPeriod(employee.id, editingTrial.id, payload);
        setTrialPeriods(prev => prev.map(t => t.id === res.data.id ? res.data : t));
        toast.success("Période d'essai mise à jour");
      } else {
        const res = await createTrialPeriod(employee.id, payload);
        setTrialPeriods(prev => [res.data, ...prev]);
        toast.success("Période d'essai créée");
      }
      setShowTrialModal(false);
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur enregistrement");
    }
    setSavingTrial(false);
  };

  const handleConfirmTrial = async (tp) => {
    setActioning(tp.id);
    try {
      const res = await confirmTrialPeriod(employee.id, tp.id);
      setTrialPeriods(prev => prev.map(t => t.id === res.data.id ? res.data : t));
      toast.success("Embauche confirmée ✓");
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur confirmation");
    }
    setActioning(null);
  };

  const handleRuptureTrial = async () => {
    const tp = showRuptureModal;
    setActioning(tp.id);
    try {
      const res = await ruptureTrialPeriod(employee.id, tp.id, ruptureMotif);
      setTrialPeriods(prev => prev.map(t => t.id === res.data.id ? res.data : t));
      toast.success("Rupture enregistrée");
      setShowRuptureModal(null);
      setRuptureMotif("");
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur rupture");
    }
    setActioning(null);
  };

  const handleDeleteTrial = async () => {
    try {
      await deleteTrialPeriod(employee.id, trialDeleteConfirm.id);
      setTrialPeriods(prev => prev.filter(t => t.id !== trialDeleteConfirm.id));
      toast.success("Période d'essai supprimée");
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur suppression");
    }
    setTrialDeleteConfirm(null);
  };

  useEffect(() => {
    if (activeTab !== "counters" || !employee) return;
    setLoadingCounters(true);
    Promise.all([
      getRcrLedger(employee.id)
        .then((res) => setRcrData(res.data))
        .catch(() => setRcrData({ balance: 0, ledger: [] })),
      getDebtLedger(employee.id)
        .then((res) => setDebtData(res.data))
        .catch(() => setDebtData({ balance: 0, ledger: [] })),
    ]).finally(() => setLoadingCounters(false));
  }, [activeTab, employee]);

  const handleOpenCalendarModal = () => {
    setIcalToken(employee?.icalToken || null);
    setIcalCopied(false);
    setPdfSection(false);
    setShowCalendarModal(true);
  };

  const handleGenerateIcal = async () => {
    setIcalLoading(true);
    try {
      const { data } = await generateEmployeeIcalToken(employee.id);
      setIcalToken(data.token);
      setEmployee((prev) => ({ ...prev, icalToken: data.token }));
    } catch {
      toast.error("Erreur lors de la génération du lien iCal");
    } finally {
      setIcalLoading(false);
    }
  };

  const handleRevokeIcal = async () => {
    setIcalLoading(true);
    try {
      const { data } = await revokeEmployeeIcalToken(employee.id);
      setIcalToken(data.token);
      setEmployee((prev) => ({ ...prev, icalToken: data.token }));
      setIcalCopied(false);
      toast.success("Lien révoqué — un nouveau lien a été généré");
    } catch {
      toast.error("Erreur lors de la révocation du lien");
    } finally {
      setIcalLoading(false);
    }
  };

  const handleCopyIcal = () => {
    if (!icalToken || !employee) return;
    const url = `${window.location.origin}/api/hr/employees/${employee.id}/ical?token=${icalToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setIcalCopied(true);
      setTimeout(() => setIcalCopied(false), 2000);
    });
  };

  const handleExportPdf = async () => {
    if (!employee) return;
    setPdfLoading(true);
    try {
      const { data } = await getEmployeePlanningEntries(employee.id);
      const { generateEmployeePlanningPdf } =
        await import("../utils/employeePlanningPdf");
      await generateEmployeePlanningPdf(employee, data.schedules, data.anchor);
    } catch {
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const openEdit = () => {
    if (!employee) return;
    setEditForm({
      civilite: employee.civilite || "",
      firstName: employee.firstName || "",
      lastName: employee.lastName || "",
      otherFirstNames: employee.otherFirstNames || "",
      birthName: employee.birthName || "",
      birthDate: employee.birthDate
        ? String(employee.birthDate).slice(0, 10)
        : "",
      inseeNumber: employee.inseeNumber || "",
      birthPostalCode: employee.birthPostalCode || "",
      birthCity: employee.birthCity || "",
      birthCountry: employee.birthCountry || "",
      email: employee.email || "",
      phone: employee.phone || "",
      address: employee.address || "",
      postalCode: employee.postalCode || "",
      city: employee.city || "",
      country: employee.country || "",
      position: employee.position || "",
      contractType: employee.contractType || "CDI",
      status: employee.status || "active",
      startDate: employee.startDate
        ? String(employee.startDate).slice(0, 10)
        : "",
      endDate:
        employee.cddEndDate || employee.endDate
          ? String(employee.cddEndDate || employee.endDate).slice(0, 10)
          : "",
      workHours: employee.workHours ?? 35,
      hourlyRate: employee.hourlyRate ?? "",
      initialSalary: employee.currentSalary ?? "",
      conventionCategory: employee.conventionCategory || "",
      conventionEchelon: employee.conventionEchelon || "",
      transportAmount: employee.transportAmount ?? "",
      mutuelleType: employee.mutuelleType || "en_attente",
      mutuelleValidUntil: employee.mutuelleValidUntil
        ? String(employee.mutuelleValidUntil).slice(0, 10)
        : "",
      mutuelleDocument: employee.mutuelleDocument || "",
      notes: employee.notes || "",
      role: employee.role || "",
      isContinuiteDirection: employee.isContinuiteDirection || false,
      orgGroupId: employee.orgGroupId || "",
      secondaryStructureIds: employee.secondaryStructures?.map(s => s.structureId) || [],
      planningPdfStructures: employee.planningPdfStructures || "",
    });
    setShowEditModal(true);
  };

  const setEditField = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

  const handleSaveEdit = async () => {
    if (
      !editForm.firstName.trim() ||
      !editForm.lastName.trim() ||
      !editForm.position.trim() ||
      !editForm.startDate
    ) {
      toast.error("Prénom, nom, poste et date d'entrée sont obligatoires");
      return;
    }
    setSavingEdit(true);
    try {
      const { initialSalary: _s, hourlyRate: _hr, secondaryStructureIds, ...editRest } = editForm;
      const payload = {
        ...editRest,
        secondaryStructureIds,
        // Date de fin acceptée pour tous les types de contrat (CDI inclus).
        // Vide → null explicite pour permettre à l'admin d'effacer une date.
        cddEndDate: editForm.endDate || null,
        // Tri-state : "" → null (= défaut), "true" → true, "false" → false
        includeInPayroll: editForm.includeInPayroll === "true" ? true
                        : editForm.includeInPayroll === "false" ? false
                        : null,
      };
      await updateEmployee(employee.id, payload);
      const { data } = await getEmployeeById(employee.id);
      setEmployee(data);
      setShowEditModal(false);
      toast.success("Employé modifié");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur sauvegarde");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAdjustCounter = async (type) => {
    if (!adjustForm.amount || adjustForm.type !== type || !employee) return;
    try {
      if (type === "rcr") {
        await adjustRcr(employee.id, {
          amount: Number(adjustForm.amount),
          comment: adjustForm.comment || "Ajustement manuel",
        });
        const res = await getRcrLedger(employee.id);
        setRcrData(res.data);
        toast.success("Compteur RCR ajusté");
      } else {
        await adjustDebt(employee.id, {
          amount: Number(adjustForm.amount),
          comment: adjustForm.comment || "Ajustement manuel",
        });
        const res = await getDebtLedger(employee.id);
        setDebtData(res.data);
        toast.success("Compteur dette ajusté");
      }
      setAdjustForm({ type: "", amount: "", comment: "" });
    } catch {
      toast.error("Erreur ajustement compteur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <LoadingSpinner size="large" text="Chargement..." />
      </div>
    );
  }
  if (!employee) return null;

  const initials =
    `${employee.firstName?.[0] || ""}${employee.lastName?.[0] || ""}`.toUpperCase();
  const isActive = employee.status === "active";
  const fullAddress = [
    employee.address,
    employee.postalCode,
    employee.city,
    employee.country,
  ]
    .filter(Boolean)
    .join(", ");
  const birthPlace = [
    employee.birthPostalCode,
    employee.birthCity,
    employee.birthCountry,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-3"
        >
          <ArrowLeft size={15} /> Retour aux employés
        </button>

        <div className="flex items-center gap-4">
          <input
            ref={avatarInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.svg"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setPhotoUploading(true);
              try {
                await uploadEmployeePhoto(employee.id, file);
                const { data } = await getEmployeeById(employee.id);
                setEmployee(data);
                setPhotoVersion(v => v + 1);
                toast.success("Photo mise à jour");
              } catch (err) {
                toast.error(err.response?.data?.error || "Erreur upload photo");
              } finally {
                setPhotoUploading(false);
                e.target.value = "";
              }
            }}
          />
          <button
            className="relative group w-12 h-12 rounded-full flex-shrink-0 focus:outline-none"
            onClick={() => avatarInputRef.current?.click()}
            title="Changer la photo"
          >
            {photoBlobUrl ? (
              <img
                src={photoBlobUrl}
                alt=""
                className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-lg">
                {initials}
              </div>
            )}
            <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              {photoUploading
                ? <Loader size={14} className="text-white animate-spin" />
                : <Camera size={14} className="text-white" />}
            </div>
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold text-gray-800">
                {employee.firstName} {employee.lastName}
              </h1>
              <CopyNameButton firstName={employee.firstName} lastName={employee.lastName} />
              <Badge
                color={
                  isActive
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }
              >
                {isActive ? "Actif" : "Inactif"}
              </Badge>
              {employee.contractType && (
                <Badge
                  color={
                    CONTRACT_COLORS[employee.contractType] ||
                    "bg-gray-100 text-gray-600"
                  }
                >
                  {employee.contractType}
                </Badge>
              )}
              {hasInapteDefinitif && (
                <Badge color="bg-red-100 text-red-700">
                  <Stethoscope size={11} className="inline mr-1" />
                  Inapte définitif
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {employee.position || "Poste non renseigné"}
            </p>
          </div>
          {activeTab === "infos" && (
            <Btn variant="secondary" size="sm" onClick={openEdit}>
              <Edit size={13} /> Modifier
            </Btn>
          )}
          {activeTab === "infos" && (
            <Btn
              variant="secondary"
              size="sm"
              onClick={handleOpenCalendarModal}
            >
              <CalendarDays size={13} /> Calendrier
            </Btn>
          )}
          {activeTab === "infos" && (
            <ExportButton
              schema={buildExportSchema(employee)}
              structureName={employee.structure?.name || currentStructure?.name}
              disabled={!employee}
            />
          )}
          {activeTab === "infos" && (
            <>
              <Btn
                variant="secondary"
                size="sm"
                onClick={() => setShowOfferLetterModal(true)}
                title="Générer une promesse d'embauche (mode dédié)"
              >
                <FileText size={13} /> Promesse d'embauche
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                onClick={() => setShowGenerateDocModal(true)}
                title="Générer un document à partir d'un modèle"
              >
                <FileText size={13} /> Générer un document
              </Btn>
            </>
          )}
        </div>

        <div className="flex gap-1 mt-4 overflow-x-auto border-t border-gray-100 pt-1">
          {(() => {
            const counts = {
              salaires: salaries.length || null,
              absences: absences.length || null,
              formations: trainings.length || null,
              entretiens: interviews.length || null,
              'visites-medicales': medicalVisits.length || null,
              'periode-essai': trialPeriods.length || null,
              documents: (employee?.documents || [])
                .filter(d => d.type !== 'transport')
                .filter(d => !d.requestItemStatus || d.requestItemStatus === 'validated')
                .length || null,
            };
            return visibleTabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === id
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon size={13} />
                {label}
                {counts[id] != null && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-normal ${
                    activeTab === id ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {counts[id]}
                  </span>
                )}
              </button>
            ));
          })()}
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        {activeTab === "infos" &&
          (() => {
            const MUTUELLE_LABELS = {
              entreprise: "Mutuelle entreprise",
              conjoint: "Mutuelle conjoint",
              en_attente: "En attente (à relancer)",
            };
            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Colonne gauche */}
                <div className="space-y-6">
                  <Section title="État civil" icon={User}>
                    <Grid>
                      <Info label="Civilité" value={employee.civilite} />
                      <Info
                        label="Prénom(s)"
                        value={[employee.firstName, employee.otherFirstNames]
                          .filter(Boolean)
                          .join(" ")}
                      />
                      <Info label="Nom" value={employee.lastName} />
                      <Info
                        label="Nom de naissance"
                        value={employee.birthName}
                      />
                      <Info
                        label="Date de naissance"
                        value={(() => {
                          if (!employee.birthDate) return null;
                          const b = new Date(employee.birthDate), now = new Date();
                          let age = now.getFullYear() - b.getFullYear();
                          if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) age--;
                          return `${fmtDate(employee.birthDate)}  (${age} ans)`;
                        })()}
                      />
                      <Info label="N° INSEE" value={employee.inseeNumber} copyable />
                      {birthPlace && (
                        <Info
                          label="Lieu de naissance"
                          value={birthPlace}
                          span={2}
                        />
                      )}
                    </Grid>
                  </Section>

                  <Section title="Contact" icon={User}>
                    <Grid>
                      <Info label="Email" value={employee.email} copyable />
                      <Info label="Téléphone" value={formatPhone(employee.phone)} copyable />
                      <Info
                        label="Adresse"
                        value={fullAddress || employee.address}
                        span={2}
                        copyable
                      />
                    </Grid>
                  </Section>

                  <Section title="Mutuelle & Transport" icon={Briefcase}>
                    <Grid>
                      <Info
                        label="Mutuelle"
                        value={
                          MUTUELLE_LABELS[employee.mutuelleType] ||
                          employee.mutuelleType
                        }
                      />
                      {employee.mutuelleType === "conjoint" && (
                        <Info
                          label="Validité mutuelle conjoint"
                          value={fmtDate(employee.mutuelleValidUntil)}
                        />
                      )}
                      <Info
                        label="Remboursement transport / mois"
                        value={
                          employee.transportAmount != null
                            ? `${Number(employee.transportAmount).toFixed(2)} €`
                            : null
                        }
                      />
                    </Grid>
                  </Section>
                </div>

                {/* Colonne droite */}
                <div className="space-y-6">
                  <Section title="Contrat" icon={Briefcase}>
                    <Grid>
                      <Info
                        label="Structure principale"
                        value={employee.structure?.name}
                      />
                      {employee.secondaryStructures?.length > 0 ? (
                        <div className="col-span-2">
                          <p className="text-xs text-gray-400 mb-1.5">Structures secondaires</p>
                          <div className="flex flex-wrap gap-1.5">
                            {employee.secondaryStructures.map(s => (
                              <span
                                key={s.structureId}
                                className="px-2.5 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium"
                              >
                                {s.structure?.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <Info label="Poste" value={employee.position} />
                      <Info
                        label="Type de contrat"
                        value={employee.contractType}
                      />
                      <Info
                        label="Date d'embauche"
                        value={fmtDate(employee.startDate)}
                      />
                      <Info
                        label="Ancienneté"
                        value={(() => {
                          if (!employee.startDate) return null;
                          const start = new Date(employee.startDate);
                          const now = new Date();
                          let years = now.getFullYear() - start.getFullYear();
                          let months = now.getMonth() - start.getMonth();
                          if (months < 0) { years--; months += 12; }
                          if (years === 0 && months === 0) return 'Moins d\'un mois';
                          const partsY = years > 0 ? `${years} an${years > 1 ? 's' : ''}` : '';
                          const partsM = months > 0 ? `${months} mois` : '';
                          return [partsY, partsM].filter(Boolean).join(' ');
                        })()}
                      />
                      <Info
                        label="Fin période d'essai"
                        value={(() => {
                          if (!employee.trialEndDate) return null;
                          const days = Math.ceil((new Date(employee.trialEndDate) - new Date()) / 86400000);
                          const dateStr = fmtDate(employee.trialEndDate);
                          if (days > 0) return `${dateStr}  (J-${days})`;
                          return dateStr;
                        })()}
                      />
                      <Info
                        label="Fin de contrat"
                        value={fmtDate(employee.cddEndDate || employee.endDate)}
                      />
                      <Info
                        label="Heures / semaine"
                        value={
                          employee.workHours ? `${employee.workHours}h` : null
                        }
                      />
                      <Info
                        label="Taux horaire"
                        value={
                          employee.hourlyRate != null
                            ? `${Number(employee.hourlyRate).toFixed(4)} €`
                            : null
                        }
                      />
                      <Info
                        label="Catégorie convention"
                        value={employee.conventionCategory}
                      />
                      <Info
                        label="Échelon"
                        value={
                          employee.conventionEchelon != null
                            ? String(employee.conventionEchelon)
                            : null
                        }
                      />
                    </Grid>
                  </Section>

                  <Section title="Notes internes" icon={FileText}>
                    {employee.notes ? (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {employee.notes}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        Aucune note — modifiez la fiche pour en ajouter.
                      </p>
                    )}
                  </Section>
                </div>
              </div>
            );
          })()}

        {activeTab === "salaires" &&
          (() => {
            const sim = computeSalarySimulation(
              salaryForm.grossMonthlySalary,
              salaryForm.salaireMutuelle,
            );
            const currentEntry = salaries[0];
            const displayedSalary =
              currentEntry?.grossMonthlySalary ?? employee?.currentSalary;
            const isRetroactive =
              salaryForm.effectiveDate &&
              currentEntry &&
              new Date(salaryForm.effectiveDate) <
                new Date(currentEntry.effectiveDate);
            const isFarFuture =
              salaryForm.effectiveDate &&
              new Date(salaryForm.effectiveDate) >
                new Date(Date.now() + 365 * 24 * 3600 * 1000);
            const chartData = [...salaries].reverse();
            const hasChart = chartData.length >= 2;
            const minVal = hasChart
              ? Math.min(...chartData.map((s) => s.grossMonthlySalary))
              : 0;
            const maxVal = hasChart
              ? Math.max(...chartData.map((s) => s.grossMonthlySalary))
              : 0;
            const range = maxVal - minVal || 1;
            const setSal = (k, v) => setSalaryForm((f) => ({ ...f, [k]: v }));
            return (
              <div className="space-y-4">
                {/* Salaire actuel */}
                <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-4">
                  <div className="flex-1">
                    <p className="text-xs text-gray-400 mb-1">
                      Salaire brut mensuel actuel
                    </p>
                    {displayedSalary != null ? (
                      <>
                        <p className="text-2xl font-bold text-gray-900">
                          {fmtCurrency(displayedSalary)}
                        </p>
                        {currentEntry ? (
                          <p className="text-xs text-gray-400 mt-1">
                            Depuis le {fmtDate(currentEntry.effectiveDate)}
                            {currentEntry.comment &&
                              ` · ${currentEntry.comment}`}
                          </p>
                        ) : (
                          <p className="text-xs text-amber-600 mt-1">
                            Renseigné dans la fiche — ajoutez une entrée pour
                            initialiser l'historique
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-400 mt-1">
                        Non renseigné
                      </p>
                    )}
                  </div>
                  {loadingSalaries ? (
                    <Loader className="animate-spin text-primary" size={20} />
                  ) : (
                    <Btn
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setSalaryForm(EMPTY_SALARY_FORM);
                        setShowSalaryModal(true);
                      }}
                    >
                      +{" "}
                      {salaries.length === 0
                        ? "Ajouter le salaire initial"
                        : "Nouvelle entrée"}
                    </Btn>
                  )}
                </div>

                {/* Graphique */}
                {hasChart && (
                  <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
                    <p className="text-xs font-semibold text-gray-500 mb-3">
                      Évolution du salaire brut
                    </p>
                    <svg
                      width="100%"
                      height={200}
                      viewBox="0 0 600 200"
                      preserveAspectRatio="none"
                    >
                      <defs>
                        <linearGradient
                          id="salGrad"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="rgb(var(--color-primary))"
                            stopOpacity="0.15"
                          />
                          <stop
                            offset="100%"
                            stopColor="rgb(var(--color-primary))"
                            stopOpacity="0"
                          />
                        </linearGradient>
                      </defs>
                      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                        <line
                          key={t}
                          x1="40"
                          x2="590"
                          y1={20 + t * 160}
                          y2={20 + t * 160}
                          stroke="#e5e7eb"
                          strokeWidth="1"
                        />
                      ))}
                      <path
                        d={[
                          ...chartData.map((s, i) => {
                            const x = 40 + (i / (chartData.length - 1)) * 550;
                            const y =
                              20 +
                              (1 - (s.grossMonthlySalary - minVal) / range) *
                                160;
                            return `${i === 0 ? "M" : "L"}${x},${y}`;
                          }),
                          `L${40 + 550},${180}`,
                          `L40,${180}Z`,
                        ].join(" ")}
                        fill="url(#salGrad)"
                      />
                      <polyline
                        points={chartData
                          .map((s, i) => {
                            const x = 40 + (i / (chartData.length - 1)) * 550;
                            const y =
                              20 +
                              (1 - (s.grossMonthlySalary - minVal) / range) *
                                160;
                            return `${x},${y}`;
                          })
                          .join(" ")}
                        fill="none"
                        stroke="rgb(var(--color-primary))"
                        strokeWidth="2.5"
                        strokeLinejoin="round"
                      />
                      {chartData.map((s, i) => {
                        const x = 40 + (i / (chartData.length - 1)) * 550;
                        const y =
                          20 +
                          (1 - (s.grossMonthlySalary - minVal) / range) * 160;
                        return (
                          <g key={s.id}>
                            <circle
                              cx={x}
                              cy={y}
                              r="4"
                              fill="rgb(var(--color-primary))"
                            />
                            <text
                              x={x}
                              y={y - 10}
                              textAnchor="middle"
                              fontSize="9"
                              fill="#6b7280"
                            >
                              {fmtCurrency(s.grossMonthlySalary)}
                            </text>
                            <text
                              x={x}
                              y={196}
                              textAnchor="middle"
                              fontSize="8"
                              fill="#9ca3af"
                            >
                              {fmtDate(s.effectiveDate).slice(3)}
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                )}

                {/* Tableau historique */}
                {salaries.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50">
                      <TrendingUp size={15} className="text-gray-400" />
                      <h2 className="text-sm font-semibold text-gray-700">
                        Historique
                      </h2>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs text-gray-500">
                          <th className="px-4 py-2.5 font-medium">
                            Date d'effet
                          </th>
                          <th className="px-4 py-2.5 font-medium">
                            Brut mensuel
                          </th>
                          <th className="px-4 py-2.5 font-medium">
                            Base salariale
                          </th>
                          <th className="px-4 py-2.5 font-medium">Mutuelle</th>
                          <th className="px-4 py-2.5 font-medium">
                            Commentaire
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {salaries.map((s, idx) => (
                          <tr
                            key={s.id}
                            className={`hover:bg-gray-50 transition-colors ${idx === 0 ? "bg-primary/5" : ""}`}
                          >
                            <td className="px-4 py-3 font-medium text-gray-800">
                              {fmtDate(s.effectiveDate)}
                            </td>
                            <td className="px-4 py-3 font-semibold text-gray-900">
                              {fmtCurrency(s.grossMonthlySalary)}
                            </td>
                            <td className="px-4 py-3 text-gray-500">
                              {s.baseSalarialBrut
                                ? fmtCurrency(s.baseSalarialBrut)
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-500">
                              {s.salaireMutuelle
                                ? fmtCurrency(s.salaireMutuelle)
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-500 max-w-xs truncate">
                              {s.comment || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Modal ajout */}
                {showSalaryModal && (
                  <Modal
                    title="Nouvelle entrée salariale"
                    onClose={() => setShowSalaryModal(false)}
                    size="xl"
                    footer={
                      <>
                        <Btn
                          variant="secondary"
                          onClick={() => setShowSalaryModal(false)}
                        >
                          Annuler
                        </Btn>
                        <Btn
                          variant="primary"
                          onClick={handleCreateSalary}
                          loading={savingSalary}
                        >
                          Enregistrer
                        </Btn>
                      </>
                    }
                  >
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <FormField label="Salaire brut mensuel (€)" required>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={salaryForm.grossMonthlySalary}
                            onChange={(e) =>
                              setSal("grossMonthlySalary", e.target.value)
                            }
                            placeholder="Ex : 2100"
                            className={inputCls}
                            autoFocus
                          />
                        </FormField>
                        <FormField label="Base salariale brut (€)">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={salaryForm.baseSalarialBrut}
                            onChange={(e) =>
                              setSal("baseSalarialBrut", e.target.value)
                            }
                            placeholder="Si différent du brut"
                            className={inputCls}
                          />
                        </FormField>
                        <FormField label="Part salariale mutuelle (€/mois)">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={salaryForm.salaireMutuelle}
                            onChange={(e) =>
                              setSal("salaireMutuelle", e.target.value)
                            }
                            placeholder="Ex : 50"
                            className={inputCls}
                          />
                        </FormField>
                        <FormField label="Date de prise d'effet" required>
                          <input
                            type="date"
                            value={salaryForm.effectiveDate}
                            onChange={(e) =>
                              setSal("effectiveDate", e.target.value)
                            }
                            className={inputCls}
                          />
                          {isRetroactive && (
                            <p className="text-xs text-amber-600 mt-1">
                              ⚠ Date antérieure à la dernière entrée
                              (rétroactif)
                            </p>
                          )}
                          {isFarFuture && (
                            <p className="text-xs text-amber-600 mt-1">
                              ⚠ Date à plus d'un an dans le futur
                            </p>
                          )}
                        </FormField>
                        <FormField label="Commentaire">
                          <textarea
                            rows={2}
                            value={salaryForm.comment}
                            onChange={(e) => setSal("comment", e.target.value)}
                            placeholder="Ex : Augmentation annuelle 3%"
                            className={inputCls}
                          />
                        </FormField>
                      </div>
                      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 self-start">
                        <p className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1">
                          <TrendingUp size={13} className="text-primary" />{" "}
                          Simulation budgétaire
                        </p>
                        {parseFloat(salaryForm.grossMonthlySalary) > 0 ? (
                          <div className="space-y-2 text-xs">
                            <SimRow
                              label="Salaire brut mensuel"
                              value={fmt2(sim.netMensuel + sim.cotisations)}
                              bold
                            />
                            <SimRow
                              label="Salaire brut annuel"
                              value={fmt2(sim.brutAnnuel)}
                            />
                            <div className="border-t border-gray-200 my-2" />
                            <SimRow
                              label="Cotisations salariées (8%)"
                              value={`− ${fmt2(sim.cotisations)}`}
                              sub
                            />
                            <SimRow
                              label="Salaire net mensuel"
                              value={fmt2(sim.netMensuel)}
                              bold
                            />
                            <SimRow
                              label="Salaire net annuel"
                              value={fmt2(sim.netAnnuel)}
                            />
                            <div className="border-t border-gray-200 my-2" />
                            <SimRow
                              label="Charges patronales (42%)"
                              value={`+ ${fmt2(sim.chargesMensuelles)}`}
                              sub
                            />
                            <SimRow
                              label="Coût total employeur/mois"
                              value={fmt2(sim.coutMensuel)}
                              bold
                            />
                            <SimRow
                              label="Coût total annuel"
                              value={fmt2(sim.coutAnnuel)}
                            />
                            {sim.mutuelle > 0 && (
                              <>
                                <div className="border-t border-gray-200 my-2" />
                                <SimRow
                                  label="Mutuelle salarié/mois"
                                  value={fmt2(sim.mutuelle)}
                                />
                              </>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">
                            Saisissez un salaire brut pour voir la simulation.
                          </p>
                        )}
                      </div>
                    </div>
                  </Modal>
                )}
              </div>
            );
          })()}

        {activeTab === "absences" &&
          (() => {
            const ABSENCE_TYPES = [
              { value: "conge_paye", label: "Congé payé" },
              { value: "conge_sans_solde", label: "Congé sans solde" },
              { value: "maladie", label: "Maladie" },
              { value: "autre", label: "Autre" },
            ];
            const ABSENCE_LABELS = Object.fromEntries(
              ABSENCE_TYPES.map((t) => [t.value, t.label]),
            );
            const STATUS_COLORS = {
              en_attente: "bg-yellow-100 text-yellow-700",
              valide: "bg-green-100 text-green-700",
              refuse: "bg-red-100 text-red-700",
            };
            const STATUS_LABELS = {
              en_attente: "En attente",
              valide: "Validée",
              refuse: "Refusée",
            };
            const setAbs = (k, v) => setAbsenceForm((f) => ({ ...f, [k]: v }));

            // Compteurs par statut
            const absCounts = { valide: 0, en_attente: 0, refuse: 0 };
            absences.forEach(a => { if (absCounts[a.status] !== undefined) absCounts[a.status]++; });

            const displayedAbsences = [...absences]
              .filter(a => !absTypeFilter || a.type === absTypeFilter)
              .sort((a, b) => absenceSortDir * (new Date(b.startDate) - new Date(a.startDate)));

            return (
              <div className="space-y-4">
                {/* ── Stats rapides ── */}
                {!loadingAbsences && absences.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-green-700">{absCounts.valide}</div>
                      <div className="text-xs text-green-600">Validées</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-yellow-700">{absCounts.en_attente}</div>
                      <div className="text-xs text-yellow-600">En attente</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-red-700">{absCounts.refuse}</div>
                      <div className="text-xs text-red-600">Refusées</div>
                    </div>
                  </div>
                )}

                {/* ── Barre filtres + actions ── */}
                <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap gap-3 items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1.5">Type</div>
                    <div className="flex gap-1 flex-wrap">
                      {[{ value: '', label: 'Tous' }, ...ABSENCE_TYPES].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setAbsTypeFilter(opt.value)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            absTypeFilter === opt.value
                              ? 'bg-primary text-white border-primary'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-primary'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAbsenceSortDir(d => -d)}
                      className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Date {absenceSortDir === -1 ? '↓' : '↑'}
                    </button>
                    <Btn
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setAbsenceForm({ type: "conge_paye", startDate: "", endDate: "", comment: "" });
                        setShowAbsenceForm(true);
                      }}
                    >
                      <Plus size={13} /> Nouvelle absence
                    </Btn>
                  </div>
                </div>

                {loadingAbsences ? (
                  <div className="flex justify-center py-8">
                    <Loader className="animate-spin text-primary" size={24} />
                  </div>
                ) : absences.length === 0 ? (
                  <div className="text-center py-12 text-gray-400 text-sm bg-white rounded-xl border border-gray-200">
                    Aucune absence enregistrée
                  </div>
                ) : displayedAbsences.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 text-sm bg-white rounded-xl border border-gray-200">
                    Aucune absence pour ce filtre
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs text-gray-500">
                          <th className="px-4 py-2.5 font-medium">Type</th>
                          <th className="px-4 py-2.5 font-medium">Du</th>
                          <th className="px-4 py-2.5 font-medium">Au</th>
                          <th className="px-4 py-2.5 font-medium">Motif</th>
                          <th className="px-4 py-2.5 font-medium">Statut</th>
                          <th className="px-4 py-2.5 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {displayedAbsences.map((a) => (
                          <tr
                            key={a.id}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-4 py-3 font-medium text-gray-800">
                              {ABSENCE_LABELS[a.type] || a.type}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {fmtDate(a.startDate)}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {fmtDate(a.endDate)}
                            </td>
                            <td className="px-4 py-3 text-gray-500 max-w-xs truncate">
                              {a.comment || "—"}
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                color={
                                  STATUS_COLORS[a.status] ||
                                  "bg-gray-100 text-gray-500"
                                }
                              >
                                {STATUS_LABELS[a.status] || a.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2 justify-end items-center">
                                {a.documentName && (
                                  <Btn
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleViewAbsenceDocument(a)}
                                    title={a.documentName}
                                  >
                                    <FileText
                                      size={13}
                                      className="text-blue-500"
                                    />
                                  </Btn>
                                )}
                                {a.status === "en_attente" && (
                                  <>
                                    <Btn
                                      size="sm"
                                      variant="primary"
                                      onClick={() =>
                                        setAbsenceConfirm({
                                          type: "validate",
                                          id: a.id,
                                        })
                                      }
                                    >
                                      Valider
                                    </Btn>
                                    <Btn
                                      size="sm"
                                      variant="secondary"
                                      onClick={() =>
                                        setAbsenceConfirm({
                                          type: "refuse",
                                          id: a.id,
                                        })
                                      }
                                    >
                                      Refuser
                                    </Btn>
                                  </>
                                )}
                                {a.type === "maladie" &&
                                  a.status === "valide" &&
                                  a.documentName && (
                                    <Btn
                                      size="sm"
                                      variant="ghost"
                                      loading={sendingEmail === a.id}
                                      onClick={() => setEmailConfirm(a)}
                                      title={
                                        a.emailSentAt
                                          ? `Renvoyé — dernier envoi le ${fmtDate(a.emailSentAt)}`
                                          : "Envoyer au cabinet RH"
                                      }
                                    >
                                      <Send
                                        size={13}
                                        className={
                                          a.emailSentAt
                                            ? "text-green-500"
                                            : "text-primary"
                                        }
                                      />
                                    </Btn>
                                  )}
                                <Btn
                                  size="sm"
                                  variant="danger"
                                  onClick={() =>
                                    setAbsenceConfirm({
                                      type: "delete",
                                      id: a.id,
                                    })
                                  }
                                >
                                  Supprimer
                                </Btn>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {showAbsenceForm && (
                  <Modal
                    title="Nouvelle absence"
                    onClose={() => setShowAbsenceForm(false)}
                    size="md"
                    footer={
                      <>
                        <Btn
                          variant="secondary"
                          onClick={() => setShowAbsenceForm(false)}
                        >
                          Annuler
                        </Btn>
                        <Btn
                          variant="primary"
                          onClick={handleCreateAbsence}
                          loading={savingAbsence}
                        >
                          Créer
                        </Btn>
                      </>
                    }
                  >
                    <div className="space-y-4">
                      <FormField label="Type" required>
                        <select
                          value={absenceForm.type}
                          onChange={(e) => setAbs("type", e.target.value)}
                          className={inputCls}
                        >
                          {ABSENCE_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <div className="grid grid-cols-2 gap-4">
                        <FormField label="Date de début" required>
                          <input
                            type="date"
                            value={absenceForm.startDate}
                            onChange={(e) =>
                              setAbs("startDate", e.target.value)
                            }
                            className={inputCls}
                          />
                        </FormField>
                        <FormField label="Date de fin" required>
                          <input
                            type="date"
                            value={absenceForm.endDate}
                            onChange={(e) => setAbs("endDate", e.target.value)}
                            className={inputCls}
                          />
                        </FormField>
                      </div>
                      <FormField label="Motif">
                        <input
                          type="text"
                          value={absenceForm.comment}
                          onChange={(e) => setAbs("comment", e.target.value)}
                          placeholder="Optionnel"
                          className={inputCls}
                        />
                      </FormField>
                      <FormField
                        label={
                          absenceForm.type === "maladie"
                            ? "Arrêt de travail (PDF) *"
                            : "Justificatif (PDF, optionnel)"
                        }
                      >
                        <input
                          ref={absenceFileRef}
                          type="file"
                          accept="application/pdf"
                          className={
                            inputCls +
                            " py-1.5 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                          }
                        />
                        {absenceForm.type === "maladie" && (
                          <p className="text-xs text-red-500 mt-1">
                            Obligatoire pour un arrêt maladie
                          </p>
                        )}
                      </FormField>
                    </div>
                  </Modal>
                )}

                <ConfirmModal
                  open={!!absenceConfirm && absenceConfirm.type === "validate"}
                  title="Valider l'absence"
                  message="Cette absence sera marquée comme validée."
                  onConfirm={handleAbsenceConfirm}
                  onCancel={() => setAbsenceConfirm(null)}
                />
                <ConfirmModal
                  open={!!absenceConfirm && absenceConfirm.type === "refuse"}
                  title="Refuser l'absence"
                  message="Cette absence sera marquée comme refusée."
                  onConfirm={handleAbsenceConfirm}
                  onCancel={() => setAbsenceConfirm(null)}
                />
                <ConfirmModal
                  open={!!absenceConfirm && absenceConfirm.type === "delete"}
                  title="Supprimer l'absence"
                  message="Cette absence sera supprimée définitivement."
                  onConfirm={handleAbsenceConfirm}
                  onCancel={() => setAbsenceConfirm(null)}
                  danger
                />
                <ConfirmModal
                  open={!!emailConfirm}
                  title="Envoyer au cabinet RH"
                  message={
                    emailConfirm
                      ? `Envoyer l'arrêt de travail de ${employee?.firstName ?? ""} ${employee?.lastName ?? ""} (du ${fmtDate(emailConfirm.startDate)} au ${fmtDate(emailConfirm.endDate)}) au cabinet RH ?${emailConfirm.emailSentAt ? ` Dernier envoi : ${fmtDate(emailConfirm.emailSentAt)}.` : ""}`
                      : ""
                  }
                  onConfirm={handleSendEmail}
                  onCancel={() => setEmailConfirm(null)}
                />
              </div>
            );
          })()}

        {activeTab === "documents" &&
          (() => {
            // Types administrables (fetch au montage du composant)
            // Skip les types "transport" qui sont gérés séparément
            const DOC_TYPES = Object.fromEntries(
              (docTypes || []).map((t) => [t.slug, t.name])
            );
            const ABSENCE_TYPE_LABELS = {
              conge_paye: "Congé payé",
              conge_sans_solde: "Sans solde",
              maladie: "Maladie",
              autre: "Autre absence",
            };

            // Filtre commun : on n'affiche dans l'onglet Documents que ce qui est validé
            // (ou non rattaché à une demande, c'est-à-dire saisie manuelle).
            // Les docs rattachés à un item uploadé/refusé restent visibles dans "Demandes docs".
            const isVisibleInDocsTab = (d) =>
              !d.requestItemStatus || d.requestItemStatus === 'validated';

            // Séparer les justificatifs transport des autres documents
            const transportDocs = (employee.documents || [])
              .filter((d) => d.type === "transport")
              .filter(isVisibleInDocsTab)
              .sort(
                (a, b) =>
                  new Date(b.period || b.uploadedAt) -
                  new Date(a.period || a.uploadedAt),
              );

            const now = new Date();
            const firstOfCurrentMonth = new Date(
              Date.UTC(now.getFullYear(), now.getMonth(), 1),
            );

            // Documents directs de l'employé (hors transport)
            const employeeDocs = (employee.documents || [])
              .filter((d) => d.type !== "transport")
              .filter(isVisibleInDocsTab)
              .map((d) => ({
                _kind: "doc",
                id: d.id,
                label: DOC_TYPES[d.type] || d.type,
                badgeColor: "bg-blue-50 text-blue-700",
                fileName: d.fileName,
                notes: d.notes || null,
                date: d.uploadedAt,
                _doc: d,
              }));

            // Documents rattachés à des absences
            const absenceDocs = (employee.absences || [])
              .filter((a) => a.documentUrl && a.documentName)
              .map((a) => ({
                _kind: "absence",
                id: a.id,
                label: ABSENCE_TYPE_LABELS[a.type] || a.type,
                badgeColor: "bg-orange-50 text-orange-700",
                fileName: a.documentName,
                notes: `Du ${fmtDate(a.startDate)} au ${fmtDate(a.endDate)}`,
                date: a.createdAt,
                _absence: a,
              }));

            // Fusion triée par date décroissante
            const allDocs = [...employeeDocs, ...absenceDocs].sort(
              (a, b) => new Date(b.date) - new Date(a.date),
            );

            return (
              <div className="space-y-4">
                {/* ── Section justificatif de transport ── */}
                {parseFloat(employee.transportAmount) > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700">
                          Justificatif de transport
                        </h4>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Titre transport :{" "}
                          {new Intl.NumberFormat("fr-FR", {
                            style: "currency",
                            currency: "EUR",
                          }).format(employee.transportAmount)}{" "}
                          / mois
                        </p>
                      </div>
                      <Btn
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setTransportForm({
                            providerId: "",
                            month: "",
                            year: "",
                          });
                          setTransportFile(null);
                          setShowTransportModal(true);
                        }}
                      >
                        <Upload size={13} /> Ajouter
                      </Btn>
                    </div>

                    {transportDocs.length === 0 ? (
                      <div className="flex items-center gap-2 py-2">
                        <Badge color="bg-orange-100 text-orange-700">
                          Justificatif manquant
                        </Badge>
                        <span className="text-xs text-gray-400">
                          Aucun justificatif déposé pour ce mois.
                        </span>
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border border-gray-100 rounded">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                              Période
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                              Fichier
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                              Déposé le
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {transportDocs.map((doc, idx) => {
                            const isPast =
                              doc.period &&
                              new Date(doc.period) < firstOfCurrentMonth;
                            return (
                              <tr
                                key={doc.id}
                                className={
                                  idx === 0 ? "bg-green-50" : "hover:bg-gray-50"
                                }
                              >
                                <td className="px-3 py-2.5 font-medium text-gray-700">
                                  {doc.period
                                    ? new Intl.DateTimeFormat("fr-FR", {
                                        month: "long",
                                        year: "numeric",
                                        timeZone: "UTC",
                                      }).format(new Date(doc.period))
                                    : "—"}
                                  {idx === 0 && (
                                    <Badge color="bg-green-100 text-green-700 ml-2">
                                      Récent
                                    </Badge>
                                  )}
                                </td>
                                <td
                                  className="px-3 py-2.5 text-gray-600 max-w-[180px] truncate font-mono text-xs"
                                  title={doc.fileName}
                                >
                                  {doc.fileName}
                                </td>
                                <td className="px-3 py-2.5 text-gray-400 text-xs whitespace-nowrap">
                                  {fmtDate(doc.uploadedAt)}
                                </td>
                                <td className="px-3 py-2.5 text-right">
                                  <div className="flex justify-end gap-1">
                                    <Btn
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleViewDocument(doc)}
                                    >
                                      <Eye size={13} />
                                    </Btn>
                                    <Btn
                                      variant="ghost"
                                      size="sm"
                                      className={
                                        isPast
                                          ? "opacity-30 cursor-not-allowed"
                                          : "text-red-500 hover:text-red-600"
                                      }
                                      onClick={() =>
                                        !isPast &&
                                        setTransportDeleteConfirm(doc)
                                      }
                                      title={
                                        isPast
                                          ? "Les justificatifs des mois passés ne peuvent pas être supprimés"
                                          : "Supprimer"
                                      }
                                    >
                                      <Trash2 size={13} />
                                    </Btn>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Modal upload justificatif transport */}
                {showTransportModal && (
                  <Modal
                    title="Ajouter un justificatif de transport"
                    size="sm"
                    onClose={() => setShowTransportModal(false)}
                    footer={
                      <>
                        <Btn
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowTransportModal(false)}
                        >
                          Annuler
                        </Btn>
                        <Btn
                          variant="primary"
                          size="sm"
                          loading={uploadingTransport}
                          onClick={handleUploadTransport}
                        >
                          Déposer
                        </Btn>
                      </>
                    }
                  >
                    <div className="space-y-4">
                      <FormField label="Fournisseur" required>
                        <select
                          className={inputCls}
                          value={transportForm.providerId}
                          onChange={(e) =>
                            setTransportForm((f) => ({
                              ...f,
                              providerId: e.target.value,
                            }))
                          }
                        >
                          <option value="">— Choisir —</option>
                          {transportProviders.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Mois concerné" required>
                        <div className="flex gap-2">
                          <select
                            className={inputCls + " flex-1"}
                            value={transportForm.month}
                            onChange={(e) =>
                              setTransportForm((f) => ({
                                ...f,
                                month: e.target.value,
                              }))
                            }
                          >
                            <option value="">— Mois —</option>
                            {[
                              "Janvier",
                              "Février",
                              "Mars",
                              "Avril",
                              "Mai",
                              "Juin",
                              "Juillet",
                              "Août",
                              "Septembre",
                              "Octobre",
                              "Novembre",
                              "Décembre",
                            ].map((m, i) => (
                              <option
                                key={m}
                                value={String(i + 1).padStart(2, "0")}
                              >
                                {m}
                              </option>
                            ))}
                          </select>
                          <select
                            className={inputCls + " w-24"}
                            value={transportForm.year}
                            onChange={(e) =>
                              setTransportForm((f) => ({
                                ...f,
                                year: e.target.value,
                              }))
                            }
                          >
                            <option value="">— Année —</option>
                            {Array.from({ length: 4 }, (_, i) =>
                              String(new Date().getFullYear() - i),
                            ).map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </select>
                        </div>
                      </FormField>
                      <FormField label="Fichier (PDF, JPG, PNG)" required>
                        <input
                          ref={transportFileRef}
                          type="file"
                          accept="application/pdf,image/jpeg,image/png"
                          className="hidden"
                          onChange={(e) => {
                            setTransportFile(e.target.files[0]);
                            e.target.value = "";
                          }}
                        />
                        <Btn
                          variant="secondary"
                          size="sm"
                          onClick={() => transportFileRef.current?.click()}
                        >
                          <Upload size={13} />{" "}
                          {transportFile
                            ? transportFile.name
                            : "Choisir un fichier"}
                        </Btn>
                      </FormField>
                    </div>
                  </Modal>
                )}

                {/* Modal confirmation suppression transport */}
                {transportDeleteConfirm && (
                  <Modal
                    title="Supprimer le justificatif"
                    size="sm"
                    onClose={() => setTransportDeleteConfirm(null)}
                    footer={
                      <>
                        <Btn
                          variant="secondary"
                          size="sm"
                          onClick={() => setTransportDeleteConfirm(null)}
                        >
                          Annuler
                        </Btn>
                        <Btn
                          variant="danger"
                          size="sm"
                          onClick={handleDeleteTransport}
                        >
                          Supprimer
                        </Btn>
                      </>
                    }
                  >
                    <p className="text-sm text-gray-700">
                      Supprimer le justificatif{" "}
                      <strong>{transportDeleteConfirm.fileName}</strong> ?
                    </p>
                  </Modal>
                )}

                {/* ── Documents généraux ── */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Ajouter un document (PDF)
                  </h4>
                  <div className="flex gap-2 flex-wrap items-end">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">
                        Type
                      </label>
                      <select
                        className={inputCls + " w-40"}
                        value={docUploadForm.type}
                        onChange={(e) =>
                          setDocUploadForm((f) => ({
                            ...f,
                            type: e.target.value,
                          }))
                        }
                      >
                        {Object.entries(DOC_TYPES).map(([k, l]) => (
                          <option key={k} value={k}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <label className="block text-xs text-gray-400 mb-1">
                        Notes (optionnel)
                      </label>
                      <input
                        className={inputCls}
                        placeholder="Ex : signé le 01/01/2025"
                        value={docUploadForm.notes}
                        onChange={(e) =>
                          setDocUploadForm((f) => ({
                            ...f,
                            notes: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <input
                        ref={docFileRef}
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          handleUploadDocument(e.target.files[0]);
                          e.target.value = "";
                        }}
                      />
                      <Btn
                        variant="primary"
                        size="sm"
                        loading={uploadingDoc}
                        onClick={() => docFileRef.current?.click()}
                      >
                        <Upload size={13} /> Choisir un PDF
                      </Btn>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {allDocs.length === 0 ? (
                    <div className="p-8 text-center text-sm text-gray-400">
                      Aucun document pour cet employé
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                            Type
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                            Fichier
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                            Notes / Période
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                            Date
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {allDocs.map((row) => (
                          <tr
                            key={`${row._kind}-${row.id}`}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-3">
                              <Badge color={row.badgeColor}>{row.label}</Badge>
                            </td>
                            <td
                              className="px-4 py-3 text-gray-700 max-w-[180px] truncate"
                              title={row.fileName}
                            >
                              {row.fileName}
                            </td>
                            <td
                              className="px-4 py-3 text-gray-500 italic text-xs max-w-[160px] truncate"
                              title={row.notes || ""}
                            >
                              {row.notes || "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                              {fmtDate(row.date)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-1">
                                <Btn
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    row._kind === "doc"
                                      ? handleViewDocument(row._doc)
                                      : handleViewAbsenceDocument(row._absence)
                                  }
                                >
                                  <Eye size={13} />
                                </Btn>
                                {row._kind === "doc" && (
                                  <Btn
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDocConfirm(row._doc)}
                                  >
                                    <Trash2
                                      size={13}
                                      className="text-red-400"
                                    />
                                  </Btn>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <ConfirmModal
                  open={!!docConfirm}
                  title="Supprimer le document"
                  message={`Le fichier « ${docConfirm?.fileName} » sera supprimé définitivement.`}
                  onConfirm={handleDeleteDocument}
                  onCancel={() => setDocConfirm(null)}
                  danger
                />
              </div>
            );
          })()}

        {activeTab === "planning" && employee && (
          <PlanningTab employee={employee} structureId={currentStructure?.id || employee.structureId} />
        )}

        {activeTab === "formations" &&
          (() => {
            // Calcul des formations "remplacées" : une formation expirée dont une version
            // plus récente du même type existe → statut affiché = 'superseded'
            const supersededIds = new Set();
            if (trainings.length > 0) {
              const byType = {};
              trainings.forEach((t) => {
                if (!t.trainingTypeId) return;
                if (!byType[t.trainingTypeId]) byType[t.trainingTypeId] = [];
                byType[t.trainingTypeId].push(t);
              });
              Object.values(byType).forEach((group) => {
                if (group.length < 2) return;
                group.sort(
                  (a, b) => new Date(b.startDate) - new Date(a.startDate),
                );
                const [latest, ...older] = group;
                if (latest.recyclingStatus !== "expired" && latest.recyclingStatus !== "planned") {
                  older.forEach((t) => {
                    if (t.recyclingStatus === "expired")
                      supersededIds.add(t.id);
                  });
                }
              });
            }

            const FILTER_OPTS = [
              { value: "planned",    label: "Planifiées",    cls: "bg-blue-100 text-blue-700"    },
              { value: "expired",    label: "Expirées",      cls: "bg-red-100 text-red-700"      },
              { value: "soon",       label: "Expire bientôt", cls: "bg-yellow-100 text-yellow-700" },
              { value: "ok",         label: "Réalisée",      cls: "bg-green-100 text-green-700"  },
              { value: "superseded", label: "Recyclées",     cls: "bg-gray-100 text-gray-400"    },
            ];

            // Filtrage + tri
            let displayed = trainings.map((t) => ({
              ...t,
              effectiveStatus: supersededIds.has(t.id)
                ? "superseded"
                : t.recyclingStatus,
            }));
            if (trainingFilterStatus.length > 0)
              displayed = displayed.filter((t) =>
                trainingFilterStatus.includes(t.effectiveStatus),
              );
            if (trainingFilterType)
              displayed = displayed.filter(
                (t) => t.trainingTypeId === trainingFilterType,
              );
            if (trainingFilterSort === "recyclingDate") {
              displayed.sort((a, b) => {
                if (!a.recyclingDate && !b.recyclingDate) return 0;
                if (!a.recyclingDate) return 1;
                if (!b.recyclingDate) return -1;
                return new Date(a.recyclingDate) - new Date(b.recyclingDate);
              });
            } else {
              displayed.sort(
                (a, b) => new Date(b.startDate) - new Date(a.startDate),
              );
            }

            // Compteurs sur données brutes (hors filtre)
            const counts = { ok: 0, soon: 0, expired: 0, superseded: 0, none: 0, planned: 0 };
            trainings.forEach((t) => {
              const es = supersededIds.has(t.id)
                ? "superseded"
                : t.recyclingStatus;
              if (counts[es] !== undefined) counts[es]++;
            });
            counts.ok += counts.none;

            return (
              <div className="space-y-4">
                {/* Compteurs rapides */}
                {!loadingTrainings && trainings.length > 0 && (
                  <div className="grid grid-cols-5 gap-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-blue-700">
                        {counts.planned}
                      </div>
                      <div className="text-xs text-blue-600">Planifiées</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-green-700">
                        {counts.ok}
                      </div>
                      <div className="text-xs text-green-600">Réalisées</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-yellow-700">
                        {counts.soon}
                      </div>
                      <div className="text-xs text-yellow-600">Expire bientôt</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-red-700">
                        {counts.expired}
                      </div>
                      <div className="text-xs text-red-600">Expirées</div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-gray-400">
                        {counts.superseded}
                      </div>
                      <div className="text-xs text-gray-400">Recyclées</div>
                    </div>
                  </div>
                )}

                {/* Filtres + bouton ajouter */}
                <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap gap-3 items-end">
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1.5">
                      Statut
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {FILTER_OPTS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() =>
                            setTrainingFilterStatus((prev) =>
                              prev.includes(opt.value)
                                ? prev.filter((s) => s !== opt.value)
                                : [...prev, opt.value],
                            )
                          }
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-all ${
                            trainingFilterStatus.includes(opt.value)
                              ? opt.cls + " border-transparent"
                              : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1.5">
                      Type
                    </div>
                    <select
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary"
                      value={trainingFilterType}
                      onChange={(e) => setTrainingFilterType(e.target.value)}
                    >
                      <option value="">Tous les types</option>
                      {trainingTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1.5">
                      Trier par
                    </div>
                    <select
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary"
                      value={trainingFilterSort}
                      onChange={(e) => setTrainingFilterSort(e.target.value)}
                    >
                      <option value="startDate">Date de formation</option>
                      <option value="recyclingDate">Date de recyclage</option>
                    </select>
                  </div>
                  <div className="ml-auto flex gap-2">
                    {trainings.length > 0 && (
                      <Btn
                        variant="secondary"
                        size="sm"
                        onClick={handleExportFormationsPdf}
                        loading={exportingFormationsPdf}
                      >
                        <FileText size={14} /> Exporter PDF
                      </Btn>
                    )}
                    <Btn
                      variant="primary"
                      size="sm"
                      onClick={openCreateTraining}
                    >
                      <Plus size={14} /> Ajouter une formation
                    </Btn>
                  </div>
                </div>

                {loadingTrainings ? (
                  <div className="flex justify-center py-8">
                    <Loader className="animate-spin text-primary" size={20} />
                  </div>
                ) : trainings.length === 0 ? (
                  <div className="text-center py-10 text-sm text-gray-400">
                    Aucune formation enregistrée.
                  </div>
                ) : displayed.length === 0 ? (
                  <div className="text-center py-10 text-sm text-gray-400">
                    Aucune formation pour ces filtres.
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                            Intitulé
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                            Type
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                            Période
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                            Recyclage
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                            Statut
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {displayed.map((t) => {
                          const statusCfg =
                            RECYCLING_STATUS_CONFIG[t.effectiveStatus] ||
                            RECYCLING_STATUS_CONFIG.none;
                          const isSuperseded =
                            t.effectiveStatus === "superseded";
                          return (
                            <tr
                              key={t.id}
                              className={`hover:bg-gray-50 ${isSuperseded ? "opacity-50" : ""}`}
                            >
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-800">
                                  {t.title}
                                </div>
                                {t.organism && (
                                  <div className="text-xs text-gray-400">
                                    {t.organism}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {t.trainingType?.name || (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {fmtDate(t.startDate)}
                                {t.endDate && <> → {fmtDate(t.endDate)}</>}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {t.recyclingDate ? (
                                  fmtDate(t.recyclingDate)
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <Badge color={statusCfg.cls}>
                                  {statusCfg.label}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex justify-end gap-1">
                                  {t.hasAttestation && (
                                    <Btn
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleViewAttestation(t)}
                                      title="Voir l'attestation"
                                      className="text-primary hover:text-primary-dark"
                                    >
                                      <FileText size={13} />
                                    </Btn>
                                  )}
                                  <Btn
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openRecyclingTraining(t)}
                                    title="Créer un recyclage"
                                    className="text-primary hover:text-primary-dark"
                                  >
                                    <RefreshCw size={13} />
                                  </Btn>
                                  <Btn
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setViewingTraining(t)}
                                    title="Visualiser"
                                  >
                                    <Eye size={13} />
                                  </Btn>
                                  <Btn
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditTraining(t)}
                                  >
                                    <Edit size={13} />
                                  </Btn>
                                  <Btn
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setTrainingDeleteConfirm(t)}
                                    className="text-red-500 hover:text-red-600"
                                  >
                                    <Trash2 size={13} />
                                  </Btn>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Modal formation */}
                {showTrainingModal && (
                  <Modal
                    title={
                      editingTraining
                        ? "Modifier la formation"
                        : "Ajouter une formation"
                    }
                    size="lg"
                    onClose={() => setShowTrainingModal(false)}
                    footer={
                      <>
                        <Btn
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowTrainingModal(false)}
                        >
                          Annuler
                        </Btn>
                        <Btn
                          variant="primary"
                          size="sm"
                          loading={savingTraining}
                          onClick={handleTrainingSave}
                        >
                          Enregistrer
                        </Btn>
                      </>
                    }
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField label="Type de formation">
                        <select
                          className={inputCls}
                          value={trainingForm.trainingTypeId}
                          onChange={(e) =>
                            handleTrainingTypeChange(e.target.value)
                          }
                        >
                          <option value="">— Sélectionner —</option>
                          {trainingTypes.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Intitulé" required>
                        <input
                          className={inputCls}
                          value={trainingForm.title}
                          onChange={(e) =>
                            setTrainingForm((f) => ({
                              ...f,
                              title: e.target.value,
                            }))
                          }
                          placeholder="Ex : CACES R489 Cat.3"
                        />
                      </FormField>
                      <FormField label="Nature">
                        <select
                          className={inputCls}
                          value={trainingForm.nature}
                          onChange={(e) =>
                            setTrainingForm((f) => ({
                              ...f,
                              nature: e.target.value,
                            }))
                          }
                        >
                          <option value="">— Sélectionner —</option>
                          <option value="Formation">Formation</option>
                          <option value="Échange de pratiques">Échange de pratiques</option>
                          <option value="Sensibilisation">Sensibilisation</option>
                        </select>
                      </FormField>
                      <FormField label="Durée (h)">
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          className={inputCls}
                          value={trainingForm.durationHours}
                          onChange={(e) =>
                            setTrainingForm((f) => ({
                              ...f,
                              durationHours: e.target.value,
                            }))
                          }
                          placeholder="Ex : 7"
                        />
                      </FormField>
                      <FormField label="Financement">
                        <input
                          className={inputCls}
                          value={trainingForm.financing}
                          onChange={(e) =>
                            setTrainingForm((f) => ({
                              ...f,
                              financing: e.target.value,
                            }))
                          }
                          placeholder="Ex : OPCO, Plan de formation, CPF"
                        />
                      </FormField>
                      <div className="sm:col-span-2 flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="training-mandatory"
                          className="rounded border-gray-300 cursor-pointer"
                          checked={trainingForm.mandatory}
                          onChange={(e) =>
                            setTrainingForm((f) => ({
                              ...f,
                              mandatory: e.target.checked,
                            }))
                          }
                        />
                        <label
                          htmlFor="training-mandatory"
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Formation obligatoire
                        </label>
                      </div>
                      <FormField label="Organisme">
                        <input
                          className={inputCls}
                          value={trainingForm.organism}
                          onChange={(e) =>
                            setTrainingForm((f) => ({
                              ...f,
                              organism: e.target.value,
                            }))
                          }
                          placeholder="Ex : AFPA Lyon"
                        />
                      </FormField>
                      <FormField label="Coût TTC (€)">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className={inputCls}
                          value={trainingForm.cost}
                          onChange={(e) =>
                            setTrainingForm((f) => ({
                              ...f,
                              cost: e.target.value,
                            }))
                          }
                          placeholder="0.00"
                        />
                      </FormField>
                      <FormField label="Date de début" required>
                        <input
                          type="date"
                          className={inputCls}
                          value={trainingForm.startDate}
                          onChange={(e) =>
                            setTrainingForm((f) => ({
                              ...f,
                              startDate: e.target.value,
                            }))
                          }
                        />
                      </FormField>
                      <FormField label="Date de fin">
                        <input
                          type="date"
                          className={inputCls}
                          value={trainingForm.endDate}
                          onChange={(e) =>
                            handleTrainingEndDateChange(e.target.value)
                          }
                        />
                      </FormField>
                      <FormField label="Date de recyclage">
                        <input
                          type="date"
                          className={inputCls}
                          value={trainingForm.recyclingDate}
                          onChange={(e) =>
                            setTrainingForm((f) => ({
                              ...f,
                              recyclingDate: e.target.value,
                            }))
                          }
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          Pré-rempli depuis le type et la date de fin.
                        </p>
                      </FormField>
                      <FormField label="Attestation (PDF / JPG / PNG, max 10 Mo)">
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className={inputCls}
                          onChange={(e) =>
                            setTrainingAttestation(e.target.files?.[0] || null)
                          }
                        />
                      </FormField>
                      <div className="sm:col-span-2">
                        <FormField label="Notes">
                          <textarea
                            className={inputCls}
                            rows={2}
                            value={trainingForm.notes}
                            onChange={(e) =>
                              setTrainingForm((f) => ({
                                ...f,
                                notes: e.target.value,
                              }))
                            }
                            placeholder="Remarques libres"
                          />
                        </FormField>
                      </div>
                      {!editingTraining &&
                        trainingForm.cost &&
                        parseFloat(trainingForm.cost) > 0 && (
                          <div className="sm:col-span-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={trainingForm.createHRCost}
                                onChange={(e) =>
                                  setTrainingForm((f) => ({
                                    ...f,
                                    createHRCost: e.target.checked,
                                  }))
                                }
                              />
                              <span className="text-sm text-gray-700">
                                Créer automatiquement une charge RH « Formation
                                » pour ce mois
                              </span>
                            </label>
                          </div>
                        )}
                    </div>
                  </Modal>
                )}

                {/* Modal visualisation formation */}
                {viewingTraining && (() => {
                  const vt = viewingTraining;
                  const sc = RECYCLING_STATUS_CONFIG[vt.effectiveStatus || vt.recyclingStatus] || RECYCLING_STATUS_CONFIG.none;
                  return (
                    <Modal
                      title="Détail de la formation"
                      size="md"
                      onClose={() => setViewingTraining(null)}
                      footer={
                        <div className="flex justify-between items-center w-full">
                          <div className="flex gap-2">
                            {vt.hasAttestation && (
                              <Btn
                                variant="secondary"
                                size="sm"
                                onClick={() => handleViewAttestation(vt)}
                              >
                                <FileText size={13} className="mr-1" />
                                Voir l'attestation
                              </Btn>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Btn
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setViewingTraining(null);
                                openEditTraining(vt);
                              }}
                            >
                              <Edit size={13} className="mr-1" />
                              Modifier
                            </Btn>
                            <Btn
                              variant="primary"
                              size="sm"
                              onClick={() => setViewingTraining(null)}
                            >
                              Fermer
                            </Btn>
                          </div>
                        </div>
                      }
                    >
                      <div className="space-y-4 text-sm">
                        {/* Ligne statut + obligatoire */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge color={sc.cls}>{sc.label}</Badge>
                          {vt.mandatory && (
                            <Badge color="bg-red-100 text-red-700">Obligatoire</Badge>
                          )}
                          {vt.nature && (
                            <Badge color="bg-blue-100 text-blue-700">{vt.nature}</Badge>
                          )}
                        </div>

                        {/* Intitulé + type */}
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Intitulé</p>
                          <p className="font-semibold text-gray-800">{vt.title}</p>
                          {vt.trainingType?.name && (
                            <p className="text-xs text-gray-500 mt-0.5">Type : {vt.trainingType.name}</p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Organisme</p>
                            <p className="text-gray-700">{vt.organism || <span className="text-gray-300">—</span>}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Durée</p>
                            <p className="text-gray-700">{vt.durationHours != null ? `${vt.durationHours} h` : <span className="text-gray-300">—</span>}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Date de réalisation</p>
                            <p className="text-gray-700">
                              {fmtDate(vt.startDate)}
                              {vt.endDate && <> → {fmtDate(vt.endDate)}</>}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Prochaine échéance</p>
                            <p className="text-gray-700">{vt.recyclingDate ? fmtDate(vt.recyclingDate) : <span className="text-gray-300">—</span>}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Coût TTC</p>
                            <p className="text-gray-700">{vt.cost != null ? fmtCurrency(vt.cost) : <span className="text-gray-300">—</span>}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Financement</p>
                            <p className="text-gray-700">{vt.financing || <span className="text-gray-300">—</span>}</p>
                          </div>
                        </div>

                        {vt.notes && (
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Notes</p>
                            <p className="text-gray-600 whitespace-pre-wrap">{vt.notes}</p>
                          </div>
                        )}
                      </div>
                    </Modal>
                  );
                })()}

                {/* Modal suppression */}
                {trainingDeleteConfirm && (
                  <Modal
                    title="Supprimer la formation"
                    size="sm"
                    onClose={() => setTrainingDeleteConfirm(null)}
                    footer={
                      <>
                        <Btn
                          variant="secondary"
                          size="sm"
                          onClick={() => setTrainingDeleteConfirm(null)}
                        >
                          Annuler
                        </Btn>
                        <Btn
                          variant="danger"
                          size="sm"
                          onClick={() =>
                            handleTrainingDelete(trainingDeleteConfirm)
                          }
                        >
                          Supprimer
                        </Btn>
                      </>
                    }
                  >
                    <p className="text-sm text-gray-700">
                      Supprimer la formation{" "}
                      <strong>{trainingDeleteConfirm.title}</strong> ?
                      L'attestation sera supprimée. La charge RH liée sera
                      conservée.
                    </p>
                  </Modal>
                )}
              </div>
            );
          })()}

        {activeTab === "entretiens" &&
          (() => {
            const STATUS_CONFIG = {
              planned: { label: "Planifié", cls: "bg-blue-100 text-blue-700" },
              in_progress: {
                label: "En cours",
                cls: "bg-yellow-100 text-yellow-700",
              },
              completed: {
                label: "Terminé",
                cls: "bg-green-100 text-green-700",
              },
            };
            const questions = activeInterview?.templateSnapshot || [];

            return (
              <div className="space-y-4">
                {/* Header actions */}
                <div className="flex items-center justify-between">
                  {activeInterview ? (
                    <button
                      onClick={() => setActiveInterview(null)}
                      className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                    >
                      <ArrowLeft size={16} /> Retour à la liste
                    </button>
                  ) : (
                    <span />
                  )}
                  {!activeInterview && (
                    <button
                      onClick={() => setShowPlanModal(true)}
                      className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary-dark"
                    >
                      <Plus size={16} /> Planifier un entretien
                    </button>
                  )}
                </div>

                {/* Vue liste */}
                {!activeInterview &&
                  (loadingInterviews ? (
                    <div className="flex justify-center py-8">
                      <Loader className="animate-spin text-primary" size={20} />
                    </div>
                  ) : interviews.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-10">
                      Aucun entretien. Cliquez sur "Planifier un entretien" pour
                      commencer.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {interviews.map((itv) => {
                        const cfg =
                          STATUS_CONFIG[itv.status] || STATUS_CONFIG.planned;
                        return (
                          <div
                            key={itv.id}
                            className="bg-white border rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-gray-800">
                                    {itv.type?.name || (
                                      <span className="italic text-gray-400">
                                        Type non défini
                                      </span>
                                    )}
                                  </span>
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}
                                  >
                                    {cfg.label}
                                  </span>
                                  {itv.template && (
                                    <span className="text-xs text-gray-400">
                                      · {itv.template.name}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(
                                    itv.scheduledDate,
                                  ).toLocaleDateString("fr-FR")}{" "}
                                  · {itv.interviewerName}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 ml-2">
                                {itv.status !== "completed" && (
                                  <button
                                    onClick={() => openInterview(itv)}
                                    className="p-1.5 text-gray-400 hover:text-primary"
                                    title="Conduire l'entretien"
                                  >
                                    <Edit size={14} />
                                  </button>
                                )}
                                {itv.status === "completed" && (
                                  <button
                                    onClick={() => openInterview(itv)}
                                    className="p-1.5 text-gray-400 hover:text-primary"
                                    title="Consulter"
                                  >
                                    <Eye size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={() => setInterviewDeleteConfirm(itv)}
                                  className="p-1.5 text-gray-400 hover:text-red-500"
                                  title="Supprimer"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}

                {/* Vue conduite d'entretien */}
                {activeInterview && (
                  <div className="bg-white border rounded-xl p-6 space-y-6">
                    {/* En-tête */}
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-800 text-base">
                          {activeInterview.type?.name || "Entretien"}
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {new Date(
                            activeInterview.completedAt ??
                              activeInterview.scheduledDate,
                          ).toLocaleDateString("fr-FR")}{" "}
                          · {activeInterview.interviewerName}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const cfg =
                            STATUS_CONFIG[activeInterview.status] ||
                            STATUS_CONFIG.planned;
                          return (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}
                            >
                              {cfg.label}
                            </span>
                          );
                        })()}
                        {activeInterview.status === "planned" && (
                          <button
                            onClick={() =>
                              handleInterviewStatus(
                                activeInterview,
                                "in_progress",
                              )
                            }
                            className="text-xs px-3 py-1 bg-primary text-white rounded-lg hover:bg-primary-dark"
                          >
                            Démarrer
                          </button>
                        )}
                        {activeInterview.status === "in_progress" && (
                          <button
                            onClick={() =>
                              handleInterviewStatus(
                                activeInterview,
                                "completed",
                              )
                            }
                            className="flex items-center gap-1 text-xs px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <CheckCircle size={13} /> Clôturer
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Formulaire questions */}
                    {questions.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">
                        Cet entretien n'a pas de template associé — utilisez les
                        notes ci-dessous.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {questions
                          .filter((q) => q.label?.trim())
                          .map((q) =>
                            q.type === "section" ? (
                              <div
                                key={q.id}
                                className="border-b-2 border-primary/20 pb-1 pt-2"
                              >
                                <h4 className="text-sm font-bold text-primary uppercase tracking-wider">
                                  {q.label}
                                </h4>
                                {q.description?.trim() && (
                                  <p className="text-xs text-gray-500 italic mt-0.5">
                                    {q.description}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div key={q.id} className="space-y-1">
                                <label className="block text-sm font-medium text-gray-700">
                                  {q.label}{" "}
                                  {q.required && (
                                    <span className="text-red-400">*</span>
                                  )}
                                </label>
                                {q.description?.trim() && (
                                  <p className="text-xs text-gray-400 italic">
                                    {q.description}
                                  </p>
                                )}
                                {q.type === "rating" ? (
                                  <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                      <button
                                        key={n}
                                        type="button"
                                        onClick={() =>
                                          activeInterview.status !==
                                            "completed" &&
                                          setAnswerValue(q.id, n)
                                        }
                                        disabled={
                                          activeInterview.status === "completed"
                                        }
                                        className="focus:outline-none disabled:cursor-default"
                                      >
                                        <Star
                                          size={22}
                                          className={
                                            Number(getAnswerValue(q.id)) >= n
                                              ? "text-accent fill-accent"
                                              : "text-gray-300"
                                          }
                                        />
                                      </button>
                                    ))}
                                  </div>
                                ) : q.type === "radio" ? (
                                  <div className="space-y-1">
                                    {(q.options || []).map((opt) => (
                                      <label
                                        key={opt}
                                        className="flex items-center gap-2 text-sm cursor-pointer"
                                      >
                                        <input
                                          type="radio"
                                          name={q.id}
                                          value={opt}
                                          checked={getAnswerValue(q.id) === opt}
                                          onChange={() =>
                                            setAnswerValue(q.id, opt)
                                          }
                                          disabled={
                                            activeInterview.status ===
                                            "completed"
                                          }
                                        />
                                        {opt}
                                      </label>
                                    ))}
                                  </div>
                                ) : q.type === "checkbox" ? (
                                  <div className="space-y-1">
                                    {(q.options || []).map((opt) => {
                                      const checked =
                                        Array.isArray(getAnswerValue(q.id)) &&
                                        getAnswerValue(q.id).includes(opt);
                                      return (
                                        <label
                                          key={opt}
                                          className="flex items-center gap-2 text-sm cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={
                                              activeInterview.status ===
                                              "completed"
                                            }
                                            onChange={() => {
                                              const cur = Array.isArray(
                                                getAnswerValue(q.id),
                                              )
                                                ? getAnswerValue(q.id)
                                                : [];
                                              setAnswerValue(
                                                q.id,
                                                checked
                                                  ? cur.filter((v) => v !== opt)
                                                  : [...cur, opt],
                                              );
                                            }}
                                          />
                                          {opt}
                                        </label>
                                      );
                                    })}
                                  </div>
                                ) : q.type === "textarea" ? (
                                  <textarea
                                    className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                                    rows={3}
                                    value={getAnswerValue(q.id)}
                                    onChange={(e) =>
                                      setAnswerValue(q.id, e.target.value)
                                    }
                                    disabled={
                                      activeInterview.status === "completed"
                                    }
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    className="w-full border rounded-lg px-3 py-2 text-sm"
                                    value={getAnswerValue(q.id)}
                                    onChange={(e) =>
                                      setAnswerValue(q.id, e.target.value)
                                    }
                                    disabled={
                                      activeInterview.status === "completed"
                                    }
                                  />
                                )}
                              </div>
                            ),
                          )}
                      </div>
                    )}

                    {/* Notes libres */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Notes
                      </label>
                      <textarea
                        className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                        rows={3}
                        value={interviewNotes}
                        onChange={(e) => setInterviewNotes(e.target.value)}
                        disabled={activeInterview.status === "completed"}
                        placeholder="Observations libres de l'intervieweur..."
                      />
                    </div>

                    {/* Actions bas */}
                    {activeInterview.status !== "completed" && (
                      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                        <button
                          onClick={handleSaveAnswers}
                          disabled={savingAnswers}
                          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary-dark disabled:opacity-50"
                        >
                          {savingAnswers ? (
                            <Loader size={14} className="animate-spin" />
                          ) : (
                            <Download size={14} />
                          )}
                          Enregistrer
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Modal planification */}
                {showPlanModal && (
                  <Modal
                    isOpen
                    onClose={() => setShowPlanModal(false)}
                    title="Planifier un entretien"
                    size="md"
                    footer={
                      <>
                        <button
                          onClick={() => setShowPlanModal(false)}
                          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handlePlanInterview}
                          disabled={savingPlan}
                          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary-dark disabled:opacity-50"
                        >
                          {savingPlan && (
                            <Loader size={13} className="animate-spin" />
                          )}{" "}
                          Planifier
                        </button>
                      </>
                    }
                  >
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Type d'entretien
                        </label>
                        <select
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                          value={planForm.typeId}
                          onChange={(e) =>
                            setPlanForm((f) => ({
                              ...f,
                              typeId: e.target.value,
                            }))
                          }
                        >
                          <option value="">— Sélectionner —</option>
                          {interviewTypes.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Template de questions
                        </label>
                        <select
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                          value={planForm.templateId}
                          onChange={(e) =>
                            setPlanForm((f) => ({
                              ...f,
                              templateId: e.target.value,
                            }))
                          }
                        >
                          <option value="">— Sans template —</option>
                          {interviewTemplates.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                              {t.isDefault ? " (par défaut)" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Intervieweur *
                        </label>
                        <input
                          type="text"
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                          value={planForm.interviewerName}
                          onChange={(e) =>
                            setPlanForm((f) => ({
                              ...f,
                              interviewerName: e.target.value,
                            }))
                          }
                          placeholder="Nom de l'intervieweur"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date prévue *
                        </label>
                        <input
                          type="date"
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                          value={planForm.scheduledDate}
                          onChange={(e) =>
                            setPlanForm((f) => ({
                              ...f,
                              scheduledDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </Modal>
                )}

                {/* Confirm suppression */}
                {interviewDeleteConfirm && (
                  <Modal
                    isOpen
                    onClose={() => setInterviewDeleteConfirm(null)}
                    title="Supprimer l'entretien"
                    size="sm"
                    footer={
                      <>
                        <button
                          onClick={() => setInterviewDeleteConfirm(null)}
                          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handleDeleteInterview}
                          className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          Supprimer
                        </button>
                      </>
                    }
                  >
                    <p className="text-sm text-gray-700">
                      Supprimer cet entretien ? Cette action est irréversible.
                    </p>
                  </Modal>
                )}
              </div>
            );
          })()}

        {activeTab === "visites-medicales" &&
          (() => {
            const VISIT_STATUS_CFG = {
              depasse: { label: "Dépassé", cls: "bg-red-100 text-red-700" },
              bientot: {
                label: "Expire bientôt",
                cls: "bg-orange-100 text-orange-700",
              },
              attente_cr: {
                label: "Attente CR",
                cls: "bg-yellow-100 text-yellow-700",
              },
              realise: { label: "Réalisé", cls: "bg-green-100 text-green-700" },
              convoque: { label: "Convoqué", cls: "bg-blue-100 text-blue-700" },
            };

            // Compteurs basés sur la dernière visite par type
            const latestByTypeMap = {};
            for (const v of medicalVisits) {
              if (
                !latestByTypeMap[v.typeId] ||
                new Date(v.visitDate) >
                  new Date(latestByTypeMap[v.typeId].visitDate)
              ) {
                latestByTypeMap[v.typeId] = v;
              }
            }
            const latestVisits = Object.values(latestByTypeMap);
            const cntAJour = latestVisits.filter(
              (v) => v.status === "realise" || v.status === "convoque",
            ).length;
            const cntEnAttente = latestVisits.filter(
              (v) => v.status === "attente_cr",
            ).length;
            const cntDepassees = latestVisits.filter(
              (v) => v.status === "depasse" || v.status === "bientot",
            ).length;

            // Options type incluant le type inactif de la visite en cours d'édition
            const typeOptions = [...medicalVisitTypes];
            if (
              editingVisit?.type &&
              !editingVisit.type.isActive &&
              !typeOptions.find((t) => t.id === editingVisit.typeId)
            ) {
              typeOptions.push({ ...editingVisit.type, _inactive: true });
            }

            return (
              <div className="space-y-4">
                {/* En-tête avec compteurs */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Visites médicales
                    </h3>
                    {!loadingMedicalVisits && medicalVisits.length > 0 && (
                      <>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          {cntAJour} à jour
                        </span>
                        {cntEnAttente > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                            {cntEnAttente} attente CR
                          </span>
                        )}
                        {cntDepassees > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            {cntDepassees} dépassée{cntDepassees > 1 ? "s" : ""}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setVisitSortDir(d => -d)}
                      title="Changer l'ordre de tri"
                      className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Date {visitSortDir === -1 ? '↓' : '↑'}
                    </button>
                    <button
                      onClick={() => openVisitModal()}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      <Plus size={14} />
                      Ajouter
                    </button>
                  </div>
                </div>

                {loadingMedicalVisits ? (
                  <div className="flex justify-center py-8">
                    <Loader className="animate-spin text-primary" size={20} />
                  </div>
                ) : medicalVisits.length === 0 ? (
                  <div className="text-center py-10 text-sm text-gray-400">
                    <p>Aucune visite médicale enregistrée.</p>
                    <p className="mt-1 text-xs">
                      La visite d'embauche (VIP) est obligatoire dans les 3 mois
                      suivant la prise de poste.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[...medicalVisits].sort((a, b) => visitSortDir * (new Date(b.visitDate) - new Date(a.visitDate))).map((v) => {
                      const cfg = VISIT_STATUS_CFG[v.status] || {
                        label: v.status,
                        cls: "bg-gray-100 text-gray-600",
                      };
                      return (
                        <div
                          key={v.id}
                          className="bg-white rounded-xl border border-gray-200 p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-sm font-semibold text-gray-800">
                                  {v.type?.name || "—"}
                                </span>
                                {!v.type?.isActive && (
                                  <span className="text-xs text-gray-400">
                                    (désactivé)
                                  </span>
                                )}
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}
                                >
                                  {cfg.label}
                                </span>
                                {v.aptitude && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                    {APTITUDE_LABELS[v.aptitude] || v.aptitude}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                {v.convocationDate && (
                                  <span>
                                    Convocation :{" "}
                                    {new Date(
                                      v.convocationDate,
                                    ).toLocaleDateString("fr-FR")}
                                  </span>
                                )}
                                <span>
                                  Visite :{" "}
                                  <strong className="text-gray-700">
                                    {new Date(v.visitDate).toLocaleDateString("fr-FR")}
                                    {v.visitTime && ` à ${v.visitTime}`}
                                  </strong>
                                </span>
                                {v.nextVisitDate && (
                                  <span>
                                    Prochaine :{" "}
                                    <strong className="text-gray-700">
                                      {new Date(
                                        v.nextVisitDate,
                                      ).toLocaleDateString("fr-FR")}
                                    </strong>
                                  </span>
                                )}
                              </div>
                              {(v.location || v.doctor) && (
                                <span className="flex items-center gap-1">
                                  {v.location && <span>{v.location}</span>}
                                  {v.location && v.doctor && <span className="text-gray-300">·</span>}
                                  {v.doctor && <span className="italic">{v.doctor}</span>}
                                </span>
                              )}
                              {v.notes && (
                                <p className="text-xs text-gray-500 mt-1 italic">
                                  {v.notes}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => openVisitModal(v)}
                                className="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-gray-50 transition-colors"
                                title="Modifier"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                onClick={() => setVisitDeleteConfirm(v)}
                                className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-50 transition-colors"
                                title="Supprimer"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>

                          {/* CR */}
                          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3 flex-wrap">
                            {v.hasCr ? (
                              <>
                                <span className="text-xs text-green-700 flex items-center gap-1">
                                  <CheckCircle size={12} /> CR disponible
                                  {v.crFileName && (
                                    <span className="text-gray-400">
                                      ({v.crFileName})
                                    </span>
                                  )}
                                </span>
                                <button
                                  onClick={() =>
                                    setViewingCr({
                                      fileName: v.crFileName || "cr_visite.pdf",
                                      fetchBlob: () =>
                                        getMedicalVisitCrFile(
                                          employee.id,
                                          v.id,
                                        ),
                                    })
                                  }
                                  className="text-xs text-primary hover:underline flex items-center gap-1"
                                >
                                  <Eye size={12} /> Visualiser
                                </button>
                                <button
                                  onClick={() => setCrDeleteConfirm(v)}
                                  className="text-xs text-red-500 hover:underline flex items-center gap-1"
                                >
                                  <Trash2 size={12} /> Supprimer CR
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="text-xs text-gray-400">
                                  Aucun CR
                                </span>
                                <label className="cursor-pointer text-xs text-primary hover:underline flex items-center gap-1">
                                  {uploadingCr === v.id ? (
                                    <Loader
                                      size={12}
                                      className="animate-spin"
                                    />
                                  ) : (
                                    <Upload size={12} />
                                  )}
                                  Uploader CR (PDF)
                                  <input
                                    type="file"
                                    accept="application/pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                      if (e.target.files[0])
                                        handleUploadCr(v, e.target.files[0]);
                                      e.target.value = "";
                                    }}
                                  />
                                </label>
                              </>
                            )}
                          </div>

                          {/* Relance email */}
                          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-3 flex-wrap">
                            <button
                              onClick={() => handleSendRelance(v)}
                              disabled={sendingRelance === v.id}
                              className="text-xs text-primary hover:underline flex items-center gap-1 disabled:opacity-50"
                            >
                              {sendingRelance === v.id
                                ? <Loader size={12} className="animate-spin" />
                                : <Mail size={12} />}
                              Envoyer relance
                            </button>
                            {v.relanceSentAt && (
                              <span className="text-xs text-gray-400">
                                Relance envoyée le{" "}
                                {new Date(v.relanceSentAt).toLocaleDateString("fr-FR")}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Modale ajout/édition */}
                {showVisitModal && (
                  <Modal
                    title={
                      editingVisit
                        ? "Modifier la visite"
                        : "Nouvelle visite médicale"
                    }
                    onClose={() => setShowVisitModal(false)}
                    size="md"
                    footer={
                      <>
                        <button
                          onClick={() => setShowVisitModal(false)}
                          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handleSaveVisit}
                          disabled={savingVisit}
                          className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center gap-2"
                        >
                          {savingVisit && (
                            <Loader size={14} className="animate-spin" />
                          )}
                          Enregistrer
                        </button>
                      </>
                    }
                  >
                    <div className="space-y-4">
                      <FormField label="Type de visite" required>
                        <select
                          className={inputCls}
                          value={visitForm.typeId}
                          onChange={(e) =>
                            handleVisitTypeChange(e.target.value)
                          }
                        >
                          <option value="">— Sélectionner —</option>
                          {typeOptions.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                              {t._inactive ? " (désactivé)" : ""}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Date de convocation">
                        <input
                          type="date"
                          className={inputCls}
                          value={visitForm.convocationDate}
                          onChange={(e) =>
                            setVisitForm({
                              ...visitForm,
                              convocationDate: e.target.value,
                            })
                          }
                        />
                      </FormField>
                      <FormField label="Date de visite" required>
                        <input
                          type="date"
                          className={inputCls}
                          value={visitForm.visitDate}
                          onChange={(e) =>
                            handleVisitDateChange(e.target.value)
                          }
                        />
                      </FormField>
                      <FormField label="Heure de la visite">
                        <input
                          type="time"
                          className={inputCls}
                          value={visitForm.visitTime}
                          onChange={(e) =>
                            setVisitForm({ ...visitForm, visitTime: e.target.value })
                          }
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          Optionnel — si renseigné, un créneau d'1h est créé dans le calendrier.
                        </p>
                      </FormField>
                      <FormField label="Lieu de la visite">
                        <input
                          className={inputCls}
                          value={visitForm.location}
                          onChange={(e) =>
                            setVisitForm({ ...visitForm, location: e.target.value })
                          }
                          placeholder="Ex : CIAMT — 17 rue de la Paix, Lyon"
                        />
                      </FormField>
                      <FormField label="Médecin / Organisme">
                        <input
                          className={inputCls}
                          value={visitForm.doctor}
                          onChange={(e) =>
                            setVisitForm({ ...visitForm, doctor: e.target.value })
                          }
                          placeholder="Ex : Dr Dupont — CIAMT Lyon"
                        />
                      </FormField>
                      {visitForm.visitDate &&
                        new Date(visitForm.visitDate) <= new Date() && (
                        <FormField label="Aptitude">
                          <select
                            className={inputCls}
                            value={visitForm.aptitude}
                            onChange={(e) =>
                              setVisitForm({
                                ...visitForm,
                                aptitude: e.target.value,
                              })
                            }
                          >
                            <option value="">— Non renseigné —</option>
                            {Object.entries(APTITUDE_LABELS).map(([val, lbl]) => (
                              <option key={val} value={val}>
                                {lbl}
                              </option>
                            ))}
                          </select>
                        </FormField>
                      )}
                      <FormField label="Date prochaine visite">
                        <input
                          type="date"
                          className={inputCls}
                          value={visitForm.nextVisitDate}
                          onChange={(e) =>
                            setVisitForm({
                              ...visitForm,
                              nextVisitDate: e.target.value,
                            })
                          }
                        />
                      </FormField>
                      <FormField label="Notes">
                        <textarea
                          className={inputCls}
                          rows={3}
                          value={visitForm.notes}
                          onChange={(e) =>
                            setVisitForm({
                              ...visitForm,
                              notes: e.target.value,
                            })
                          }
                        />
                      </FormField>
                    </div>
                  </Modal>
                )}

                {/* Confirmation suppression visite */}
                {visitDeleteConfirm && (
                  <Modal
                    title="Supprimer la visite"
                    onClose={() => setVisitDeleteConfirm(null)}
                    size="sm"
                    footer={
                      <>
                        <button
                          onClick={() => setVisitDeleteConfirm(null)}
                          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handleDeleteVisit}
                          className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          Supprimer
                        </button>
                      </>
                    }
                  >
                    <p className="text-sm text-gray-700">
                      Supprimer cette visite médicale ? Cette action est
                      irréversible.
                    </p>
                  </Modal>
                )}

                {/* Confirmation suppression CR */}
                {crDeleteConfirm && (
                  <Modal
                    title="Supprimer le compte-rendu"
                    onClose={() => setCrDeleteConfirm(null)}
                    size="sm"
                    footer={
                      <>
                        <button
                          onClick={() => setCrDeleteConfirm(null)}
                          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handleConfirmDeleteCr}
                          className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          Supprimer
                        </button>
                      </>
                    }
                  >
                    <p className="text-sm text-gray-700">
                      Supprimer le CR de cette visite ? Le fichier PDF sera
                      définitivement effacé.
                    </p>
                  </Modal>
                )}

                {/* Visualiseur CR */}
                {viewingCr && (
                  <HRDocViewer
                    fileName={viewingCr.fileName}
                    fetchBlob={viewingCr.fetchBlob}
                    onClose={() => setViewingCr(null)}
                  />
                )}

                {/* Confirmation recalcul date lors d'un changement de type */}
                {pendingTypeChange && (
                  <Modal
                    title="Recalculer la date de prochaine visite ?"
                    onClose={() => setPendingTypeChange(null)}
                    size="sm"
                    footer={
                      <>
                        <button
                          onClick={() => {
                            applyVisitTypeChange(
                              pendingTypeChange.typeId,
                              false,
                            );
                            setPendingTypeChange(null);
                          }}
                          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Garder la date actuelle
                        </button>
                        <button
                          onClick={() => {
                            applyVisitTypeChange(
                              pendingTypeChange.typeId,
                              true,
                            );
                            setPendingTypeChange(null);
                          }}
                          className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark"
                        >
                          Recalculer
                        </button>
                      </>
                    }
                  >
                    <p className="text-sm text-gray-700">
                      Le type <strong>{pendingTypeChange.typeName}</strong>{" "}
                      possède une périodicité par défaut. Voulez-vous recalculer
                      la date de prochaine visite à partir de la date de visite
                      actuelle ?
                    </p>
                  </Modal>
                )}
              </div>
            );
          })()}

        {activeTab === "periode-essai" && (() => {
          const active = trialPeriods.find(t => t.status === "active");
          const history = trialPeriods.filter(t => t.status !== "active");
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-gray-700">Période d'essai</h3>
                {!active && (
                  <Btn variant="primary" size="sm" onClick={() => openTrialModal()}>
                    <Plus size={14} /> Créer
                  </Btn>
                )}
              </div>

              {loadingTrial ? (
                <div className="flex justify-center py-8"><Loader className="animate-spin text-primary" size={20} /></div>
              ) : !active && history.length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-400">
                  <p>Aucune période d'essai enregistrée.</p>
                </div>
              ) : active ? (
                <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                  {/* En-tête statut */}
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Timer size={16} className="text-gray-500" />
                      <span className="font-medium text-gray-800">
                        {TRIAL_CATEGORY_LABELS[active.category] || active.category}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TRIAL_STATUS_CFG[active.computedStatus]?.cls || "bg-gray-100 text-gray-600"}`}>
                        {TRIAL_STATUS_CFG[active.computedStatus]?.label || active.computedStatus}
                      </span>
                    </div>
                    <Btn variant="ghost" size="sm" onClick={() => openTrialModal(active)}>
                      <Edit size={13} /> Modifier
                    </Btn>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Début</p>
                      <p className="font-medium text-gray-800">{fmtDate(active.startDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Fin prévue</p>
                      <p className="font-medium text-gray-800">{fmtDate(active.endDate)}</p>
                    </div>
                    {active.renewedEndDate && (
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Fin après renouvellement</p>
                        <p className="font-medium text-orange-600">{fmtDate(active.renewedEndDate)}</p>
                      </div>
                    )}
                  </div>

                  {active.notes && (
                    <p className="text-xs text-gray-500 italic">{active.notes}</p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t border-gray-100 flex-wrap">
                    <Btn
                      variant="primary"
                      size="sm"
                      loading={actioning === active.id}
                      onClick={() => handleConfirmTrial(active)}
                    >
                      <ThumbsUp size={13} /> Confirmer l'embauche
                    </Btn>
                    <Btn
                      variant="danger"
                      size="sm"
                      onClick={() => { setShowRuptureModal(active); setRuptureMotif(""); }}
                    >
                      <ThumbsDown size={13} /> Rompre la PE
                    </Btn>
                    <Btn
                      variant="ghost"
                      size="sm"
                      onClick={() => setTrialDeleteConfirm(active)}
                      className="text-red-400 hover:text-red-600 ml-auto"
                    >
                      <Trash2 size={13} />
                    </Btn>
                  </div>
                </div>
              ) : null}

              {/* Historique */}
              {history.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Historique</p>
                  {history.map(tp => {
                    const cfg = TRIAL_STATUS_CFG[tp.computedStatus] || { label: tp.computedStatus, cls: "bg-gray-100 text-gray-600" };
                    return (
                      <div key={tp.id} className="bg-gray-50 rounded-lg border border-gray-100 p-3 flex items-center gap-3 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>
                        <span className="text-gray-600">{TRIAL_CATEGORY_LABELS[tp.category] || tp.category}</span>
                        <span className="text-gray-400">{fmtDate(tp.startDate)} → {fmtDate(tp.endDate)}</span>
                        {tp.decisionDate && (
                          <span className="text-gray-400 text-xs ml-auto">
                            {tp.status === "confirmee" ? "Confirmée" : "Rompue"} le {fmtDate(tp.decisionDate)}
                            {tp.decisionBy && ` par ${tp.decisionBy}`}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Modale création / édition */}
              {showTrialModal && (
                <Modal
                  title={editingTrial ? "Modifier la période d'essai" : "Nouvelle période d'essai"}
                  size="sm"
                  onClose={() => setShowTrialModal(false)}
                  footer={
                    <>
                      <Btn variant="secondary" size="sm" onClick={() => setShowTrialModal(false)}>Annuler</Btn>
                      <Btn variant="primary" size="sm" loading={savingTrial} onClick={handleSaveTrial}>Enregistrer</Btn>
                    </>
                  }
                >
                  <div className="space-y-4">
                    <FormField label="Catégorie" required>
                      <select className={inputCls} value={trialForm.category} onChange={e => handleTrialCategoryChange(e.target.value)}>
                        {Object.entries(TRIAL_CATEGORY_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Date de début" required>
                      <input type="date" className={inputCls} value={trialForm.startDate}
                        onChange={e => setTrialForm(f => ({ ...f, startDate: e.target.value }))} />
                    </FormField>
                    <FormField label="Date de fin" required>
                      <input type="date" className={inputCls} value={trialForm.endDate}
                        onChange={e => setTrialForm(f => ({ ...f, endDate: e.target.value }))} />
                    </FormField>
                    <FormField label="Date de fin après renouvellement">
                      <input type="date" className={inputCls} value={trialForm.renewedEndDate}
                        onChange={e => setTrialForm(f => ({ ...f, renewedEndDate: e.target.value }))} />
                      <p className="text-xs text-gray-400 mt-1">Optionnel — uniquement si renouvellement accordé.</p>
                    </FormField>
                    <FormField label="Notes">
                      <textarea className={inputCls} rows={2} value={trialForm.notes}
                        onChange={e => setTrialForm(f => ({ ...f, notes: e.target.value }))} />
                    </FormField>
                  </div>
                </Modal>
              )}

              {/* Modale rupture */}
              {showRuptureModal && (
                <Modal
                  title="Rompre la période d'essai"
                  size="sm"
                  onClose={() => setShowRuptureModal(null)}
                  footer={
                    <>
                      <Btn variant="secondary" size="sm" onClick={() => setShowRuptureModal(null)}>Annuler</Btn>
                      <Btn variant="danger" size="sm" loading={actioning === showRuptureModal?.id} onClick={handleRuptureTrial}>Confirmer la rupture</Btn>
                    </>
                  }
                >
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">La période d'essai sera clôturée avec le statut <strong>Rompue</strong>.</p>
                    <FormField label="Motif (optionnel)">
                      <input className={inputCls} value={ruptureMotif}
                        onChange={e => setRuptureMotif(e.target.value)}
                        placeholder="Ex : Incompatibilité, absence répétée..." />
                    </FormField>
                  </div>
                </Modal>
              )}

              {/* Modale suppression */}
              {trialDeleteConfirm && (
                <Modal
                  title="Supprimer la période d'essai"
                  size="sm"
                  onClose={() => setTrialDeleteConfirm(null)}
                  footer={
                    <>
                      <Btn variant="secondary" size="sm" onClick={() => setTrialDeleteConfirm(null)}>Annuler</Btn>
                      <Btn variant="danger" size="sm" onClick={handleDeleteTrial}>Supprimer</Btn>
                    </>
                  }
                >
                  <p className="text-sm text-gray-700">Supprimer cette période d'essai ? Cette action est irréversible.</p>
                </Modal>
              )}
            </div>
          );
        })()}

        {activeTab === "doc-requests" && (
          <DocumentRequestsTab employee={employee} />
        )}

        {activeTab === "counters" && (
          <div className="max-w-3xl space-y-6">
            {loadingCounters ? (
              <div className="flex justify-center py-6">
                <Loader className="animate-spin text-primary" size={20} />
              </div>
            ) : (
              <>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">
                      Compteur RCR
                    </h4>
                    <span
                      className={`text-lg font-bold ${Number(rcrData.balance) > 0 ? "text-green-600" : Number(rcrData.balance) < 0 ? "text-red-600" : "text-gray-400"}`}
                    >
                      {Number(rcrData.balance || 0).toFixed(2)}h
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.25"
                      placeholder="Heures (+/-)"
                      value={adjustForm.type === "rcr" ? adjustForm.amount : ""}
                      onChange={(e) =>
                        setAdjustForm({
                          type: "rcr",
                          amount: e.target.value,
                          comment:
                            adjustForm.type === "rcr" ? adjustForm.comment : "",
                        })
                      }
                      className="w-28 border rounded-lg px-2 py-1.5 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Commentaire"
                      value={
                        adjustForm.type === "rcr" ? adjustForm.comment : ""
                      }
                      onChange={(e) =>
                        setAdjustForm((f) => ({
                          ...f,
                          type: "rcr",
                          comment: e.target.value,
                        }))
                      }
                      className="flex-1 border rounded-lg px-2 py-1.5 text-sm"
                    />
                    <button
                      onClick={() => handleAdjustCounter("rcr")}
                      disabled={!adjustForm.amount || adjustForm.type !== "rcr"}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Ajuster
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">
                      Heures à rattraper (dette)
                    </h4>
                    <span
                      className={`text-lg font-bold ${Number(debtData.balance) > 0 ? "text-amber-600" : "text-gray-400"}`}
                    >
                      {Number(debtData.balance || 0).toFixed(2)}h
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.25"
                      placeholder="Heures (+/-)"
                      value={
                        adjustForm.type === "debt" ? adjustForm.amount : ""
                      }
                      onChange={(e) =>
                        setAdjustForm({
                          type: "debt",
                          amount: e.target.value,
                          comment:
                            adjustForm.type === "debt"
                              ? adjustForm.comment
                              : "",
                        })
                      }
                      className="w-28 border rounded-lg px-2 py-1.5 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Commentaire"
                      value={
                        adjustForm.type === "debt" ? adjustForm.comment : ""
                      }
                      onChange={(e) =>
                        setAdjustForm((f) => ({
                          ...f,
                          type: "debt",
                          comment: e.target.value,
                        }))
                      }
                      className="flex-1 border rounded-lg px-2 py-1.5 text-sm"
                    />
                    <button
                      onClick={() => handleAdjustCounter("debt")}
                      disabled={
                        !adjustForm.amount || adjustForm.type !== "debt"
                      }
                      className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
                    >
                      Ajuster
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {viewingDoc && (
        <HRDocViewer
          fileName={viewingDoc.fileName}
          fetchBlob={viewingDoc.fetchBlob}
          onClose={() => setViewingDoc(null)}
        />
      )}

      {showCalendarModal && employee && (
        <Modal
          title="Exporter le planning"
          onClose={() => setShowCalendarModal(false)}
          size="sm"
          footer={
            <Btn
              variant="secondary"
              onClick={() => setShowCalendarModal(false)}
            >
              Fermer
            </Btn>
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Choisissez le format d'export pour le planning de{" "}
              <span className="font-medium">
                {employee.firstName} {employee.lastName}
              </span>
              .
            </p>

            {/* Boutons de choix */}
            <div className="grid grid-cols-2 gap-3">
              {/* iCal */}
              <button
                onClick={handleGenerateIcal}
                disabled={icalLoading || !!icalToken}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-60 disabled:cursor-default"
              >
                <Link2 size={22} className="text-primary" />
                <span className="text-sm font-medium text-primary">iCal</span>
                <span className="text-xs text-gray-500 text-center">
                  Lien de synchronisation
                </span>
              </button>

              {/* PDF */}
              <button
                onClick={() => setPdfSection((v) => !v)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors ${
                  pdfSection
                    ? "border-primary bg-primary/10"
                    : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <FileText
                  size={22}
                  className={pdfSection ? "text-primary" : "text-gray-500"}
                />
                <span
                  className={`text-sm font-medium ${pdfSection ? "text-primary" : "text-gray-600"}`}
                >
                  PDF
                </span>
                <span className="text-xs text-gray-500 text-center">
                  Export mensuel
                </span>
              </button>
            </div>

            {/* Zone lien iCal — visible après génération */}
            {icalToken && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                <p className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <Link2 size={12} className="text-primary" />
                  Lien iCal généré
                </p>

                {/* URL */}
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white border border-gray-200 rounded px-2 py-1.5 truncate text-gray-600">
                    {`${window.location.origin}/api/hr/employees/${employee.id}/ical?token=${icalToken}`}
                  </code>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <Btn
                    variant={icalCopied ? "primary" : "secondary"}
                    size="sm"
                    onClick={handleCopyIcal}
                  >
                    <Copy size={12} />
                    {icalCopied ? "Copié !" : "Copier le lien"}
                  </Btn>
                  <Btn
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const url = `webcal://${window.location.host}/api/hr/employees/${employee.id}/ical?token=${icalToken}`;
                      window.location.href = url;
                    }}
                  >
                    <ExternalLink size={12} />
                    Ouvrir dans le calendrier
                  </Btn>
                  <Btn
                    variant="ghost"
                    size="sm"
                    loading={icalLoading}
                    onClick={handleRevokeIcal}
                  >
                    <RefreshCcw size={12} />
                    Révoquer
                  </Btn>
                </div>

                <p className="text-xs text-gray-400">
                  Ce lien donne accès au planning des 3 derniers mois et des 12
                  prochains mois. Révoquer l'ancien lien en génère
                  automatiquement un nouveau.
                </p>
              </div>
            )}

            {/* Zone PDF — visible après clic sur le bouton PDF */}
            {pdfSection && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                <p className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                  <FileText size={12} className="text-gray-500" />
                  Planning (cycle complet)
                </p>
                <p className="text-xs text-gray-500">
                  2 pages : cycle théorique (semaines) + vue calendrier du mois
                  en cours.
                </p>
                <Btn
                  variant="primary"
                  size="sm"
                  loading={pdfLoading}
                  onClick={handleExportPdf}
                  className="w-full justify-center"
                >
                  <Download size={13} />
                  Télécharger le PDF
                </Btn>
              </div>
            )}
          </div>
        </Modal>
      )}

      {showEditModal && (
        <Modal
          title="Modifier l'employé"
          onClose={() => setShowEditModal(false)}
          size="xl"
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowEditModal(false)}>
                Annuler
              </Btn>
              <Btn
                variant="primary"
                onClick={handleSaveEdit}
                loading={savingEdit}
              >
                Enregistrer
              </Btn>
            </>
          }
        >
          <div className="space-y-6">
            {/* État civil */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                État civil
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Civilité">
                  <select
                    value={editForm.civilite || ""}
                    onChange={(e) => setEditField("civilite", e.target.value)}
                    className={inputCls}
                  >
                    <option value="">—</option>
                    <option value="M.">M.</option>
                    <option value="Mme">Mme</option>
                  </select>
                </FormField>
                <FormField label="Prénom" required>
                  <input
                    type="text"
                    value={editForm.firstName || ""}
                    onChange={(e) => setEditField("firstName", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Autres prénoms">
                  <input
                    type="text"
                    value={editForm.otherFirstNames || ""}
                    onChange={(e) =>
                      setEditField("otherFirstNames", e.target.value)
                    }
                    placeholder="Tous les prénoms"
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Nom" required>
                  <input
                    type="text"
                    value={editForm.lastName || ""}
                    onChange={(e) => setEditField("lastName", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Nom de naissance">
                  <input
                    type="text"
                    value={editForm.birthName || ""}
                    onChange={(e) => setEditField("birthName", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Date de naissance">
                  <input
                    type="date"
                    value={editForm.birthDate || ""}
                    onChange={(e) => setEditField("birthDate", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField
                  label="N° INSEE (carte vitale)"
                  className="col-span-2"
                >
                  <input
                    type="text"
                    value={editForm.inseeNumber || ""}
                    onChange={(e) =>
                      setEditField("inseeNumber", e.target.value)
                    }
                    placeholder="Ex: 1 85 12 75 123 456 78"
                    className={inputCls}
                  />
                </FormField>
              </div>
            </div>

            {/* Lieu de naissance */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Lieu de naissance
              </p>
              <div className="grid grid-cols-2 gap-4">
                <CommuneField
                  postalValue={editForm.birthPostalCode || ""}
                  cityValue={editForm.birthCity || ""}
                  onPostalChange={(v) => setEditField("birthPostalCode", v)}
                  onCityChange={(v) => setEditField("birthCity", v)}
                  postalLabel="Code postal de naissance"
                  cityLabel="Commune de naissance"
                />
                <FormField label="Pays de naissance" className="col-span-2">
                  <input
                    type="text"
                    value={editForm.birthCountry || ""}
                    onChange={(e) =>
                      setEditField("birthCountry", e.target.value)
                    }
                    placeholder="Ex: France"
                    className={inputCls}
                  />
                </FormField>
              </div>
            </div>

            {/* Contact */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Contact
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Email">
                  <input
                    type="email"
                    value={editForm.email || ""}
                    onChange={(e) => setEditField("email", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Téléphone">
                  <input
                    type="text"
                    value={editForm.phone || ""}
                    onChange={(e) => setEditField("phone", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Adresse" className="col-span-2">
                  <input
                    type="text"
                    value={editForm.address || ""}
                    onChange={(e) => setEditField("address", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <CommuneField
                  postalValue={editForm.postalCode || ""}
                  cityValue={editForm.city || ""}
                  onPostalChange={(v) => setEditField("postalCode", v)}
                  onCityChange={(v) => setEditField("city", v)}
                />
                <FormField label="Pays" className="col-span-2">
                  <input
                    type="text"
                    value={editForm.country || ""}
                    onChange={(e) => setEditField("country", e.target.value)}
                    placeholder="Ex: France"
                    className={inputCls}
                  />
                </FormField>
              </div>
            </div>

            {/* Contrat */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Contrat
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Poste" required className="col-span-2">
                  <input
                    type="text"
                    value={editForm.position || ""}
                    onChange={(e) => setEditField("position", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Type de contrat">
                  <select
                    value={editForm.contractType || "CDI"}
                    onChange={(e) =>
                      setEditField("contractType", e.target.value)
                    }
                    className={inputCls}
                  >
                    {STAGE_CONTRACT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Statut">
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditField("status", e.target.value)}
                    className={inputCls}
                  >
                    {STAGE_STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Date d'embauche" required>
                  <input
                    type="date"
                    value={editForm.startDate || ""}
                    onChange={(e) => setEditField("startDate", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField
                  label={getEndDateLabel(editForm.contractType)}
                  required={isEndDateMandatory(editForm.contractType)}
                  help={editForm.contractType === "CDI" ? "Optionnel — l'employé passera inactif automatiquement après cette date" : undefined}
                >
                  <input
                    type="date"
                    value={editForm.endDate || ""}
                    onChange={(e) => setEditField("endDate", e.target.value)}
                    className={inputCls}
                    required={isEndDateMandatory(editForm.contractType)}
                  />
                </FormField>
                <FormField label="Heures/semaine">
                  <input
                    type="number"
                    step="0.5"
                    value={editForm.workHours ?? ""}
                    onChange={(e) => setEditField("workHours", e.target.value)}
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Taux horaire (€)">
                  {(() => {
                    const wh = parseFloat(editForm.workHours);
                    const sal = employee?.currentSalary;
                    const hr =
                      sal && wh > 0
                        ? (parseFloat(sal) / ((wh * 52) / 12)).toFixed(4)
                        : null;
                    return (
                      <div className={`${inputCls} bg-gray-50 text-gray-600`}>
                        {hr ? (
                          `${hr} €/h`
                        ) : (
                          <span className="text-gray-400 italic">
                            calculé depuis le salaire
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </FormField>
                <FormField label="Salaire actuel" className="col-span-2">
                  <div
                    className={`${inputCls} bg-gray-50 text-gray-600 flex items-center justify-between`}
                  >
                    <span>
                      {employee?.currentSalary ? (
                        new Intl.NumberFormat("fr-FR", {
                          style: "currency",
                          currency: "EUR",
                        }).format(employee.currentSalary)
                      ) : (
                        <span className="text-gray-400 italic">
                          Non renseigné
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setActiveTab("salaires");
                      }}
                      className="text-xs text-primary hover:underline ml-2 flex-shrink-0"
                    >
                      Modifier via Salaires →
                    </button>
                  </div>
                </FormField>
                <FormField label="Catégorie convention">
                  <input
                    type="text"
                    value={editForm.conventionCategory}
                    onChange={(e) =>
                      setEditField("conventionCategory", e.target.value)
                    }
                    placeholder="Ex: Technicien"
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Échelon">
                  <input
                    type="text"
                    value={editForm.conventionEchelon}
                    onChange={(e) =>
                      setEditField("conventionEchelon", e.target.value)
                    }
                    placeholder="Ex: 2"
                    className={inputCls}
                  />
                </FormField>
                <FormField
                  label="Titre transport (€/mois)"
                  className="col-span-2"
                >
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={editForm.transportAmount}
                    onChange={(e) =>
                      setEditField("transportAmount", e.target.value)
                    }
                    placeholder="Ex: 86.34"
                    className={inputCls}
                  />
                </FormField>
              </div>
            </div>

            {/* Mutuelle */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Mutuelle
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Mutuelle">
                  <select
                    value={editForm.mutuelleType}
                    onChange={(e) =>
                      setEditField("mutuelleType", e.target.value)
                    }
                    className={inputCls}
                  >
                    {STAGE_MUTUELLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                {editForm.mutuelleType === "conjoint" && (
                  <FormField label="Validité mutuelle conjoint">
                    <input
                      type="date"
                      value={editForm.mutuelleValidUntil}
                      onChange={(e) =>
                        setEditField("mutuelleValidUntil", e.target.value)
                      }
                      className={inputCls}
                    />
                  </FormField>
                )}
                <FormField
                  label="Justificatif mutuelle"
                  className={
                    editForm.mutuelleType === "conjoint" ? "col-span-2" : ""
                  }
                >
                  <input
                    type="text"
                    value={editForm.mutuelleDocument}
                    onChange={(e) =>
                      setEditField("mutuelleDocument", e.target.value)
                    }
                    placeholder="Nom du document"
                    className={inputCls}
                  />
                </FormField>
              </div>
            </div>

            {/* ── Plannings PDF (structures) ──────────────────────── */}
            <div className="border-t border-gray-100 pt-4 mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Plannings PDF
              </p>
              <FormField label="Structures affichées dans l'export planning">
                <div className="flex flex-wrap gap-2">
                  {structures.map((s) => {
                    const selected = editForm.planningPdfStructures
                      ? typeof editForm.planningPdfStructures === "string"
                        ? (() => {
                            try {
                              return JSON.parse(editForm.planningPdfStructures || "[]");
                            } catch {
                              return [];
                            }
                          })()
                        : editForm.planningPdfStructures
                      : [];
                    const isSelected =
                      Array.isArray(selected) && selected.includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition ${
                          isSelected
                            ? "bg-indigo-100 border-indigo-300 text-indigo-700"
                            : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={isSelected}
                          onChange={() => {
                            const current = Array.isArray(selected)
                              ? [...selected]
                              : [];
                            const updated = isSelected
                              ? current.filter((id) => id !== s.id)
                              : [...current, s.id];
                            setEditField(
                              "planningPdfStructures",
                              JSON.stringify(updated),
                            );
                          }}
                        />
                        {s.name}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Coche les structures dans lesquelles l'employé doit apparaître
                  sur l'export PDF planning. Vide = exclu de tous les PDF.
                </p>
              </FormField>
            </div>

            {/* Notes */}
            <FormField label="Notes">
              <textarea
                value={editForm.notes || ""}
                onChange={(e) => setEditField("notes", e.target.value)}
                rows={2}
                className={inputCls}
              />
            </FormField>

            {/* ── Organigramme ──────────────────────────────────── */}
            <div className="border-t border-gray-100 pt-4 mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Organigramme</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Rôle hiérarchique">
                  <select
                    className={inputCls}
                    value={editForm.role || ""}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      setEditField("role", newRole);
                      // Reset continuité si le rôle sélectionné ne l'autorise pas
                      const roleObj = orgRoles.find(r => r.code === newRole);
                      if (!roleObj?.allowsContinuiteDir) {
                        setEditField("isContinuiteDirection", false);
                      }
                    }}
                  >
                    <option value="">— Non défini —</option>
                    {orgRoles.map(r => (
                      <option key={r.id} value={r.code}>{r.name}</option>
                    ))}
                  </select>
                </FormField>

                <FormField label="Groupe">
                  <select
                    className={inputCls}
                    value={editForm.orgGroupId || ""}
                    onChange={(e) => setEditField("orgGroupId", e.target.value || null)}
                  >
                    <option value="">— Aucun groupe —</option>
                    {orgGroups.map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </FormField>

                {/* Continuité direction — uniquement si le rôle l'autorise */}
                {orgRoles.find(r => r.code === editForm.role)?.allowsContinuiteDir && (
                  <div className="flex items-center gap-2 pt-6">
                    <input
                      type="checkbox"
                      id="edit-continuite"
                      className="rounded border-gray-300 cursor-pointer"
                      checked={editForm.isContinuiteDirection || false}
                      onChange={(e) => setEditField("isContinuiteDirection", e.target.checked)}
                    />
                    <label htmlFor="edit-continuite" className="text-sm text-gray-700 cursor-pointer">
                      Continuité de direction
                    </label>
                  </div>
                )}
              </div>

              {/* Structures secondaires */}
              {allStructures.length > 1 && (
                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-2">
                    Structures secondaires
                    <span className="text-gray-400 ml-1">(en plus de la structure principale : {employee?.structure?.name})</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {allStructures
                      .filter(s => s.id !== employee?.structureId)
                      .map(s => {
                        const isSelected = (editForm.secondaryStructureIds || []).includes(s.id);
                        return (
                          <label
                            key={s.id}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition ${isSelected ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                          >
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={isSelected}
                              onChange={() => {
                                const current = editForm.secondaryStructureIds || [];
                                setEditField(
                                  'secondaryStructureIds',
                                  isSelected ? current.filter(id => id !== s.id) : [...current, s.id]
                                );
                              }}
                            />
                            {s.name}
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Photo */}
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-2">Photo (JPG, PNG ou SVG, max 10 Mo)</p>
                <div className="flex items-center gap-3">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.svg"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setPhotoUploading(true);
                      try {
                        await uploadEmployeePhoto(employee.id, file);
                        const { data } = await getEmployeeById(employee.id);
                        setEmployee(data);
                        toast.success("Photo mise à jour");
                      } catch (err) {
                        toast.error(err.response?.data?.error || "Erreur upload photo");
                      } finally {
                        setPhotoUploading(false);
                        e.target.value = "";
                      }
                    }}
                  />
                  <Btn
                    variant="secondary"
                    size="sm"
                    loading={photoUploading}
                    onClick={() => photoInputRef.current?.click()}
                  >
                    {employee?.photoUrl ? "Changer la photo" : "Ajouter une photo"}
                  </Btn>
                  {employee?.photoUrl && (
                    <Btn
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={async () => {
                        try {
                          await deleteEmployeePhoto(employee.id);
                          const { data } = await getEmployeeById(employee.id);
                          setEmployee(data);
                          toast.success("Photo supprimée");
                        } catch {
                          toast.error("Erreur suppression photo");
                        }
                      }}
                    >
                      Supprimer
                    </Btn>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
      {showOfferLetterModal && (
        <OfferLetterModal
          employee={employee}
          onClose={() => setShowOfferLetterModal(false)}
          onGenerated={async () => {
            try {
              const { data } = await getEmployeeById(employee.id);
              setEmployee(data);
            } catch (_) {}
          }}
        />
      )}
      {showGenerateDocModal && (
        <GenerateDocumentModal
          employee={employee}
          onClose={() => setShowGenerateDocModal(false)}
        />
      )}
    </div>
  );
};

// ─── Onglet Charges RH ────────────────────────────────────────────────────────

const ChargesTab = ({ showAllStructures = false }) => {
  const toast = useToast();
  const { currentStructure } = useStructure();
  const [deleteCostConfirm, setDeleteCostConfirm] = useState(null);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [costForm, setCostForm] = useState({
    category: "salaires_bruts",
    amount: "",
  });

  const structureId = showAllStructures ? null : currentStructure?.id || null;
  const isMulti = showAllStructures || !currentStructure?.id;

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (structureId) params.structureId = structureId;
    getHRCosts(params)
      .then((cr) => {
        setCosts((cr.data || []).filter((c) => c.month?.startsWith(month)));
      })
      .finally(() => setLoading(false));
  }, [structureId, month]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpsertCost = async () => {
    if (!costForm.amount || !structureId) return;
    setLoading(true);
    try {
      await upsertHRCost({
        structureId: String(structureId),
        month,
        category: costForm.category,
        amount: Number(costForm.amount),
      });
      setCostForm((f) => ({ ...f, amount: "" }));
      fetchData();
      toast.success("Charge ajoutée");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'ajout de la charge");
    }
    setLoading(false);
  };

  const handleDeleteCost = (id) => setDeleteCostConfirm(id);

  const doDeleteCost = async () => {
    const id = deleteCostConfirm;
    setDeleteCostConfirm(null);
    await deleteHRCost(id);
    fetchData();
  };

  const totalCosts = costs.reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Filtre mois */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Mois</label>
          <input
            className={inputCls}
            style={{ width: "auto" }}
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-xs text-gray-400 mb-1">
            Total charges du mois
          </div>
          <div className="text-xl font-bold text-red-600">
            {fmtCurrency(totalCosts)}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-semibold text-gray-700 text-sm">Charges RH</h3>
        {isMulti ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
            Sélectionnez une structure dans l'en-tête pour saisir des charges.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
            <div className="flex gap-2">
              <select
                className={inputCls}
                value={costForm.category}
                onChange={(e) =>
                  setCostForm((f) => ({ ...f, category: e.target.value }))
                }
              >
                {COST_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {COST_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
              <input
                className={inputCls}
                type="number"
                min={0}
                placeholder="Montant (€)"
                value={costForm.amount}
                onChange={(e) =>
                  setCostForm((f) => ({ ...f, amount: e.target.value }))
                }
              />
              <Btn onClick={handleUpsertCost} loading={loading} size="sm">
                <Check size={14} />
              </Btn>
            </div>
          </div>
        )}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center p-4">
              <Loader className="animate-spin text-primary" size={18} />
            </div>
          ) : costs.length === 0 ? (
            <div className="p-4 text-sm text-gray-400 text-center">
              Aucune charge saisie
            </div>
          ) : (
            <ul className="divide-y divide-gray-50">
              {costs.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between px-4 py-2.5 text-sm"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-gray-700">
                      {COST_CATEGORY_LABELS[c.category] || c.category}
                    </span>
                    {isMulti && c.structure?.name && (
                      <span className="text-xs text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 truncate">
                        {c.structure.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{fmtCurrency(c.amount)}</span>
                    {!isMulti && (
                      <button
                        onClick={() => handleDeleteCost(c.id)}
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <ConfirmModal
        open={!!deleteCostConfirm}
        title="Supprimer cette charge ?"
        message="Cette action est irréversible."
        onConfirm={doDeleteCost}
        onCancel={() => setDeleteCostConfirm(null)}
        danger
      />
    </div>
  );
};

// ─── Panneau de détail d'une évaluation IA ────────────────────────────────────

const EvaluationDetailPanel = ({
  ev,
  criteriaRows,
  summaryRows,
  onValidate,
}) => {
  const [showRaw, setShowRaw] = useState(false);
  const hasParsedRows = criteriaRows.length > 0;
  return (
    <div className="bg-purple-50 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-purple-800 text-sm flex items-center gap-1.5">
          <Zap size={14} />
          Analyse IA
        </h4>
        <div className="flex items-center gap-2">
          {ev.validatedByAdmin ? (
            <Badge color="bg-green-100 text-green-700">Validée</Badge>
          ) : (
            <button
              onClick={() => onValidate(ev.id)}
              className="text-xs text-purple-600 hover:underline font-medium"
            >
              Valider
            </button>
          )}
        </div>
      </div>

      {/* Avis final (résumé rapide) */}
      {ev.extractedScores?.avisFinal && (
        <div className="text-sm text-purple-800 font-medium bg-white rounded-lg px-3 py-2 border border-purple-100">
          {ev.extractedScores.avisFinal}
        </div>
      )}

      {/* Score total */}
      {ev.extractedScores?.total != null && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-purple-600">Score total :</span>
          <span className="font-bold text-purple-800 text-sm">
            {ev.extractedScores.total}/100
          </span>
          <div className="flex-1 bg-purple-200 rounded-full h-2">
            <div
              className="bg-purple-600 h-2 rounded-full"
              style={{ width: `${Math.min(100, ev.extractedScores.total)}%` }}
            />
          </div>
        </div>
      )}

      {/* Tableau détaillé Critère | Analyse | Score */}
      {hasParsedRows ? (
        <div className="overflow-x-auto rounded-lg border border-purple-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-purple-100 text-purple-700">
                <th className="px-3 py-2 text-left font-semibold">Critère</th>
                <th className="px-3 py-2 text-left font-semibold">Analyse</th>
                <th className="px-3 py-2 text-right font-semibold w-16">
                  Score
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-purple-100 bg-white">
              {criteriaRows.map((row, i) => (
                <tr key={i} className="hover:bg-purple-50">
                  <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">
                    {row.criterion}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{row.analysis}</td>
                  <td className="px-3 py-2 text-right font-semibold text-purple-700 whitespace-nowrap">
                    {row.score}
                  </td>
                </tr>
              ))}
              {summaryRows.map((row, i) => (
                <tr
                  key={`s${i}`}
                  className={`${row.criterion === "Avis final" ? "bg-purple-50" : ""}`}
                >
                  <td className="px-3 py-2 font-semibold text-purple-700">
                    {row.criterion}
                  </td>
                  <td
                    className="px-3 py-2 text-gray-700 italic"
                    colSpan={row.score === "—" ? 2 : 1}
                  >
                    {row.analysis}
                  </td>
                  {row.score !== "—" && (
                    <td className="px-3 py-2 text-right font-bold text-purple-700">
                      {row.score}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        ev.extractedScores && (
          /* Fallback : 2-col si parsing échoué */
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(ev.extractedScores)
              .filter(([k]) => !["total", "avisFinal"].includes(k))
              .map(([k, v]) => (
                <div
                  key={k}
                  className="flex justify-between text-xs bg-white rounded px-3 py-1.5 border border-purple-100"
                >
                  <span className="text-purple-600">{k}</span>
                  <span className="font-semibold">{v}</span>
                </div>
              ))}
          </div>
        )
      )}

      {/* Toggle texte brut */}
      {ev.markdownOutput && (
        <div>
          <button
            onClick={() => setShowRaw((r) => !r)}
            className="text-xs text-purple-500 hover:text-purple-700 underline"
          >
            {showRaw
              ? "Masquer le texte complet"
              : "Voir le texte complet de l'analyse"}
          </button>
          {showRaw && (
            <pre className="mt-2 text-xs text-gray-600 bg-white border border-purple-100 rounded-lg p-3 whitespace-pre-wrap overflow-x-auto max-h-60 overflow-y-auto">
              {ev.markdownOutput}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Onglet Recrutement ───────────────────────────────────────────────────────

const RecruitmentTab = () => {
  const toast = useToast();
  const [applications, setApplications] = useState([]);
  const [grids, setGrids] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [llmConfigured, setLlmConfigured] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [detailApp, setDetailApp] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [hireConfirm, setHireConfirm] = useState(null);
  const [deleteAppConfirm, setDeleteAppConfirm] = useState(null);

  // Formulaire candidat
  const [appForm, setAppForm] = useState({
    candidateName: "",
    candidateEmail: "",
    candidatePhone: "",
    position: "",
    status: "to_review",
    source: "",
    notes: "",
    structureId: "",
  });
  const [cvFile, setCvFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [appsRes, gridsRes, settingsRes, structsRes] = await Promise.all([
        getApplications(),
        getEvaluationGrids(),
        getHRSettings(),
        getStructures(),
      ]);
      setApplications(appsRes.data || []);
      setGrids(gridsRes.data || []);
      setLlmConfigured(!!settingsRes.data?.llmProvider);
      const structs = structsRes.data || [];
      setStructures(structs);
      setAppForm((f) => ({
        ...f,
        structureId:
          f.structureId || (structs[0]?.id ? String(structs[0].id) : ""),
      }));
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des candidatures");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSubmitApp = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createApplication({
        ...appForm,
        ...(cvFile ? { cv: cvFile } : {}),
      });
      setShowForm(false);
      setAppForm({
        candidateName: "",
        candidateEmail: "",
        candidatePhone: "",
        position: "",
        status: "to_review",
        source: "",
        notes: "",
        structureId: structures[0]?.id ? String(structures[0].id) : "",
      });
      setCvFile(null);
      fetchAll();
      toast.success("Candidature ajoutée");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'ajout de la candidature");
    }
    setSaving(false);
  };

  const handleStatusChange = async (id, status) => {
    await updateApplication(id, { status });
    fetchAll();
  };

  const handleHire = (id) => setHireConfirm(id);
  const doHire = async () => {
    const id = hireConfirm;
    setHireConfirm(null);
    await hireApplicant(id);
    fetchAll();
  };

  const handleDelete = (id) => setDeleteAppConfirm(id);
  const doDeleteApp = async () => {
    const id = deleteAppConfirm;
    setDeleteAppConfirm(null);
    await deleteApplication(id);
    fetchAll();
  };

  const handleAnalyzeCV = async (app) => {
    setAnalyzing(app.id);
    setAnalyzeError(null);
    try {
      if (!app.cvUrl) {
        setAnalyzeError(
          "Aucun CV attaché à cette candidature. Uploadez un CV (PDF) d'abord.",
        );
        setAnalyzing(null);
        return;
      }
      const defaultGrid = grids.find((g) => g.isDefault) || grids[0];
      if (!defaultGrid) {
        setAnalyzeError(
          "Aucune grille d'évaluation configurée. Créez-en une via Administration > RH.",
        );
        setAnalyzing(null);
        return;
      }
      await analyzeCV(app.id, { gridId: defaultGrid.id });
      fetchAll();
    } catch (err) {
      console.error(err);
      setAnalyzeError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Erreur lors de l'analyse CV",
      );
      toast.error("Erreur lors de l'analyse CV");
    }
    setAnalyzing(null);
  };

  const handleValidateEval = async (evalId) => {
    await validateEvaluation(evalId);
    fetchAll();
  };

  const filtered = applications.filter((a) => {
    const matchSearch =
      !search ||
      (a.candidateName || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.position || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || a.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-4">
      {/* Alerte LLM non configuré */}
      {!llmConfigured && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={16} className="shrink-0 text-amber-500" />
          <span>
            LLM non configuré — l'analyse automatique des CV est désactivée.
            Configurez un fournisseur dans{" "}
            <strong>Administration → Paramètres LLM</strong>.
          </span>
        </div>
      )}
      {/* Erreur analyse CV */}
      {analyzeError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <AlertTriangle size={16} className="shrink-0 text-red-500" />
          <span className="flex-1">{analyzeError}</span>
          <button
            onClick={() => setAnalyzeError(null)}
            className="text-red-400 hover:text-red-600"
          >
            <X size={15} />
          </button>
        </div>
      )}
      {/* Filtres */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            className={`${inputCls} pl-8`}
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className={inputCls}
          style={{ width: "auto" }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Tous statuts</option>
          {Object.entries(APP_STATUS_LABELS).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
        <Btn onClick={() => setShowForm(true)}>
          <Plus size={15} />
          Candidature
        </Btn>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader className="animate-spin text-primary" size={24} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          Aucune candidature
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => (
            <div
              key={a.id}
              className="bg-white rounded-xl border border-gray-200 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800">
                      {a.candidateName}
                    </span>
                    <Badge
                      color={
                        APP_STATUS_COLORS[a.status] ||
                        "bg-gray-100 text-gray-600"
                      }
                    >
                      {APP_STATUS_LABELS[a.status] || a.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{a.position}</p>
                  {(a.email || a.phone) && (
                    <div className="flex gap-3 mt-1 text-xs text-gray-400">
                      {a.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={11} />
                          {a.email}
                        </span>
                      )}
                      {a.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={11} />
                          {a.phone}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {a.cvUrl && (
                    <>
                      <a
                        href={a.cvUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 text-gray-400 hover:text-primary rounded-lg"
                        title="Voir CV"
                      >
                        <FileText size={15} />
                      </a>
                      {a.status !== "hired" && (
                        <button
                          onClick={() => handleAnalyzeCV(a)}
                          disabled={analyzing === a.id}
                          className="p-1.5 text-gray-400 hover:text-purple-600 rounded-lg disabled:opacity-50"
                          title="Analyser CV (IA)"
                        >
                          {analyzing === a.id ? (
                            <Loader size={15} className="animate-spin" />
                          ) : (
                            <Zap size={15} />
                          )}
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => setDetailApp(a)}
                    className="p-1.5 text-gray-400 hover:text-primary rounded-lg"
                    title="Détail"
                  >
                    <Eye size={15} />
                  </button>
                  {a.status !== "hired" && (
                    <button
                      onClick={() => handleHire(a.id)}
                      className="p-1.5 text-gray-400 hover:text-green-600 rounded-lg"
                      title="Embaucher"
                    >
                      <Check size={15} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(a.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg"
                    title="Supprimer"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              {/* Évaluation IA */}
              {a.evaluations?.[0] &&
                (() => {
                  const ev = a.evaluations[0];
                  return (
                    <div className="mt-3 bg-purple-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-purple-700 flex items-center gap-1">
                          <Zap size={11} />
                          Analyse IA
                        </span>
                        {!ev.validatedByAdmin && (
                          <button
                            onClick={() => handleValidateEval(ev.id)}
                            className="text-xs text-purple-600 hover:underline"
                          >
                            Valider
                          </button>
                        )}
                        {ev.validatedByAdmin && (
                          <Badge color="bg-green-100 text-green-700">
                            Validée
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-purple-800">
                        {ev.extractedScores?.avisFinal || "—"}
                      </div>
                      {ev.extractedScores?.total != null && (
                        <div className="text-xs text-purple-600 mt-1">
                          Score total : {ev.extractedScores.total}/100
                        </div>
                      )}
                    </div>
                  );
                })()}

              {/* Changement de statut rapide */}
              <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-gray-100 flex-wrap">
                <span className="text-xs text-gray-400">Statut :</span>
                {Object.entries(APP_STATUS_LABELS)
                  .filter(([k]) => k !== "hired")
                  .map(([k, l]) => (
                    <button
                      key={k}
                      onClick={() => handleStatusChange(a.id, k)}
                      className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${a.status === k ? "bg-primary text-white border-primary" : "border-gray-200 text-gray-500 hover:border-primary hover:text-primary"}`}
                    >
                      {l}
                    </button>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal nouvelle candidature */}
      {showForm && (
        <Modal
          title="Nouvelle candidature"
          onClose={() => setShowForm(false)}
          size="lg"
        >
          <form onSubmit={handleSubmitApp} className="space-y-4">
            <FormField label="Structure" required>
              <select
                className={inputCls}
                value={appForm.structureId}
                onChange={(e) =>
                  setAppForm((f) => ({ ...f, structureId: e.target.value }))
                }
                required
              >
                <option value="">Sélectionner une structure...</option>
                {structures.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Nom complet" required>
              <input
                className={inputCls}
                value={appForm.candidateName}
                onChange={(e) =>
                  setAppForm((f) => ({ ...f, candidateName: e.target.value }))
                }
                required
              />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Email">
                <input
                  className={inputCls}
                  type="email"
                  value={appForm.candidateEmail}
                  onChange={(e) =>
                    setAppForm((f) => ({
                      ...f,
                      candidateEmail: e.target.value,
                    }))
                  }
                />
              </FormField>
              <FormField label="Téléphone">
                <input
                  className={inputCls}
                  value={appForm.candidatePhone}
                  onChange={(e) =>
                    setAppForm((f) => ({
                      ...f,
                      candidatePhone: e.target.value,
                    }))
                  }
                />
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Poste visé" required>
                <input
                  className={inputCls}
                  value={appForm.position}
                  onChange={(e) =>
                    setAppForm((f) => ({ ...f, position: e.target.value }))
                  }
                  required
                />
              </FormField>
              <FormField label="Source">
                <input
                  className={inputCls}
                  placeholder="Indeed, recommandation..."
                  value={appForm.source}
                  onChange={(e) =>
                    setAppForm((f) => ({ ...f, source: e.target.value }))
                  }
                />
              </FormField>
            </div>
            <FormField label="CV (PDF/image)">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setCvFile(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
              />
            </FormField>
            <FormField label="Notes">
              <textarea
                className={inputCls}
                rows={2}
                value={appForm.notes}
                onChange={(e) =>
                  setAppForm((f) => ({ ...f, notes: e.target.value }))
                }
              />
            </FormField>
            <div className="flex gap-2 justify-end pt-2">
              <Btn variant="secondary" onClick={() => setShowForm(false)}>
                Annuler
              </Btn>
              <Btn type="submit" loading={saving}>
                Enregistrer
              </Btn>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal détail candidature */}
      {detailApp && (
        <Modal
          title={detailApp.candidateName}
          onClose={() => setDetailApp(null)}
          size="lg"
        >
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              {[
                ["Poste", detailApp.position],
                ["Statut", APP_STATUS_LABELS[detailApp.status]],
                ["Email", detailApp.candidateEmail],
                ["Téléphone", detailApp.candidatePhone],
                ["Source", detailApp.source],
                ["Date", fmtDate(detailApp.createdAt)],
              ].map(([l, v]) =>
                v ? (
                  <div key={l}>
                    <div className="text-xs text-gray-400">{l}</div>
                    <div className="font-medium text-gray-800">{v}</div>
                  </div>
                ) : null,
              )}
            </div>
            {detailApp.notes && (
              <div>
                <div className="text-xs text-gray-400 mb-1">Notes</div>
                <div className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">
                  {detailApp.notes}
                </div>
              </div>
            )}
            {detailApp.cvUrl && (
              <a
                href={detailApp.cvUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium"
              >
                <FileText size={15} />
                Voir le CV
              </a>
            )}
            {detailApp.evaluations?.[0] &&
              (() => {
                const ev = detailApp.evaluations[0];
                const tableRows = parseMarkdownTable(ev.markdownOutput);
                const criteriaRows = tableRows.filter(
                  (r) =>
                    ![
                      "Points forts",
                      "Points faibles",
                      "Score global",
                      "Avis final",
                    ].includes(r.criterion),
                );
                const summaryRows = tableRows.filter((r) =>
                  [
                    "Points forts",
                    "Points faibles",
                    "Score global",
                    "Avis final",
                  ].includes(r.criterion),
                );
                return (
                  <EvaluationDetailPanel
                    ev={ev}
                    criteriaRows={criteriaRows}
                    summaryRows={summaryRows}
                    onValidate={handleValidateEval}
                  />
                );
              })()}
          </div>
        </Modal>
      )}
      <ConfirmModal
        open={!!hireConfirm}
        title="Confirmer l'embauche ?"
        message="Le candidat sera marqué comme embauché."
        onConfirm={doHire}
        onCancel={() => setHireConfirm(null)}
      />
      <ConfirmModal
        open={!!deleteAppConfirm}
        title="Supprimer cette candidature ?"
        message="Cette action est irréversible."
        onConfirm={doDeleteApp}
        onCancel={() => setDeleteAppConfirm(null)}
        danger
      />
    </div>
  );
};

// ─── Onglet Documents RH ──────────────────────────────────────────────────────

const HR_DOC_CATEGORIES = {
  convention: "Convention collective",
  charge: "Charges RH",
  contrat_type: "Contrat type",
  autre: "Autre",
};

const DocumentsTab = ({ showAllStructures = false }) => {
  const toast = useToast();
  const { currentStructure } = useStructure();
  // Types administrables (depuis admin > RH > Types de documents employé)
  const [docTypesList, setDocTypesList] = useState([]);
  useEffect(() => {
    getEmployeeDocumentTypes()
      .then((r) => setDocTypesList(r.data || []))
      .catch(() => setDocTypesList([]));
  }, []);
  const EMP_DOC_TYPE_LABELS = Object.fromEntries(
    (docTypesList || []).map((t) => [t.slug, t.name])
  );

  const [viewMode, setViewMode] = useState("hr");
  const [viewingDoc, setViewingDoc] = useState(null);

  // Documents RH généraux
  const [hrDocs, setHrDocs] = useState([]);
  const [loadingHr, setLoadingHr] = useState(true);
  const [catFilter, setCatFilter] = useState("");
  const [reindexing, setReindexing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: "",
    category: "",
    notes: "",
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Documents employés
  const [empDocs, setEmpDocs] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(true);
  const [empTypeFilter, setEmpTypeFilter] = useState("");

  const fetchHrDocs = useCallback(async () => {
    setLoadingHr(true);
    try {
      const params = {};
      if (catFilter) params.category = catFilter;
      params.structureId = showAllStructures
        ? "all"
        : currentStructure?.id || undefined;
      const res = await getHRDocuments(params);
      setHrDocs(res.data);
    } catch {
      toast.error("Erreur chargement documents RH");
    }
    setLoadingHr(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catFilter, currentStructure?.id, showAllStructures]);

  const fetchEmpDocs = useCallback(async () => {
    setLoadingEmp(true);
    try {
      const params = {};
      params.structureId = showAllStructures
        ? "all"
        : currentStructure?.id || undefined;
      const res = await getAllEmployeeDocuments(params);
      setEmpDocs(res.data);
    } catch {
      toast.error("Erreur chargement documents employés");
    }
    setLoadingEmp(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStructure?.id, showAllStructures]);

  useEffect(() => {
    fetchHrDocs();
  }, [fetchHrDocs]);
  useEffect(() => {
    fetchEmpDocs();
  }, [fetchEmpDocs]);

  const handleUpload = async () => {
    if (!uploadFile || !uploadForm.name.trim()) return;
    setSaving(true);
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("name", uploadForm.name);
    if (uploadForm.category) fd.append("category", uploadForm.category);
    if (uploadForm.notes) fd.append("notes", uploadForm.notes);
    if (currentStructure?.id) fd.append("structureId", currentStructure.id);
    try {
      await uploadHRDocument(fd);
      setShowUpload(false);
      setUploadFile(null);
      setUploadForm({ name: "", category: "", notes: "" });
      fetchHrDocs();
      toast.success("Document ajouté");
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de l'upload");
    }
    setSaving(false);
  };

  const handleReindex = async (id) => {
    setReindexing(id);
    try {
      await reindexHRDocument(id);
      await fetchHrDocs();
      toast.success("Réindexation lancée");
    } catch {
      toast.error("Erreur réindexation");
    }
    setReindexing(null);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteHRDocument(deleteConfirm.id);
      setDeleteConfirm(null);
      fetchHrDocs();
      toast.success("Document supprimé");
    } catch {
      toast.error("Erreur suppression");
    }
  };

  const handleDownloadHr = async (doc) => {
    try {
      const res = await getHRDocumentFile(doc.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur téléchargement");
    }
  };

  const handleDownloadEmp = async (doc) => {
    try {
      const res = await getEmployeeDocumentFile(doc.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur téléchargement");
    }
  };

  const filteredEmpDocs = empTypeFilter
    ? empDocs.filter((d) => d.type === empTypeFilter)
    : empDocs;

  const tabCls = (id) =>
    `px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
      viewMode === id
        ? "border-primary text-primary"
        : "border-transparent text-gray-500 hover:text-gray-700"
    }`;

  return (
    <div className="space-y-4">
      {/* Sous-onglets */}
      <div className="flex gap-1 border-b border-gray-200">
        <button onClick={() => setViewMode("hr")} className={tabCls("hr")}>
          Documents RH
        </button>
        <button
          onClick={() => setViewMode("employees")}
          className={tabCls("employees")}
        >
          Documents employés ({empDocs.length})
        </button>
        <button
          onClick={() => setViewMode("payroll")}
          className={tabCls("payroll")}
        >
          Paie & Déclarations
        </button>
      </div>

      {/* ── Vue Documents RH généraux ── */}
      {viewMode === "hr" && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              className={inputCls}
              style={{ width: "auto" }}
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
            >
              <option value="">Toutes catégories</option>
              {Object.entries(HR_DOC_CATEGORIES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <div className="flex-1" />
            <Btn
              onClick={() => {
                setUploadForm({ name: "", category: "", notes: "" });
                setUploadFile(null);
                setShowUpload(true);
              }}
            >
              <Upload size={14} /> Ajouter un document
            </Btn>
          </div>

          {loadingHr ? (
            <div className="flex justify-center py-8">
              <Loader className="animate-spin text-primary" size={24} />
            </div>
          ) : hrDocs.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm bg-white rounded-xl border border-gray-200">
              <FileText size={32} className="mx-auto mb-3 opacity-30" />
              Aucun document RH. Ajoutez des PDF (convention collective,
              contrats types…)
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                      Nom
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                      Catégorie
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                      Taille
                    </th>
                    <th className="px-4 py-2.5 text-center text-xs font-medium text-gray-500">
                      Indexé
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                      Date
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {hrDocs.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            setViewingDoc({
                              fileName: doc.fileName,
                              mimeType: doc.mimeType,
                              fetchBlob: () => getHRDocumentFile(doc.id),
                            })
                          }
                          className="font-medium text-primary hover:underline text-left"
                        >
                          {doc.name}
                        </button>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {doc.fileName}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {HR_DOC_CATEGORIES[doc.category] || doc.category || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {doc.size ? `${Math.round(doc.size / 1024)} Ko` : "—"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {doc.indexed ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            <Check size={11} /> {doc._count?.chunks || 0} chunks
                          </span>
                        ) : (
                          <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                            En attente
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {fmtDate(doc.uploadedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => handleReindex(doc.id)}
                            disabled={reindexing === doc.id}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-40"
                            title="Réindexer"
                          >
                            <RefreshCw
                              size={14}
                              className={
                                reindexing === doc.id ? "animate-spin" : ""
                              }
                            />
                          </button>
                          <button
                            onClick={() => handleDownloadHr(doc)}
                            className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg"
                            title="Télécharger"
                          >
                            <Download size={14} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(doc)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Supprimer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Vue Documents employés ── */}
      {viewMode === "employees" && (
        <>
          <div className="flex items-center gap-3">
            <select
              className={inputCls}
              style={{ width: "auto" }}
              value={empTypeFilter}
              onChange={(e) => setEmpTypeFilter(e.target.value)}
            >
              <option value="">Tous les types</option>
              {Object.entries(EMP_DOC_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            {empTypeFilter && (
              <span className="text-xs text-gray-400">
                {filteredEmpDocs.length} résultat
                {filteredEmpDocs.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {loadingEmp ? (
            <div className="flex justify-center py-8">
              <Loader className="animate-spin text-primary" size={24} />
            </div>
          ) : filteredEmpDocs.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm bg-white rounded-xl border border-gray-200">
              Aucun document{empTypeFilter ? " pour ce type" : ""}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                      Employé
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                      Type
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                      Fichier
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                      Notes
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                      Date
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredEmpDocs.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {doc.employee?.lastName} {doc.employee?.firstName}
                      </td>
                      <td className="px-4 py-3">
                        <Badge color="bg-blue-50 text-blue-700">
                          {EMP_DOC_TYPE_LABELS[doc.type] || doc.type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            setViewingDoc({
                              fileName: doc.fileName,
                              fetchBlob: () => getEmployeeDocumentFile(doc.id),
                            })
                          }
                          className="text-primary hover:underline text-left max-w-[160px] truncate block"
                          title={doc.fileName}
                        >
                          {doc.fileName}
                        </button>
                      </td>
                      <td
                        className="px-4 py-3 text-gray-500 italic text-xs max-w-[140px] truncate"
                        title={doc.notes || ""}
                      >
                        {doc.notes || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {fmtDate(doc.uploadedAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDownloadEmp(doc)}
                          className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg"
                          title="Télécharger"
                        >
                          <Download size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* DocViewer */}
      {viewingDoc && (
        <HRDocViewer
          fileName={viewingDoc.fileName}
          mimeType={viewingDoc.mimeType}
          fetchBlob={viewingDoc.fetchBlob}
          onClose={() => setViewingDoc(null)}
        />
      )}

      {/* Modal upload */}
      {showUpload && (
        <Modal
          title="Ajouter un document RH"
          onClose={() => setShowUpload(false)}
        >
          <div className="space-y-4">
            <FormField label="Nom du document" required>
              <input
                className={inputCls}
                value={uploadForm.name}
                onChange={(e) =>
                  setUploadForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Ex : Convention collective 2024"
                autoFocus
              />
            </FormField>
            <FormField label="Catégorie">
              <select
                className={inputCls}
                value={uploadForm.category}
                onChange={(e) =>
                  setUploadForm((f) => ({ ...f, category: e.target.value }))
                }
              >
                <option value="">— Catégorie —</option>
                {Object.entries(HR_DOC_CATEGORIES).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Notes">
              <input
                className={inputCls}
                value={uploadForm.notes}
                onChange={(e) =>
                  setUploadForm((f) => ({ ...f, notes: e.target.value }))
                }
                placeholder="Version, date d'application…"
              />
            </FormField>
            <FormField label="Fichier (PDF, JPG, PNG)" required>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className={
                  inputCls +
                  " py-1.5 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                }
              />
            </FormField>
            <div className="flex gap-2 justify-end pt-2">
              <Btn variant="secondary" onClick={() => setShowUpload(false)}>
                Annuler
              </Btn>
              <Btn
                onClick={handleUpload}
                loading={saving}
                disabled={!uploadFile || !uploadForm.name.trim()}
              >
                <Upload size={14} /> Téléverser
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirm suppression */}
      <ConfirmModal
        open={!!deleteConfirm}
        title="Supprimer le document"
        message={`« ${deleteConfirm?.name} » et ses données indexées seront supprimés définitivement.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
        danger
      />

      {/* ── Vue Paie & Déclarations ── */}
      {viewMode === "payroll" && <HRPayrollTab />}
    </div>
  );
};

// ─── Vue transversale formations ─────────────────────────────────────────────

const RECYCLING_STATUS_CONFIG = {
  ok:         { label: "Réalisée",      cls: "bg-green-100 text-green-700"   },
  soon:       { label: "Expire bientôt", cls: "bg-yellow-100 text-yellow-700" },
  expired:    { label: "Expiré",        cls: "bg-red-100 text-red-700"       },
  none:       { label: "Réalisée",      cls: "bg-green-100 text-green-700"   },
  superseded: { label: "Recyclé",       cls: "bg-gray-100 text-gray-400"     },
  planned:    { label: "Planifié",      cls: "bg-blue-100 text-blue-700"     },
};

const RECYCLING_STATUS_OPTS = [
  { value: "planned",    label: "Planifiées",    cls: "bg-blue-100 text-blue-700"    },
  { value: "expired",    label: "Expirées",      cls: "bg-red-100 text-red-700"      },
  { value: "soon",       label: "Expire bientôt", cls: "bg-yellow-100 text-yellow-700" },
  { value: "ok",         label: "Réalisée",      cls: "bg-green-100 text-green-700"  },
  { value: "superseded", label: "Recyclées",     cls: "bg-gray-100 text-gray-400"    },
];

const FormationsAllTab = ({ showAllStructures = false }) => {
  const { currentStructure } = useStructure();
  const toast = useToast();

  const [view, setView] = useState("list"); // "list" | "dashboard"
  const [trainings, setTrainings] = useState([]);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(["expired", "soon", "ok"]);
  const [typeFilter, setTypeFilter] = useState("");
  const [sortBy, setSortBy] = useState("recyclingDate");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [pendingEditTrainingId, setPendingEditTrainingId] = useState(null);

  const structureId = showAllStructures ? "all" : currentStructure?.id || "all";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { structureId, sort: sortBy };
      if (statusFilter.length > 0) params.status = statusFilter;
      if (typeFilter) params.trainingTypeId = typeFilter;
      const [trainRes, typesRes] = await Promise.all([
        getAllTrainings(params),
        getTrainingTypes(),
      ]);
      setTrainings(trainRes.data);
      setTrainingTypes(typesRes.data);
    } catch {
      toast.error("Erreur chargement formations");
    }
    setLoading(false);
  }, [structureId, statusFilter, sortBy, typeFilter, showAllStructures]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = (val) => {
    setStatusFilter((prev) =>
      prev.includes(val) ? prev.filter((s) => s !== val) : [...prev, val],
    );
  };

  if (selectedEmployee) {
    return (
      <EmployeeDetailPage
        employeeId={selectedEmployee}
        initialTab="formations"
        allowedTabs={["formations"]}
        initialFormationId={pendingEditTrainingId}
        onBack={() => { setSelectedEmployee(null); setPendingEditTrainingId(null); }}
      />
    );
  }

  const isMulti = showAllStructures || !currentStructure?.id;

  return (
    <div className="space-y-4">
      {/* Toggle liste / dashboard */}
      <div className="flex gap-2">
        <button
          onClick={() => setView("list")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
            view === "list"
              ? "bg-primary text-white border-primary"
              : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
          }`}
        >
          <List size={14} />
          Liste
        </button>
        <button
          onClick={() => setView("dashboard")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
            view === "dashboard"
              ? "bg-primary text-white border-primary"
              : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
          }`}
        >
          <BarChart2 size={14} />
          Dashboard
        </button>
      </div>

      {view === "dashboard" && (
        <FormationsDashboardTab
          showAllStructures={showAllStructures}
          onNavigateToEmployee={(employeeId, trainingId = null) => {
            setPendingEditTrainingId(trainingId);
            setView("list");
            setSelectedEmployee(employeeId);
          }}
        />
      )}

      {view === "list" && <>
      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-4 items-end">
        {/* Statut — boutons toggle */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">
            Statut formation
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {RECYCLING_STATUS_OPTS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => toggleStatus(opt.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                  statusFilter.includes(opt.value)
                    ? opt.cls + " border-transparent"
                    : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Type */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Type</div>
          <select
            className={inputCls + " py-1 text-sm"}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Tous les types</option>
            {trainingTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tri */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">
            Trier par
          </div>
          <select
            className={inputCls + " py-1 text-sm"}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="recyclingDate">Date de recyclage</option>
            <option value="lastName">Nom employé</option>
          </select>
        </div>

        <div className="ml-auto text-xs text-gray-400 self-center">
          {!loading &&
            `${trainings.length} formation${trainings.length > 1 ? "s" : ""}`}
        </div>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader className="animate-spin text-primary" size={24} />
        </div>
      ) : trainings.length === 0 ? (
        <div className="text-center py-14 text-sm text-gray-400 bg-white rounded-xl border border-gray-200">
          <GraduationCap size={32} className="mx-auto mb-3 opacity-25" />
          Aucune formation ne correspond aux filtres sélectionnés.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Employé
                </th>
                {isMulti && (
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                    Structure
                  </th>
                )}
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Type
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Intitulé
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Nature
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Durée (h)
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Date fin
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Recyclage
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Statut
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Attestation
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {trainings.map((t) => {
                const eff = t.effectiveStatus || t.recyclingStatus;
                const statusCfg = RECYCLING_STATUS_OPTS.find(
                  (o) => o.value === eff,
                ) || RECYCLING_STATUS_CONFIG[eff] || { label: eff, cls: "bg-gray-100 text-gray-500" };
                const isSuperseded = eff === "superseded";
                return (
                  <tr
                    key={t.id}
                    className={`hover:bg-gray-50 cursor-pointer ${isSuperseded ? "opacity-50" : ""}`}
                    onClick={() => setSelectedEmployee(t.employee.id)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {t.employee.lastName} {t.employee.firstName}
                    </td>
                    {isMulti && (
                      <td className="px-4 py-3 text-gray-500">
                        {t.employee.structureName || "—"}
                      </td>
                    )}
                    <td className="px-4 py-3 text-gray-500">
                      {t.trainingType?.name || (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{t.title}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {t.nature || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-right">
                      {t.durationHours != null ? `${t.durationHours} h` : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {t.endDate ? (
                        fmtDate(t.endDate)
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {t.recyclingDate ? (
                        <span
                          className={
                            eff === "expired"
                              ? "text-red-600 font-medium"
                              : eff === "soon"
                                ? "text-yellow-700 font-medium"
                                : "text-gray-600"
                          }
                        >
                          {fmtDate(t.recyclingDate)}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge color={statusCfg.cls}>{statusCfg.label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {t.hasAttestation ? (
                        <Check size={14} className="text-green-500 mx-auto" />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>}
    </div>
  );
};

// ─── Vue transversale entretiens ─────────────────────────────────────────────

const INTERVIEW_STATUS_OPTS = [
  { value: "planned", label: "Planifié", cls: "bg-blue-100 text-blue-700" },
  {
    value: "in_progress",
    label: "En cours",
    cls: "bg-yellow-100 text-yellow-700",
  },
  { value: "completed", label: "Terminé", cls: "bg-green-100 text-green-700" },
];

const EntretiensAllTab = ({ showAllStructures = false }) => {
  const { currentStructure } = useStructure();
  const toast = useToast();

  const [interviews, setInterviews] = useState([]);
  const [interviewTypes, setInterviewTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("planned");
  const [typeFilter, setTypeFilter] = useState("");
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const structureId = showAllStructures ? undefined : currentStructure?.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (structureId) params.structureId = structureId;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.typeId = typeFilter;
      if (upcomingOnly) params.upcoming = "true";
      const [itvsRes, typesRes] = await Promise.all([
        getAllEmployeeInterviews(params),
        getEmployeeInterviewTypes(),
      ]);
      setInterviews(itvsRes.data);
      setInterviewTypes(typesRes.data);
    } catch {
      toast.error("Erreur chargement entretiens");
    }
    setLoading(false);
  }, [structureId, statusFilter, typeFilter, upcomingOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  if (selectedEmployee) {
    return (
      <EmployeeDetailPage
        employeeId={selectedEmployee}
        initialTab="entretiens"
        allowedTabs={["entretiens"]}
        onBack={() => setSelectedEmployee(null)}
      />
    );
  }

  const isMulti = showAllStructures || !currentStructure?.id;

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-4 items-end">
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Statut</div>
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setStatusFilter("")}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                statusFilter === ""
                  ? "bg-gray-200 text-gray-700 border-transparent"
                  : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"
              }`}
            >
              Tous
            </button>
            {INTERVIEW_STATUS_OPTS.map((opt) => (
              <button
                key={opt.value}
                onClick={() =>
                  setStatusFilter(opt.value === statusFilter ? "" : opt.value)
                }
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                  statusFilter === opt.value
                    ? opt.cls + " border-transparent"
                    : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Type</div>
          <select
            className={inputCls + " py-1 text-sm"}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Tous les types</option>
            {interviewTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="upcomingOnly"
            checked={upcomingOnly}
            onChange={(e) => setUpcomingOnly(e.target.checked)}
            className="accent-primary"
          />
          <label
            htmlFor="upcomingOnly"
            className="text-sm text-gray-600 cursor-pointer"
          >
            30 prochains jours
          </label>
        </div>

        <div className="ml-auto text-xs text-gray-400 self-center">
          {!loading &&
            `${interviews.length} entretien${interviews.length > 1 ? "s" : ""}`}
        </div>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader className="animate-spin text-primary" size={24} />
        </div>
      ) : interviews.length === 0 ? (
        <div className="text-center py-14 text-sm text-gray-400 bg-white rounded-xl border border-gray-200">
          <MessageSquare size={32} className="mx-auto mb-3 opacity-25" />
          Aucun entretien ne correspond aux filtres sélectionnés.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Employé
                </th>
                {isMulti && (
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                    Structure
                  </th>
                )}
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Type
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Date prévue
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Intervieweur
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">
                  Statut
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {interviews.map((itv) => {
                const statusCfg = INTERVIEW_STATUS_OPTS.find(
                  (o) => o.value === itv.status,
                ) || {
                  label: itv.status,
                  cls: "bg-gray-100 text-gray-500",
                };
                return (
                  <tr
                    key={itv.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedEmployee(itv.employee.id)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {itv.employee.lastName} {itv.employee.firstName}
                    </td>
                    {isMulti && (
                      <td className="px-4 py-3 text-gray-500">
                        {itv.employee.structure?.name || "—"}
                      </td>
                    )}
                    <td className="px-4 py-3 text-gray-500">
                      {itv.type?.name || (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {fmtDate(itv.scheduledDate)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {itv.interviewerName}
                    </td>
                    <td className="px-4 py-3">
                      <Badge color={statusCfg.cls}>{statusCfg.label}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Vue transversale visites médicales ─────────────────────────────────────

const VISIT_STATUS_OPTS_ALL = [
  { value: "depasse", label: "Dépassé", cls: "bg-red-100 text-red-700" },
  { value: "bientot", label: "Expire bientôt", cls: "bg-orange-100 text-orange-700" },
  {
    value: "attente_cr",
    label: "Attente CR",
    cls: "bg-yellow-100 text-yellow-700",
  },
  { value: "realise", label: "Réalisé", cls: "bg-green-100 text-green-700" },
  { value: "convoque", label: "Convoqué", cls: "bg-blue-100 text-blue-700" },
  {
    value: "non_renseigne",
    label: "Non renseigné",
    cls: "bg-gray-100 text-gray-600",
  },
];

const MedicalVisitsAllTab = ({
  showAllStructures = false,
  initialStatut = "",
}) => {
  const { currentStructure } = useStructure();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statutFilter, setStatutFilter] = useState(initialStatut);
  const [typeFilter, setTypeFilter] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const structureId = showAllStructures ? "all" : currentStructure?.id;

  const isMulti = showAllStructures || !currentStructure?.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (structureId) params.structureId = structureId;
      if (statutFilter) params.statut = statutFilter;
      if (typeFilter) params.typeId = typeFilter;
      const [rowsRes, typesRes] = await Promise.all([
        getAllMedicalVisits(params),
        getMedicalVisitTypes(),
      ]);
      setRows(rowsRes.data);
      setVisitTypes(typesRes.data);
    } catch {
      toast.error("Erreur chargement visites médicales");
    }
    setLoading(false);
  }, [structureId, statutFilter, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  if (selectedEmployee) {
    return (
      <EmployeeDetailPage
        employeeId={selectedEmployee}
        initialTab="visites-medicales"
        allowedTabs={["visites-medicales"]}
        onBack={() => setSelectedEmployee(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-4 items-end">
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Statut</div>
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setStatutFilter("")}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                statutFilter === ""
                  ? "bg-gray-200 text-gray-700 border-transparent"
                  : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"
              }`}
            >
              Tous
            </button>
            {VISIT_STATUS_OPTS_ALL.map((opt) => (
              <button
                key={opt.value}
                onClick={() =>
                  setStatutFilter(opt.value === statutFilter ? "" : opt.value)
                }
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                  statutFilter === opt.value
                    ? opt.cls + " border-transparent"
                    : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Type</div>
          <select
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 focus:ring-2 focus:ring-primary focus:border-transparent"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Tous les types</option>
            {visitTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={load}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw size={13} /> Actualiser
        </button>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader className="animate-spin text-primary" size={24} />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-400">
          Aucune visite médicale
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">
                  Employé
                </th>
                {isMulti && (
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">
                    Structure
                  </th>
                )}
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">
                  Type
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">
                  Dernière visite
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">
                  Prochaine visite
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">
                  Statut
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">
                  CR
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, idx) => {
                const statusCfg = VISIT_STATUS_OPTS_ALL.find(
                  (o) => o.value === row.status,
                ) || { label: row.status, cls: "bg-gray-100 text-gray-600" };
                return (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {row.firstName} {row.lastName}
                    </td>
                    {isMulti && (
                      <td className="px-4 py-3 text-gray-500">
                        {row.structureName || "—"}
                      </td>
                    )}
                    <td className="px-4 py-3 text-gray-600">
                      {row.visit?.type?.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {row.visit?.visitDate
                        ? new Date(row.visit.visitDate).toLocaleDateString(
                            "fr-FR",
                          )
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {row.visit?.nextVisitDate
                        ? new Date(row.visit.nextVisitDate).toLocaleDateString(
                            "fr-FR",
                          )
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.cls}`}
                      >
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {row.visit?.hasCr ? (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle size={12} /> Disponible
                        </span>
                      ) : row.visit ? (
                        <span className="text-xs text-gray-400">Non</span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedEmployee(row.employeeId)}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <ArrowRight size={12} /> Voir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────

// Onglets visibles sur la page "Employés"
// ─── Onglet Analytique RH ────────────────────────────────────────────────────

const ANALYTICS_CHART_COLORS = [
  "#14b8a6",
  "#fbbf24",
  "#60a5fa",
  "#f87171",
  "#a78bfa",
  "#34d399",
  "#fb923c",
  "#38bdf8",
];

function formatSeniorityLabel(months) {
  if (months === null || months === undefined) return "—";
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (years === 0) return `${rem} mois`;
  if (rem === 0) return `${years} an${years > 1 ? "s" : ""}`;
  return `${years} an${years > 1 ? "s" : ""} ${rem} mois`;
}

const AnalytiqueTab = ({ showAllStructures = false }) => {
  const { currentStructure } = useStructure();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const structureId = showAllStructures ? "all" : currentStructure?.id;

  useEffect(() => {
    setLoading(true);
    getHRAnalytics({ structureId })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Erreur chargement analytique RH"))
      .finally(() => setLoading(false));
  }, [structureId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400 gap-2">
        <Loader size={18} className="animate-spin" />
        <span>Chargement des données analytiques…</span>
      </div>
    );
  }

  if (!data) return null;

  const { composition, age, seniority, topPositions, hrCosts } = data;

  // Données donut contrats
  const contractData = composition.byContractType.map((d, i) => ({
    ...d,
    fill: ANALYTICS_CHART_COLORS[i % ANALYTICS_CHART_COLORS.length],
  }));

  // Données donut actifs/inactifs
  const statusData = [
    { name: "Actifs", value: composition.activeCount, fill: "#14b8a6" },
    { name: "Inactifs", value: composition.inactiveCount, fill: "#f87171" },
  ];

  // Données LineChart charges — une série par catégorie
  const costMonths = hrCosts.months;
  const costCategories =
    costMonths.length > 0 ? Object.keys(costMonths[0].byCategory) : [];
  const costChartData = costMonths.map((m) => ({
    month: m.month,
    ...m.byCategory,
  }));

  return (
    <div className="space-y-8">
      {/* ── Section 1 : Composition ────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <Users size={16} className="text-primary" />
          Composition des effectifs
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Donut contrats */}
          <div className="flex flex-col items-center gap-2">
            <p className="text-sm font-medium text-gray-600">
              Par type de contrat
            </p>
            {contractData.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-8">Aucune donnée</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={contractData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="count"
                      nameKey="type"
                    >
                      {contractData.map((d, i) => (
                        <Cell key={i} fill={d.fill} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-2">
                  {contractData.map((d, i) => (
                    <span
                      key={i}
                      className="flex items-center gap-1 text-xs text-gray-600"
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full inline-block"
                        style={{ background: d.fill }}
                      />
                      {d.type} ({d.count})
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Donut actifs/inactifs */}
          <div className="flex flex-col items-center gap-2">
            <p className="text-sm font-medium text-gray-600">
              Actifs / Inactifs
            </p>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  nameKey="name"
                >
                  {statusData.map((d, i) => (
                    <Cell key={i} fill={d.fill} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex gap-4">
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <span className="w-2.5 h-2.5 rounded-full bg-primary inline-block" />
                Actifs ({composition.activeCount})
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" />
                Inactifs ({composition.inactiveCount})
              </span>
            </div>
          </div>

          {/* Top 5 postes */}
          <div>
            <p className="text-sm font-medium text-gray-600 mb-3">
              Top 5 postes
            </p>
            {topPositions.length === 0 ? (
              <p className="text-sm text-gray-400 italic">
                Aucun poste renseigné.
              </p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {topPositions.map((p, i) => (
                    <tr
                      key={i}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <td className="py-1.5 text-gray-700">{p.position}</td>
                      <td className="py-1.5 text-right font-medium text-primary">
                        {p.count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* ── Section 2 : Pyramide des âges ─────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Users size={16} className="text-primary" />
          Pyramide des âges
        </h2>

        {age.coveredCount === 0 ? (
          <p className="text-sm text-gray-400 italic py-6">
            Aucun employé avec date de naissance renseignée. Compléter via la
            fiche employé.
          </p>
        ) : (
          <>
            {age.coveredCount / age.totalActive < 0.5 && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
                Données partielles : {age.coveredCount}/{age.totalActive}{" "}
                employés avec date de naissance
              </p>
            )}
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={age.brackets}
                  layout="vertical"
                  margin={{ left: 16, right: 24, top: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={80}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    name="Employés"
                    fill="#14b8a6"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex-shrink-0 space-y-2 min-w-[120px]">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">
                    {age.median ?? "—"}
                  </p>
                  <p className="text-xs text-gray-500">ans (médiane)</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-semibold text-gray-700">
                    {age.average ?? "—"}
                  </p>
                  <p className="text-xs text-gray-500">ans (moyenne)</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Section 3 : Ancienneté ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Clock size={16} className="text-primary" />
          Ancienneté
        </h2>

        {seniority.brackets.every((b) => b.count === 0) ? (
          <p className="text-sm text-gray-400 italic py-6">
            Aucune date d'embauche renseignée.
          </p>
        ) : (
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={seniority.brackets}
                layout="vertical"
                margin={{ left: 16, right: 24, top: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={80}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip />
                <Bar
                  dataKey="count"
                  name="Employés"
                  fill="#fbbf24"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex-shrink-0 space-y-2 min-w-[140px]">
              <div className="text-center">
                <p className="text-lg font-bold text-primary">
                  {formatSeniorityLabel(seniority.medianMonths)}
                </p>
                <p className="text-xs text-gray-500">médiane</p>
              </div>
              <div className="text-center">
                <p className="text-base font-semibold text-gray-700">
                  {formatSeniorityLabel(seniority.averageMonths)}
                </p>
                <p className="text-xs text-gray-500">moyenne</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Section 4 : Évolution charges RH ──────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp size={16} className="text-primary" />
          Évolution des charges RH — 12 derniers mois
        </h2>

        {costMonths.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-6">
            Aucune charge saisie sur les 12 derniers mois.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={costChartData}
              margin={{ left: 8, right: 8, top: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name) => [
                  `${value.toLocaleString("fr-FR")} €`,
                  COST_CATEGORY_LABELS[name] || name,
                ]}
              />
              <Legend
                formatter={(name) => COST_CATEGORY_LABELS[name] || name}
              />
              {costCategories.map((cat, i) => (
                <Line
                  key={cat}
                  type="monotone"
                  dataKey={cat}
                  stroke={
                    ANALYTICS_CHART_COLORS[i % ANALYTICS_CHART_COLORS.length]
                  }
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

// ─── Dossiers RH (Formations + Documents + Visites médicales) ────────────────

const DOSSIERS_SUB_TABS = [
  {
    id: "formations",
    label: "Formations",
    icon: GraduationCap,
    module: "hr_formations",
  },
  {
    id: "documents",
    label: "Documents",
    icon: FileText,
    module: "hr_documents",
  },
  {
    id: "visites-medicales",
    label: "Visites médicales",
    icon: Stethoscope,
    module: "hr_visites_medicales",
  },
  {
    id: "periodes-essai",
    label: "Périodes d'essai",
    icon: Timer,
    module: "hr_periodes_essai",
  },
];

const TRIAL_STATUS_CFG_ALL = {
  en_cours:  { label: "En cours",  cls: "bg-blue-100 text-blue-700" },
  bientot:   { label: "Bientôt",   cls: "bg-orange-100 text-orange-700" },
  expiree:   { label: "Expirée",   cls: "bg-red-100 text-red-700" },
  confirmee: { label: "Confirmée", cls: "bg-green-100 text-green-700" },
  rompue:    { label: "Rompue",    cls: "bg-gray-100 text-gray-600" },
};

const TrialPeriodsAllTab = ({ showAllStructures = false, onNavigateToEmployee }) => {
  const { currentStructure } = useStructure();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState("actives");

  const structureId = showAllStructures ? "all" : currentStructure?.id || undefined;

  useEffect(() => {
    setLoading(true);
    getTrialPeriods(structureId ? { structureId } : {})
      .then(res => setRows(res.data))
      .catch(() => toast.error("Erreur chargement périodes d'essai"))
      .finally(() => setLoading(false));
  }, [structureId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = rows.filter(tp => {
    if (filterStatut === "actives") return ["en_cours", "bientot", "expiree"].includes(tp.computedStatus);
    if (filterStatut === "toutes") return true;
    return tp.computedStatus === filterStatut;
  });

  const FILTERS = [
    { key: "actives", label: "Actives" },
    { key: "expiree", label: "Expirées" },
    { key: "bientot", label: "Bientôt" },
    { key: "confirmee", label: "Confirmées" },
    { key: "rompue", label: "Rompues" },
    { key: "toutes", label: "Toutes" },
  ];

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilterStatut(f.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
              filterStatut === f.key
                ? "bg-primary text-white border-primary"
                : "bg-white text-gray-600 border-gray-200 hover:border-primary hover:text-primary"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader className="animate-spin text-primary" size={24} /></div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400">Aucune période d'essai correspondante.</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Employé</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Poste</th>
                {showAllStructures && <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Structure</th>}
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Début</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Fin</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Statut</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(tp => {
                const cfg = TRIAL_STATUS_CFG_ALL[tp.computedStatus] || { label: tp.computedStatus, cls: "bg-gray-100 text-gray-600" };
                const effectiveEnd = tp.renewedEndDate ?? tp.endDate;
                return (
                  <tr key={tp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {tp.employee.firstName} {tp.employee.lastName}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{tp.employee.position || "—"}</td>
                    {showAllStructures && <td className="px-4 py-3 text-gray-500">{tp.employee.structure?.name || "—"}</td>}
                    <td className="px-4 py-3 text-gray-600">{fmtDate(tp.startDate)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtDate(effectiveEnd)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Btn variant="ghost" size="sm" onClick={() => onNavigateToEmployee?.(tp.employee.id, "periode-essai")}>
                        Voir
                      </Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const DossiersAllTab = ({
  showAllStructures = false,
  initialSubTab = "formations",
}) => {
  const { effectiveUser } = useImpersonate();
  const isAdmin = effectiveUser?.role === "admin";
  const userModules = Array.isArray(effectiveUser?.modules)
    ? effectiveUser.modules
    : [];

  const visibleSubTabs = isAdmin
    ? DOSSIERS_SUB_TABS
    : DOSSIERS_SUB_TABS.filter((t) => userModules.includes(t.module));

  const firstVisible = visibleSubTabs[0]?.id ?? initialSubTab;
  const [subTab, setSubTab] = useState(
    visibleSubTabs.some((t) => t.id === initialSubTab)
      ? initialSubTab
      : firstVisible,
  );

  if (visibleSubTabs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400">
        <Archive size={40} className="mb-3 opacity-40" />
        <p className="text-sm">Aucun dossier autorisé pour ce compte.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {visibleSubTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                subTab === t.id
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon size={15} />
              {t.label}
            </button>
          );
        })}
      </div>
      {subTab === "formations" && (
        <FormationsAllTab showAllStructures={showAllStructures} />
      )}
      {subTab === "documents" && (
        <DocumentsTab showAllStructures={showAllStructures} />
      )}
      {subTab === "visites-medicales" && (
        <MedicalVisitsAllTab showAllStructures={showAllStructures} />
      )}
      {subTab === "periodes-essai" && (
        <TrialPeriodsAllTab showAllStructures={showAllStructures} />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const DASH_FORM_COLORS = ['#14b8a6', '#fbbf24', '#6366f1', '#f97316', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6'];
const STATUT_COLORS = { ok: '#16a34a', soon: '#d97706', expired: '#dc2626', none: '#9ca3af', superseded: '#6b7280', planned: '#3b82f6' };
const STATUT_LABELS = { ok: 'Réalisée', soon: 'Expire bientôt', expired: 'Expiré', none: 'Réalisée', superseded: 'Recyclé', planned: 'Planifié' };

const FormationsDashboardTab = ({ showAllStructures = false, onNavigateToEmployee }) => {
  const { currentStructure } = useStructure();
  const toast = useToast();
  const TODAY = useMemo(() => new Date(), []);

  const [viewingDashTraining, setViewingDashTraining] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fStructure, setFStructure] = useState('');
  const [fSalarie, setFSalarie]   = useState('');
  const [fNature, setFNature]     = useState('');
  const [fObligatoire, setFObligatoire] = useState('');
  const [fOrganisme, setFOrganisme]     = useState('');
  const [fStatut, setFStatut]           = useState('');
  const [fSearch, setFSearch]           = useState('');
  const [tableTab, setTableTab]         = useState('echeances');
  const [sortKey, setSortKey]           = useState('startDate');
  const [sortDir, setSortDir]           = useState(-1);

  const structureId = showAllStructures ? 'all' : currentStructure?.id || 'all';
  const isMulti     = showAllStructures || !currentStructure?.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllTrainings({ structureId });
      setTrainings(res.data);
    } catch {
      toast.error('Erreur chargement formations');
    }
    setLoading(false);
  }, [structureId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // ── Options de filtres ────────────────────────────────────────────────────
  const structures = useMemo(() =>
    [...new Set(trainings.map(t => t.employee.structureName).filter(Boolean))].sort(), [trainings]);
  const salaries = useMemo(() =>
    [...new Set(trainings.map(t => `${t.employee.firstName} ${t.employee.lastName}`))].sort(), [trainings]);
  const natures = useMemo(() =>
    [...new Set(trainings.map(t => t.nature).filter(Boolean))].sort(), [trainings]);
  const organismes = useMemo(() =>
    [...new Set(trainings.map(t => t.organism).filter(Boolean))].sort(), [trainings]);

  // ── Données filtrées ──────────────────────────────────────────────────────
  const filtered = useMemo(() => trainings.filter(t => {
    const nom = `${t.employee.firstName} ${t.employee.lastName}`;
    if (fStructure   && t.employee.structureName !== fStructure) return false;
    if (fSalarie     && nom !== fSalarie) return false;
    if (fNature      && t.nature !== fNature) return false;
    if (fObligatoire === 'Oui' && !t.mandatory) return false;
    if (fObligatoire === 'Non' && t.mandatory)  return false;
    if (fOrganisme   && t.organism !== fOrganisme) return false;
    if (fStatut && !(t.effectiveStatus === fStatut || (fStatut === 'ok' && t.effectiveStatus === 'none'))) return false;
    if (fSearch) {
      const q = fSearch.toLowerCase();
      if (![t.title, t.organism, nom].filter(Boolean).join(' ').toLowerCase().includes(q)) return false;
    }
    return true;
  }), [trainings, fStructure, fSalarie, fNature, fObligatoire, fOrganisme, fStatut, fSearch]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const heures = filtered.reduce((s, t) => s + (parseFloat(t.durationHours) || 0), 0);
    const withEch = filtered.filter(t => t.recyclingDate);
    const depasses = withEch.filter(t => new Date(t.recyclingDate) < TODAY).length;
    const bientot  = withEch.filter(t => {
      const diff = Math.round((new Date(t.recyclingDate) - TODAY) / 86400000);
      return diff >= 0 && diff < 60;
    }).length;
    return {
      total:        filtered.length,
      salariesCount: new Set(filtered.map(t => t.employee.id)).size,
      heures,
      obligatoires: filtered.filter(t => t.mandatory).length,
      ajour:        filtered.filter(t => t.effectiveStatus === 'ok' || t.effectiveStatus === 'none').length,
      expired:      filtered.filter(t => t.effectiveStatus === 'expired').length,
      planned:      filtered.filter(t => t.effectiveStatus === 'planned').length,
      bientot,
      depasses,
    };
  }, [filtered, TODAY]);

  // ── Données graphiques ────────────────────────────────────────────────────
  const statutChartData = useMemo(() => {
    const c = {};
    filtered.forEach(t => { const k = t.effectiveStatus || 'none'; c[k] = (c[k] || 0) + 1; });
    return Object.entries(c).map(([k, v]) => ({ name: STATUT_LABELS[k] || k, value: v, fill: STATUT_COLORS[k] || '#9ca3af' }));
  }, [filtered]);

  const structureChartData = useMemo(() => {
    const c = {};
    filtered.forEach(t => { const k = t.employee.structureName || '—'; c[k] = (c[k] || 0) + 1; });
    return Object.entries(c).map(([k, v], i) => ({ name: k, value: v, fill: DASH_FORM_COLORS[i % DASH_FORM_COLORS.length] }));
  }, [filtered]);

  const natureChartData = useMemo(() => {
    const c = {};
    filtered.forEach(t => { const k = t.nature || 'Non renseigné'; c[k] = (c[k] || 0) + 1; });
    return Object.entries(c).map(([k, v], i) => ({ name: k, value: v, fill: DASH_FORM_COLORS[i % DASH_FORM_COLORS.length] }));
  }, [filtered]);

  const organismeChartData = useMemo(() => {
    const c = {};
    filtered.forEach(t => { const k = t.organism || '—'; c[k] = (c[k] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [filtered]);

  const timelineChartData = useMemo(() => {
    const months = {};
    filtered.forEach(t => {
      if (!t.startDate) return;
      const m = t.startDate.slice(0, 7);
      if (!months[m]) months[m] = { month: m, 'Planifié': 0, 'Réalisée': 0, 'Expire bientôt': 0, 'Expiré': 0, 'Recyclé': 0 };
      const label = STATUT_LABELS[t.effectiveStatus || 'none'];
      months[m][label] = (months[m][label] || 0) + 1;
    });
    return Object.values(months).sort((a, b) => a.month.localeCompare(b.month));
  }, [filtered]);

  // ── Table échéances ───────────────────────────────────────────────────────
  const echeancesData = useMemo(() => filtered
    .filter(t => t.recyclingDate)
    .map(t => ({ ...t, _diff: Math.round((new Date(t.recyclingDate) - TODAY) / 86400000) }))
    .sort((a, b) => a._diff - b._diff),
  [filtered, TODAY]);

  // ── Récap par salarié ─────────────────────────────────────────────────────
  const bySalarieData = useMemo(() => {
    const map = {};
    filtered.forEach(t => {
      const k = t.employee.id;
      if (!map[k]) map[k] = {
        name: `${t.employee.firstName} ${t.employee.lastName}`,
        structure: t.employee.structureName || '—',
        total: 0, heures: 0, depasses: 0, bientot: 0,
      };
      if (!map[k].id) map[k].id = k;
      map[k].total++;
      map[k].heures += parseFloat(t.durationHours) || 0;
      if (t.recyclingDate) {
        const diff = Math.round((new Date(t.recyclingDate) - TODAY) / 86400000);
        if (diff < 0) map[k].depasses++;
        else if (diff < 60) map[k].bientot++;
      }
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filtered, TODAY]);

  // ── Tableau "toutes" trié ─────────────────────────────────────────────────
  const allSorted = useMemo(() => {
    const KEY = { date: 'startDate', intitule: 'title', nature: 'nature', organisme: 'organism', statut: 'effectiveStatus', duree: 'durationHours', echeance: 'recyclingDate' };
    return [...filtered].sort((a, b) => {
      let va, vb;
      if (sortKey === 'salarie') {
        va = `${a.employee.lastName} ${a.employee.firstName}`;
        vb = `${b.employee.lastName} ${b.employee.firstName}`;
      } else if (sortKey === 'structure') {
        va = a.employee.structureName || '';
        vb = b.employee.structureName || '';
      } else {
        const k = KEY[sortKey] || sortKey;
        va = a[k]; vb = b[k];
      }
      if (va == null) return 1; if (vb == null) return -1;
      return va < vb ? sortDir : va > vb ? -sortDir : 0;
    });
  }, [filtered, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => -d);
    else { setSortKey(key); setSortDir(-1); }
  };

  const echInfo = (recyclingDate) => {
    if (!recyclingDate) return null;
    const diff = Math.round((new Date(recyclingDate) - TODAY) / 86400000);
    if (diff < 0)  return { cls: 'bg-red-100 text-red-700',    txt: 'text-red-600 font-semibold',    label: 'Dépassée',   diff };
    if (diff < 60) return { cls: 'bg-yellow-100 text-yellow-700', txt: 'text-yellow-600 font-semibold', label: '< 60 jours', diff };
    return              { cls: 'bg-green-100 text-green-700',  txt: 'text-green-600',                label: 'Réalisée',     diff };
  };

  const PieDonut = ({ data }) => (
    <>
      <ResponsiveContainer width="100%" height={190}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" nameKey="name">
            {data.map((d, i) => <Cell key={i} fill={d.fill || DASH_FORM_COLORS[i % DASH_FORM_COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [v, n]} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 justify-center">
        {data.map((d, i) => (
          <span key={i} className="flex items-center gap-1 text-xs text-gray-600">
            <span className="w-2 h-2 rounded-full" style={{ background: d.fill || DASH_FORM_COLORS[i % DASH_FORM_COLORS.length] }} />
            {d.name} ({d.value})
          </span>
        ))}
      </div>
    </>
  );

  const thCls = (key) =>
    `px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100 select-none ${sortKey === key ? 'text-primary' : ''}`;

  const resetFilters = () => {
    setFStructure(''); setFSalarie(''); setFNature('');
    setFObligatoire(''); setFOrganisme(''); setFStatut(''); setFSearch('');
  };

  if (loading) return (
    <div className="py-20">
      <LoadingSpinner size="large" text="Chargement..." />
    </div>
  );

  return (
    <div className="space-y-6">

      {/* ── KPIs ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total formations', value: kpis.total,        color: 'text-primary' },
          { label: 'Salariés concernés', value: kpis.salariesCount, color: 'text-primary' },
          { label: 'Heures cumulées', value: `${kpis.heures.toFixed(1)} h`, color: 'text-primary' },
          { label: 'Obligatoires',    value: kpis.obligatoires,  color: 'text-accent-dark' },
          { label: 'Planifiées',      value: kpis.planned,       color: 'text-blue-600' },
          { label: 'Réalisées',       value: kpis.ajour,         color: 'text-green-600' },
          { label: 'Expirées',        value: kpis.expired,       color: 'text-red-600' },
          { label: 'Échéance < 60 j', value: kpis.bientot,       color: 'text-yellow-600' },
          { label: 'Éch. dépassées',  value: kpis.depasses,      color: 'text-red-600' },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{k.label}</p>
            <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* ── Filtres ────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-2">
          {isMulti && (
            <div className="flex-1 min-w-[110px]">
              <label className="text-xs text-gray-500 mb-1 block">Structure</label>
              <select className={inputCls} value={fStructure} onChange={e => setFStructure(e.target.value)}>
                <option value="">Toutes</option>
                {structures.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div className="flex-1 min-w-[110px]">
            <label className="text-xs text-gray-500 mb-1 block">Salarié</label>
            <select className={inputCls} value={fSalarie} onChange={e => setFSalarie(e.target.value)}>
              <option value="">Tous</option>
              {salaries.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="text-xs text-gray-500 mb-1 block">Statut</label>
            <select className={inputCls} value={fStatut} onChange={e => setFStatut(e.target.value)}>
              <option value="">Tous</option>
              {RECYCLING_STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[110px]">
            <label className="text-xs text-gray-500 mb-1 block">Nature</label>
            <select className={inputCls} value={fNature} onChange={e => setFNature(e.target.value)}>
              <option value="">Toutes</option>
              {natures.map(n => <option key={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[100px]">
            <label className="text-xs text-gray-500 mb-1 block">Obligatoire</label>
            <select className={inputCls} value={fObligatoire} onChange={e => setFObligatoire(e.target.value)}>
              <option value="">Tous</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          <div className="flex-1 min-w-[110px]">
            <label className="text-xs text-gray-500 mb-1 block">Organisme</label>
            <select className={inputCls} value={fOrganisme} onChange={e => setFOrganisme(e.target.value)}>
              <option value="">Tous</option>
              {organismes.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="flex-[2] min-w-[150px]">
            <label className="text-xs text-gray-500 mb-1 block">Recherche</label>
            <input type="search" className={inputCls} placeholder="Intitulé, organisme…"
              value={fSearch} onChange={e => setFSearch(e.target.value)} />
          </div>
          <div className="flex-none flex items-end">
            <Btn variant="secondary" size="sm" onClick={resetFilters}>Réinitialiser</Btn>
          </div>
        </div>
      </div>

      {/* ── Graphiques 4 donuts ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { title: 'Répartition par statut',     data: statutChartData },
          { title: 'Répartition par structure',  data: structureChartData },
          { title: 'Répartition par nature',     data: natureChartData },
        ].map(({ title, data }) => (
          <div key={title} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{title}</p>
            {data.length === 0
              ? <p className="text-sm text-gray-400 italic py-8 text-center">Aucune donnée</p>
              : <PieDonut data={data} />}
          </div>
        ))}

        {/* Top organismes — barre horizontale */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Top organismes</p>
          {organismeChartData.length === 0
            ? <p className="text-sm text-gray-400 italic py-8 text-center">Aucune donnée</p>
            : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart layout="vertical" data={organismeChartData}
                  margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
                  <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" name="Formations" fill="#14b8a6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
        </div>
      </div>

      {/* ── Timeline formations par mois ─────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
          Formations par mois (date de réalisation)
        </p>
        {timelineChartData.length === 0
          ? <p className="text-sm text-gray-400 italic py-8 text-center">Aucune donnée</p>
          : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={timelineChartData} margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Planifié"       stackId="a" fill={STATUT_COLORS.planned}    radius={[0,0,0,0]} />
                <Bar dataKey="Réalisée"       stackId="a" fill={STATUT_COLORS.ok}         radius={[0,0,0,0]} />
                <Bar dataKey="Expire bientôt" stackId="a" fill={STATUT_COLORS.soon}        radius={[0,0,0,0]} />
                <Bar dataKey="Expiré"         stackId="a" fill={STATUT_COLORS.expired}     radius={[0,0,0,0]} />
                <Bar dataKey="Recyclé"        stackId="a" fill={STATUT_COLORS.superseded}  radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
      </div>

      {/* ── Onglets tables ──────────────────────────────────────────────────── */}
      <div>
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { id: 'echeances', label: 'Échéances à surveiller' },
            { id: 'bySalarie', label: 'Récap par salarié' },
            { id: 'all',       label: `Toutes les formations (${filtered.length})` },
          ].map(t => (
            <button key={t.id} onClick={() => setTableTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                tableTab === t.id
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Échéances */}
        {tableTab === 'echeances' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Salarié','Structure','Formation','Réalisée le','Validité (mois)','Échéance','Jours restants','Statut'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {echeancesData.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">Aucune formation avec échéance</td></tr>
                  ) : echeancesData.map(t => {
                    const ei = echInfo(t.recyclingDate);
                    const jours = ei.diff < 0 ? `il y a ${-ei.diff} j` : `dans ${ei.diff} j`;
                    return (
                      <tr key={t.id} className="hover:bg-primary/5 cursor-pointer" onClick={() => setViewingDashTraining(t)}>
                        <td className="px-3 py-2.5 font-medium text-gray-800">{t.employee.firstName} {t.employee.lastName}</td>
                        <td className="px-3 py-2.5"><Badge color="bg-gray-100 text-gray-600">{t.employee.structureName || '—'}</Badge></td>
                        <td className="px-3 py-2.5 text-gray-700">{t.title}</td>
                        <td className="px-3 py-2.5 text-gray-600">{fmtDate(t.startDate)}</td>
                        <td className="px-3 py-2.5 text-gray-600 text-center">{t.trainingType?.defaultRecyclingMonths || '—'}</td>
                        <td className={`px-3 py-2.5 ${ei.txt}`}>{fmtDate(t.recyclingDate)}</td>
                        <td className={`px-3 py-2.5 ${ei.txt}`}>{jours}</td>
                        <td className="px-3 py-2.5"><Badge color={ei.cls}>{ei.label}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Récap par salarié */}
        {tableTab === 'bySalarie' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Salarié','Structure','Total','Heures cumulées','Éch. dépassées','Éch. < 60 j'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {bySalarieData.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">Aucun résultat</td></tr>
                  ) : bySalarieData.map(r => (
                    <tr key={r.name} className="hover:bg-primary/5 cursor-pointer" onClick={() => onNavigateToEmployee?.(r.id)}>
                      <td className="px-3 py-2.5 font-medium text-gray-800">{r.name}</td>
                      <td className="px-3 py-2.5"><Badge color="bg-gray-100 text-gray-600">{r.structure}</Badge></td>
                      <td className="px-3 py-2.5 text-gray-700">{r.total}</td>
                      <td className="px-3 py-2.5 text-gray-700">{r.heures.toFixed(1)} h</td>
                      <td className="px-3 py-2.5">{r.depasses > 0 ? <Badge color="bg-red-100 text-red-700">{r.depasses}</Badge> : <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2.5">{r.bientot  > 0 ? <Badge color="bg-yellow-100 text-yellow-700">{r.bientot}</Badge>  : <span className="text-gray-300">—</span>}</td>
                    </tr>
                  ))}
                  {bySalarieData.length > 0 && (
                    <tr className="bg-gray-50 font-semibold text-gray-700">
                      <td className="px-3 py-2.5">TOTAL ({bySalarieData.length} salariés)</td>
                      <td className="px-3 py-2.5">—</td>
                      <td className="px-3 py-2.5">{bySalarieData.reduce((s, r) => s + r.total, 0)}</td>
                      <td className="px-3 py-2.5">{bySalarieData.reduce((s, r) => s + r.heures, 0).toFixed(1)} h</td>
                      <td className="px-3 py-2.5">{bySalarieData.reduce((s, r) => s + r.depasses, 0)}</td>
                      <td className="px-3 py-2.5">{bySalarieData.reduce((s, r) => s + r.bientot, 0)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Toutes les formations */}
        {tableTab === 'all' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {[
                      ['date','Date'],['salarie','Salarié'],['structure','Structure'],
                      ['intitule','Intitulé'],['nature','Nature'],['obligatoire','Oblig.'],
                      ['organisme','Organisme'],['statut','Statut'],['duree','Durée (h)'],['echeance','Échéance'],
                    ].map(([key, label]) => (
                      <th key={key} className={thCls(key)} onClick={() => handleSort(key)}>
                        {label}{sortKey === key ? (sortDir === 1 ? ' ↑' : ' ↓') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {allSorted.length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400 text-sm">Aucun résultat</td></tr>
                  ) : allSorted.map(t => {
                    const ei = echInfo(t.recyclingDate);
                    const sc = RECYCLING_STATUS_OPTS.find(o => o.value === t.effectiveStatus) || RECYCLING_STATUS_CONFIG[t.effectiveStatus];
                    return (
                      <tr key={t.id} className="hover:bg-primary/5 cursor-pointer" onClick={() => setViewingDashTraining(t)}>
                        <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{fmtDate(t.startDate)}</td>
                        <td className="px-3 py-2.5 font-medium text-gray-800 whitespace-nowrap">{t.employee.firstName} {t.employee.lastName}</td>
                        <td className="px-3 py-2.5"><Badge color="bg-gray-100 text-gray-600">{t.employee.structureName || '—'}</Badge></td>
                        <td className="px-3 py-2.5 text-gray-700 max-w-[200px] truncate" title={t.title}>{t.title}</td>
                        <td className="px-3 py-2.5 text-gray-600">{t.nature || <span className="text-gray-300">—</span>}</td>
                        <td className="px-3 py-2.5">
                          {t.mandatory
                            ? <Badge color="bg-red-100 text-red-700">Oui</Badge>
                            : <span className="text-gray-400 text-xs">Non</span>}
                        </td>
                        <td className="px-3 py-2.5 text-gray-600">{t.organism || <span className="text-gray-300">—</span>}</td>
                        <td className="px-3 py-2.5">
                          {sc ? <Badge color={sc.cls}>{sc.label}</Badge> : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-gray-600 text-right">{t.durationHours != null ? `${t.durationHours} h` : <span className="text-gray-300">—</span>}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {ei ? <Badge color={ei.cls}>{fmtDate(t.recyclingDate)}</Badge> : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Modale détail formation (depuis dashboard) ───────────────────── */}
      {viewingDashTraining && (() => {
        const vt = viewingDashTraining;
        const sc = RECYCLING_STATUS_OPTS.find(o => o.value === vt.effectiveStatus) || RECYCLING_STATUS_CONFIG[vt.effectiveStatus];
        const ei = echInfo(vt.recyclingDate);
        return (
          <Modal
            title={
              <div className="flex items-center gap-3">
                <span>Détail de la formation</span>
                <Btn
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setViewingDashTraining(null);
                    onNavigateToEmployee?.(vt.employee.id, vt.id);
                  }}
                >
                  <Edit size={13} className="mr-1" />
                  Modifier
                </Btn>
              </div>
            }
            size="md"
            onClose={() => setViewingDashTraining(null)}
            footer={
              <Btn variant="secondary" size="sm" onClick={() => setViewingDashTraining(null)}>
                Fermer
              </Btn>
            }
          >
            <div className="space-y-4 text-sm">
              {/* Salarié + structure */}
              <div className="flex items-center gap-2 text-gray-500 text-xs">
                <span className="font-semibold text-gray-800 text-base">
                  {vt.employee.firstName} {vt.employee.lastName}
                </span>
                <Badge color="bg-gray-100 text-gray-600">{vt.employee.structureName || '—'}</Badge>
              </div>

              {/* Badges statut */}
              <div className="flex items-center gap-2 flex-wrap">
                {sc && <Badge color={sc.cls}>{sc.label}</Badge>}
                {vt.mandatory && <Badge color="bg-red-100 text-red-700">Obligatoire</Badge>}
                {vt.nature && <Badge color="bg-blue-100 text-blue-700">{vt.nature}</Badge>}
              </div>

              {/* Intitulé + type */}
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Intitulé</p>
                <p className="font-semibold text-gray-800">{vt.title}</p>
                {vt.trainingType?.name && (
                  <p className="text-xs text-gray-500 mt-0.5">Type : {vt.trainingType.name}</p>
                )}
              </div>

              {/* Grille infos */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Organisme</p>
                  <p className="text-gray-700">{vt.organism || <span className="text-gray-300">—</span>}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Durée</p>
                  <p className="text-gray-700">{vt.durationHours != null ? `${vt.durationHours} h` : <span className="text-gray-300">—</span>}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Date de réalisation</p>
                  <p className="text-gray-700">
                    {fmtDate(vt.startDate)}
                    {vt.endDate && <> → {fmtDate(vt.endDate)}</>}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Prochaine échéance</p>
                  <p className={ei ? ei.txt : 'text-gray-300'}>
                    {vt.recyclingDate ? fmtDate(vt.recyclingDate) : '—'}
                    {ei && <span className="ml-1 text-xs font-normal">
                      ({ei.diff < 0 ? `il y a ${-ei.diff} j` : `dans ${ei.diff} j`})
                    </span>}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Coût TTC</p>
                  <p className="text-gray-700">{vt.cost != null ? fmtCurrency(vt.cost) : <span className="text-gray-300">—</span>}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Financement</p>
                  <p className="text-gray-700">{vt.financing || <span className="text-gray-300">—</span>}</p>
                </div>
              </div>

              {vt.notes && (
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Notes</p>
                  <p className="text-gray-600 whitespace-pre-wrap">{vt.notes}</p>
                </div>
              )}
            </div>
          </Modal>
        );
      })()}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const EMPLOYEES_TABS = [
  { id: "employees", label: "Employés", icon: Users },
  { id: "analytique", label: "Analytique RH", icon: BarChart2 },
];

// Pages standalone (pas de barre d'onglets)
const STANDALONE_TABS = [
  "dashboard",
  "dossiers",
  "entretiens",
  "visites-medicales",
  "formations-dashboard",
  "transport-collection",
  "document-templates",
];

const PAGE_META = {
  employees: { label: "Employés", icon: Users },
  dashboard: { label: "Dashboard RH", icon: BarChart3 },
  dossiers: { label: "Dossiers RH", icon: Archive },
  entretiens: { label: "Entretiens", icon: MessageSquare },
  "visites-medicales": { label: "Visites médicales", icon: Stethoscope },
  analytique: { label: "Analytique RH", icon: BarChart2 },
  "formations-dashboard": { label: "Dashboard Formations", icon: GraduationCap },
  "transport-collection": { label: "Collecte transport", icon: Bus },
  "document-templates": { label: "Modèles de documents", icon: FileText },
};

// ─────────────────────────────────────────────────────────────────────────────

const HRPage = ({ showAllStructures = false, initialTab = "employees", onNavigate, navPayload, onNavPayloadConsumed }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [prevTab, setPrevTab] = useState(null);
  const [medVisitsInitialStatut, setMedVisitsInitialStatut] = useState("");
  // Auto-ouverture fiche employé depuis recherche globale (navPayload.employeeId)
  // ou autres voies internes (clic dans liste, etc.). Consommé par EmployeesTab.
  const [pendingEmployeeId, setPendingEmployeeId] = useState(null);

  // Consommation du payload de recherche globale (Ctrl+K → employé sélectionné)
  useEffect(() => {
    if (navPayload?.employeeId) {
      setActiveTab('employees');
      setPendingEmployeeId(navPayload.employeeId);
      onNavPayloadConsumed?.();
    }
  }, [navPayload, onNavPayloadConsumed]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const isStandalone = STANDALONE_TABS.includes(initialTab);
  const meta = PAGE_META[initialTab] || PAGE_META.employees;
  const PageIcon = meta.icon;

  return (
    <div className="min-h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 sm:px-6 py-4">
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <PageIcon size={22} className="text-primary" />
            {meta.label}
            {showAllStructures && (
              <span className="ml-2 text-xs font-normal text-primary bg-primary/10 px-2 py-0.5 rounded">
                Toutes les structures
              </span>
            )}
          </h1>
        </div>
        {/* Barre d'onglets uniquement si plusieurs onglets disponibles */}
        {!isStandalone && EMPLOYEES_TABS.length > 1 && (
          <div className="px-4 sm:px-6 flex gap-1 overflow-x-auto">
            {EMPLOYEES_TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => { setPrevTab(null); setActiveTab(t.id); }}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === t.id
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Icon size={15} />
                  {t.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 py-6">
        {prevTab === "dashboard" && activeTab !== "dashboard" && (
          <button
            onClick={() => { setPrevTab(null); setActiveTab("dashboard"); }}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft size={15} />
            Retour au dashboard RH
          </button>
        )}
        {activeTab === "dashboard" && (
          <DashboardTab
            onNavigate={(empId, tab, extra) => {
              setPrevTab("dashboard");
              setActiveTab(tab || "employees");
              if (extra?.statut !== undefined)
                setMedVisitsInitialStatut(extra.statut);
            }}
            showAllStructures={showAllStructures}
          />
        )}
        {activeTab === "employees" && (
          <EmployeesTab
            showAllStructures={showAllStructures}
            onNavigate={onNavigate}
            pendingEmployeeId={pendingEmployeeId}
            onPendingConsumed={() => setPendingEmployeeId(null)}
          />
        )}
        {activeTab === "dossiers" && (
          <DossiersAllTab showAllStructures={showAllStructures} />
        )}
        {activeTab === "entretiens" && (
          <EntretiensAllTab showAllStructures={showAllStructures} />
        )}
        {activeTab === "visites-medicales" && (
          <MedicalVisitsAllTab
            key={medVisitsInitialStatut || "default"}
            showAllStructures={showAllStructures}
            initialStatut={medVisitsInitialStatut}
          />
        )}
        {activeTab === "charges" && (
          <ChargesTab showAllStructures={showAllStructures} />
        )}
        {activeTab === "analytique" && (
          <AnalytiqueTab showAllStructures={showAllStructures} />
        )}
        {activeTab === "formations-dashboard" && (
          <FormationsDashboardTab showAllStructures={showAllStructures} />
        )}
        {activeTab === "transport-collection" && (
          <TransportCollectionTab
            showAllStructures={showAllStructures}
            onNavigate={onNavigate}
          />
        )}
        {activeTab === "document-templates" && (
          <HRDocumentTemplatesTab />
        )}
      </div>

    </div>
  );
};

export default HRPage;
